import { getSocketId, REAUTH_CODES } from "@/socket";
import { markProjectImported, projectIdOfPath } from "@/shared/composables/useImportedProjects";
import { markLicenseRequired } from "@/shared/composables/useLicenseGate";
import { markSpaceRefused, spaceOfPath } from "@/shared/composables/useSpaceRestriction";
import type { PanelAlign, PanelColumn } from "@/shared/utils/panelPlacement";

// ── Types matching server API ──

export interface UserProfile {
	id: string;
	email: string;
	email_confirmed: boolean;
	display_name: string;
	color: string;
	language: string;
	system_role: string;
	/** What `system_role` grants — which control-center sections to offer. */
	system_entitlements: string[];
	created_at: string;
	updated_at: string;
	/** Origin of the account service, where password, MFA and passkeys are managed. */
	provider_account_url: string;
	/** What the server's license switches on; `null` while no license is confirmed. */
	license_features: LicenseFeatures | null;
}

/**
 * What the server's license switches on, as the signed-in user's profile and session carry it —
 * enough to decide what to OFFER (a location picker's "Personal", the member limit said where people
 * are added to a personal space). The server decides on every request regardless: a stale copy here
 * is refused by code (`personal_spaces_disabled`, `personal_member_limit`).
 */
export interface LicenseFeatures {
	/** Whether the Designer is open on this server. */
	designer: boolean;
	/** Whether people may have spaces of their own, outside any department. */
	personal_spaces: boolean;
	/** How many members a personal space may have, the owner included. */
	personal_member_limit: number;
}

/** Why a project's Designer is closed: the server's license has it off, or its department's does. */
export type DesignerUnavailableReason = "server" | "department";

/**
 * Why a space is locked — a project read-only, a dataspace closed to the Operator. Its department's
 * license is not valid; the server's license allows no personal spaces and it is one; or it is a
 * personal space with more members than the license allows.
 */
export type SpaceLockReason =
	| "department_unlicensed"
	| "personal_spaces_disabled"
	| "personal_member_limit";

/**
 * What a license takes away from one space right now, as `ProjectResponse` and `DataspaceResponse`
 * carry it. `designer` is projects only (always `null` on a dataspace). Both are `null` for a space
 * nothing is taken away from. `effective_entitlements` next to it is already without what the
 * restriction refuses, so whatever the client hides by entitlement is hidden without reading this;
 * this says WHY.
 */
export interface SpaceRestriction {
	designer: { reason: DesignerUnavailableReason } | null;
	locked: { reason: SpaceLockReason } | null;
}

/**
 * `details` of a `409 personal_member_limit`: the limit on a personal space's members, the explicit
 * members the space has, and its pending invitations (counted or not, as the route says). A refused
 * move to personal adds the space it names (`kind`, `id`). Read it through `memberLimitFromError`.
 */
export interface PersonalMemberLimitDetails {
	limit: number;
	members: number;
	pending: number;
	kind?: SpaceKind;
	id?: string;
}

interface ApiError {
	error: { message: string; code?: string; details?: unknown };
}

/**
 * Error thrown by API helpers. Carries the HTTP status code and an optional
 * machine-readable `code` (e.g. "already_invited", "email_mismatch").
 *
 * `details` is whatever structured payload the server attached to the error body. Today that is
 * `dependency_impact_rejected`, whose `details.impact` is the report of what the refused change
 * would have broken — read it through `impactFromError` — and the four in-use refusals, whose
 * `details.references` lists what is in the way, through `referencesFromError`. Never by hand.
 */
export class ApiCallError extends Error {
	status: number;
	code?: string;
	details?: unknown;
	constructor(message: string, status: number, code?: string, details?: unknown) {
		super(message);
		this.name = "ApiCallError";
		this.status = status;
		this.code = code;
		this.details = details;
	}
}

/** The machine-readable `code`s a client branches on. Never branch on the message. */
export const API_ERROR_CODES = {
	/** 409 — a write on an entity that lives in a dependency (or a term only a dependency defines). */
	dependencyEntityProtected: "dependency_entity_protected",
	/** 400 — a dependency change would orphan local references, or a moved `wip` surface is held. */
	dependencyImpactRejected: "dependency_impact_rejected",
	/** 409 — an entity name in the dependency collides with one already in scope. */
	dependencyNameCollision: "dependency_name_collision",
	/**
	 * 409 — a write on a dataobject Notitia itself ships (`Location`). The server answers this with
	 * a plain 409 and NO code today, so nothing maps to it yet: the UI blocks the write before the
	 * request instead. The mapping is in place for the day the server names it.
	 */
	predefinedDataobjectProtected: "predefined_dataobject_protected",
	/**
	 * 409 — a dataobject cannot be deleted while an action names it as the concept it makes or acts on.
	 * The message names the activity and the action.
	 */
	dataobjectInUseByAction: "dataobject_in_use_by_action",
	/** 409 — a relation cannot be deleted while an action names it. The message names the action. */
	relationInUseByAction: "relation_in_use_by_action",
	/**
	 * 409 — a process cannot be deleted while an action (spawn, send event, list) names it as its
	 * `definition`. The message names the activity and the action.
	 */
	processInUseByAction: "process_in_use_by_action",
	/** 409 — a dataobject delete refused because an attribute somewhere has it as its type. */
	dataobjectInUse: "dataobject_in_use",
	/** 409 — a delete refused because an axiom elsewhere reads one of the attributes, or a relation
	 *  that would go with it. */
	attributeInUseByAxiom: "attribute_in_use_by_axiom",
	/**
	 * 409 — an attribute cannot be removed while an action or a graphly field reads or writes it.
	 * The four `…InUse` codes below carry `details.references`, every place in the way: read it
	 * through `referencesFromError`.
	 */
	attributeInUse: "attribute_in_use",
	/** 409 — a type cannot be removed while an attribute, a type annotation, a select or another
	 *  type's union still names it. */
	typeInUse: "type_in_use",
	/** 409 — a type update would take out a value a condition or an axiom still compares with. */
	typeValueInUse: "type_value_in_use",
	/** 409 — a process entry cannot be removed while a DATAOBJECT_OPERATION `create` needs it. */
	processDataobjectInUseByAction: "process_dataobject_in_use_by_action",
	/** 404 — a history cursor (`after`) that is not a commit of this project. Start again from the top. */
	commitNotFound: "commit_not_found",
	/** 404 — a dataspace whose build ships no start page. Go to the process list instead. */
	landingDisabled: "landing_disabled",
	/**
	 * 409 — a delete, or a change beyond position, name, description and colour, on an action a
	 * question manages (`depends_on`). The UI blocks both before the request; this is the net.
	 */
	actionManagedByDependency: "action_managed_by_dependency",
	/** 403 — a system entitlement the caller's system role does not grant. */
	missingEntitlement: "missing_entitlement",
	/** 401 from `/auth/session` — the cookie names a session the server no longer holds (it
	 *  was swept as unused). The browser was signed in, so sign in again at once. */
	sessionLost: "session_lost",
	/** 401 from `/auth/session` — the provider refused to renew the session (it ended there, or its
	 *  refresh token lapsed). The provider may still know the browser, so sign in again at once. */
	sessionEnded: "session_ended",
	/** 401 from `/auth/session` — no session, and the server runs silent sign-in
	 *  (`OIDC_SILENT_SIGNIN`): ask the provider without a prompt before showing the signed-out page. */
	silentSignin: "silent_signin",
	/** 409 — nobody could manage users and roles after it, or a project or dataspace would lose
	 *  its last admin. */
	lastAdmin: "last_admin",
	/** 403 — a system role's own holder may not change it. */
	selfRoleChange: "self_role_change",
	/** 403 — `server_admin` and `banned`: what they grant is fixed. */
	roleLocked: "role_locked",
	/** 409 — a system role somebody still holds. */
	roleInUse: "role_in_use",
	/** 409 — a system role of that name exists. */
	roleExists: "role_exists",
	/** 404 — the project has a record but its files are gone from the server. */
	projectFilesMissing: "project_files_missing",
	/** 400 — a settings field was refused; `details.field` names it. */
	invalidSetting: "invalid_setting",
	/** 503 — mail is not configured (no host or no sender). */
	mailNotConfigured: "mail_not_configured",
	/** 502 — the test mail was not sent; the message says why. */
	mailTestFailed: "mail_test_failed",
	/** 503 — the LLM gateway is not configured (not all four values are set). */
	llmNotConfigured: "llm_not_configured",
	/** 502 — the gateway test request failed; the message says why. */
	llmTestFailed: "llm_test_failed",
	/** 503 — the Operator's map is turned off on this server (`map.switch`), or has no tile server. */
	mapDisabled: "map_disabled",
	/** 503 — no location service is set: no address search, no pin-to-address. */
	geocodingDisabled: "geocoding_disabled",
	/** 503 — the location service is older than 1.2.0 and has no reverse lookup. */
	reverseUnsupported: "reverse_unsupported",
	/** 404 — nothing is near enough to the pin to claim an address. Its coordinates still answer. */
	noAddressHere: "no_address_here",
	/** 502 — the map's tile server or the location service gave nothing usable. */
	mapUpstreamFailed: "map_upstream_failed",
	/** 502 — the map test failed; the message says why. */
	mapTestFailed: "map_test_failed",
	/** 503 — no location service is set (control center). */
	locationNotConfigured: "location_not_configured",
	/** 502 — the location service test failed; the message says why. */
	locationTestFailed: "location_test_failed",
	/** 503 — processes get no gateway: no URL for them, or no token (control center). */
	processesNotConfigured: "processes_not_configured",
	/** 502 — the gateway refused the processes' test request; the message says why. */
	processesTestFailed: "processes_test_failed",
	/** 403 — the Designer or the Operator on a server without a valid license. */
	licenseRequired: "license_required",
	/** 400 — the license server does not know the key. */
	licenseKeyUnknown: "license_key_unknown",
	/** 409 — the license server says the license is disabled. */
	licenseDisabled: "license_disabled",
	/** 502 — the license server could not be asked; the message says why. */
	licenseServerUnreachable: "license_server_unreachable",
	/** 502 — the license server's answer was not a license. */
	licenseResponseInvalid: "license_response_invalid",
	/** 409 — "check now" with no key stored. */
	licenseKeyMissing: "license_key_missing",
	/** 400 — the key entered is empty or not one word. */
	invalidLicenseKey: "invalid_license_key",
	/**
	 * 409 — a route that would open a project known here only from imported releases: it has no
	 * source on this server. `@/api` reports it to `useImportedProjects`, whichever call met it.
	 */
	projectImported: "project_imported",
	/** 400 — a release version that is not semver. */
	invalidVersion: "invalid_version",
	/** 409 — a release version not greater than the latest release. */
	versionNotGreater: "version_not_greater",
	/** 409 — that version has a release, a build or a package already. */
	releaseExists: "release_exists",
	/**
	 * 409 — the project changed while its release was building (nothing was released), or an
	 * import of a version this server has from a different archive.
	 */
	releaseConflict: "release_conflict",
	/** 422 — the release build failed; `details` is `ReleaseBuildFailedDetails`. */
	releaseBuildFailed: "release_build_failed",
	/** 422 — a version is set through the metadata; only a release may set one. */
	useRelease: "use_release",
	/** 422 — a dataspace asked to run a version with no build on this server. */
	versionNotBuilt: "version_not_built",
	/** 400 — an upload that is not a release archive, or one that fails its checks. */
	invalidArchive: "invalid_archive",
	/** 404 — a release whose build is no longer on the server, so it cannot be exported. */
	buildMissing: "build_missing",
	/** 400 — a department's name or description that is empty, too long or otherwise refused. */
	invalidName: "invalid_name",
	invalidDescription: "invalid_description",
	/** 409 — another department already has that name (ignoring case). */
	departmentNameTaken: "department_name_taken",
	/** 400 — the license server does not know the department key. */
	departmentKeyUnknown: "department_key_unknown",
	/** 400 — the license server says the department key is not a valid one. */
	departmentKeyInvalid: "department_key_invalid",
	/** 409 — the license server says the department's license is disabled. */
	departmentKeyDisabled: "department_key_disabled",
	/** 409 — the department key is bound to another server. */
	departmentKeyBoundElsewhere: "department_key_bound_elsewhere",
	/** 409 — another department on this server already uses the key. */
	departmentKeyInUse: "department_key_in_use",
	/** 502 — the license server gave no answer for the key; nothing was stored. */
	departmentKeyUnconfirmed: "department_key_unconfirmed",
	/** 409 — a department with a project or a dataspace in it cannot be deleted. */
	departmentNotEmpty: "department_not_empty",
	/** 409 — the department every user belongs to cannot be deleted. */
	departmentIsDefault: "department_is_default",
	/** 409 — a department keeps at least one owner. */
	lastOwner: "last_owner",
	/** 409 — nobody leaves the default department: everyone is its member. */
	defaultDepartment: "default_department",
	/** 409 — the user is a member of the department already. */
	alreadyMember: "already_member",
	/** 400 — a role of another department, or none, was named. */
	invalidRole: "invalid_role",
	/** 400 — a department role asked for an entitlement that is not a department's. */
	invalidEntitlement: "invalid_entitlement",
	/** 403 — a built-in department role cannot be renamed or deleted. */
	roleBuiltIn: "role_built_in",
	/** 409 — the department's `owner` role always gets the space's `admin` role: a row that gives it another is refused. */
	mappingLocked: "mapping_locked",
	/** 409 — a space is in no department (so it has no department access) or not the one named. */
	notInDepartment: "not_in_department",
	/**
	 * 403 — adding somebody to a space by their id: the space is personal, or they are not an explicit
	 * member of its department. People are added to a space by invitation, except the department's own
	 * members, who may also be picked from `listMemberCandidates`.
	 */
	invitationRequired: "invitation_required",
	/**
	 * 409 — a department's space is only its members': somebody outside the department is not added
	 * (the control center included), an invitation goes only to an address one of its members has, and
	 * one that is accepted by somebody outside it is refused, and stays pending. They join the
	 * department first.
	 */
	notADepartmentMember: "not_a_department_member",
	/** 409 — the control center cannot make a space personal that would be left with no admin of its own. */
	spaceNeedsAdmin: "space_needs_admin",
	/** 400 — a department role is mapped to more than one role of the space. */
	duplicateMapping: "duplicate_mapping",
	/** 400 — a move that names no project and no dataspace. */
	noSpaces: "no_spaces",
	/** 400 — the default department needs the role it gives everyone. */
	roleRequired: "role_required",
	/** 404 — a space that is not there, or the caller cannot reach. Nothing moved. */
	spaceNotFound: "space_not_found",
	/**
	 * 403 — the space is locked: a project is read-only, a dataspace closed to the Operator.
	 * `details.reason` is a `SpaceLockReason`. `@/api` reports it to `useSpaceRestriction`, whichever
	 * call met it, scoped to the space its path names. Creating in a department whose license is not
	 * valid is this too, with no space to name.
	 */
	spaceLocked: "space_locked",
	/**
	 * 403 — the Designer is closed for the project; `details.reason` is `server` or `department`.
	 * Reported to `useSpaceRestriction` like `space_locked`. Creating a project is this too, with no
	 * project to name.
	 */
	designerUnavailable: "designer_unavailable",
	/** 403 — creating a personal space, when the server's license allows none. */
	personalSpacesDisabled: "personal_spaces_disabled",
	/**
	 * 409 — adding, inviting or accepting a member of a personal space with no room for one more, or
	 * moving a space out to personal with too many; `details` is `PersonalMemberLimitDetails`.
	 */
	personalMemberLimit: "personal_member_limit",
	/**
	 * 403 — acting in a department's dataspace (creating a process, setting an attribute, a channel
	 * event, an embedded create or remove) with every floating seat of the department taken, and none
	 * reserved for the caller; `details` is `SeatUnavailableDetails`. Read it through
	 * `seatUnavailableFromError`. `useSeat` is how the process view meets the same refusal, over the
	 * socket.
	 */
	seatUnavailable: "seat_unavailable",
	/** 409 — a named seat can be given to an explicit member only; this one is a member because everyone is. */
	notAMember: "not_a_member",
	/**
	 * 409 — sending an invitation again while invitation mail is switched off (or the server cannot
	 * send mail): the new link would have no way to reach anybody and the old one would stop working.
	 * Revoke the invitation and invite again to get a link to pass on.
	 */
	invitationMailDisabled: "invitation_mail_disabled",
	/**
	 * 403 — answering an invitation from the inbox, or by its link, while the account provider has
	 * not verified the signed-in account's email address: an invitation binds only to a verified one.
	 */
	emailNotVerified: "email_not_verified",
	/**
	 * 409 — a dataspace's SIP trunk saved with a digest username another dataspace's trunk already
	 * has: the username is how a coms system's call finds its dataspace, so it names exactly one.
	 */
	sipInboundUsernameTaken: "inbound_username_taken",
	/**
	 * 400 — a process shared with somebody who does not reach its dataspace (or nobody): a grant on a
	 * single process is no way into the dataspace.
	 */
	userNotInDataspace: "user_not_in_dataspace",
	/**
	 * 403 — a channel event on an interactable its model bound to other people: only they may answer
	 * it, press it, close it or use its chat. See `InteractableStamp.actors`.
	 */
	notAnActor: "not_an_actor",
	/**
	 * 400 — an alias that is no alias: not `a-z`, `0-9` and inner hyphens once normalized, longer than
	 * 63, shaped like an id, or a word the routes use. `shared/utils/alias.ts` says which, as it is typed.
	 */
	invalidAlias: "invalid_alias",
	/**
	 * 409 — an alias another dataspace on the server, or another process of the dataspace, already has.
	 * Never says which: it may be a dataspace the caller cannot see.
	 */
	aliasTaken: "alias_taken",
} as const;

/**
 * `details` of a `403 seat_unavailable`: the department whose seats are all in use, by name for the
 * words, and the number of floating seats it has. Read it through `seatUnavailableFromError`.
 */
export interface SeatUnavailableDetails {
	department_id: string;
	department_name: string;
	limit: number;
}

/** Whether `e` is the server refusing a write on a dependency-owned entity. */
export function isDependencyProtectedError(e: unknown): boolean {
	return e instanceof ApiCallError && e.code === API_ERROR_CODES.dependencyEntityProtected;
}

// ── HTTP helpers ──

interface AuthHooks {
	getToken: () => string | null;
	onUnauthorized?: (code: string | undefined) => Promise<boolean>;
}

let authHooks: AuthHooks | null = null;

/** Registered once by useAuth; api.ts must not import it (import cycle via socket). */
export function configureAuth(hooks: AuthHooks): void {
	authHooks = hooks;
}

function getToken(): string | null {
	return authHooks?.getToken() ?? null;
}

// ── Session (OIDC relying party) ──

export interface SessionUser {
	id: string;
	email: string;
	email_confirmed: boolean;
	display_name: string;
	system_role: string;
	/** What `system_role` grants — which control-center sections to offer. */
	system_entitlements: string[];
	language: string;
	color: string;
	/** What the server's license switches on; `null` while no license is confirmed. */
	license_features: LicenseFeatures | null;
}

export interface SessionResponse {
	access_token: string;
	expires_in: number;
	user: SessionUser;
}

/** Exchanges the same-origin session cookie for a short-lived access token. */
export async function createSession(): Promise<SessionResponse> {
	return request<SessionResponse>("POST", "/auth/session");
}

/** Ends the server session; the provider's end-session URL is null when there was none. */
export async function logout(): Promise<{ end_session_url: string | null }> {
	return request<{ end_session_url: string | null }>("POST", "/auth/logout");
}

/**
 * The one place a bearer is attached and a 401 is handled, for every helper.
 * When a request that carried the bearer is refused with a re-authentication code,
 * `onUnauthorized` runs once (useAuth dedupes concurrent refreshes) and, if a session
 * came back, the request is repeated once with the new token. The retry is safe for
 * every method: the server rejected the request at the auth layer, before any side
 * effect. A second 401 is returned as is, never retried. `auth: false` calls
 * (createSession, logout) skip all of this, so the session refresh cannot recurse.
 */
async function send(
	auth: boolean,
	doFetch: (bearer: string | null) => Promise<Response>,
): Promise<Response> {
	const res = await doFetch(auth ? getToken() : null);
	if (!auth || res.status !== 401 || !authHooks?.onUnauthorized) return res;
	const body: ApiError | null = await res
		.clone()
		.json()
		.catch(() => null);
	const code = body?.error.code;
	if (!code || !REAUTH_CODES.has(code)) return res;
	return (await authHooks.onUnauthorized(code)) ? doFetch(getToken()) : res;
}

function withBearer(
	headers: Record<string, string>,
	bearer: string | null,
): Record<string, string> {
	return bearer ? { ...headers, Authorization: `Bearer ${bearer}` } : headers;
}

/** The headers every request helper starts from: on anything but a GET, the socket id, so the
 *  server's broadcast of the change skips the session that made it. One place, so a helper cannot
 *  forget it. The bearer token is `send`'s to attach, since it renews it. */
function headersFor(method: string): Record<string, string> {
	const headers: Record<string, string> = {};
	if (method !== "GET") {
		const sid = getSocketId();
		if (sid) headers["X-Socket-Id"] = sid;
	}
	return headers;
}

/** Tells `useSpaceRestriction` about a `403 space_locked` / `designer_unavailable` for the space `path` names. */
function markIfSpaceRefusal(path: string, error: ApiError["error"]): void {
	const code = error.code;
	if (code !== API_ERROR_CODES.spaceLocked && code !== API_ERROR_CODES.designerUnavailable)
		return;
	const space = spaceOfPath(path);
	if (!space) return;
	const reason = (error.details as { reason?: unknown } | undefined)?.reason;
	markSpaceRefused({
		...space,
		code,
		reason: typeof reason === "string" ? reason : "",
	});
}

/**
 * The error a failed response stands for. A `403 license_required` — the Designer or the Operator
 * on a server without a valid license — is also told to `useLicenseGate`, whichever call met it,
 * so both layouts can show it as the whole page instead of each view as an error of its own.
 */
async function failure(res: Response, path: string): Promise<ApiCallError> {
	const err: ApiError = await res.json().catch(() => ({
		error: { message: res.statusText },
	}));
	if (res.status === 403 && err.error.code === API_ERROR_CODES.licenseRequired) {
		markLicenseRequired();
	}
	// A locked space, or a Designer that is closed: told to `useSpaceRestriction` for the space the
	// path names — a create has no space yet, and its dialog words the refusal itself.
	if (res.status === 403) markIfSpaceRefusal(path, err.error);
	// A project with no source here (imported releases only): every page of it is its read-only
	// home, whichever request found out.
	if (res.status === 409 && err.error.code === API_ERROR_CODES.projectImported) {
		const projectId = projectIdOfPath(path);
		if (projectId) markProjectImported(projectId);
	}
	return new ApiCallError(err.error.message, res.status, err.error.code, err.error.details);
}

async function request<T>(
	method: string,
	path: string,
	body?: unknown,
	auth = false,
	signal?: AbortSignal,
): Promise<T> {
	const headers = { ...headersFor(method), "Content-Type": "application/json" };
	const res = await send(auth, (bearer) =>
		fetch(`/api/v2${path}`, {
			method,
			headers: withBearer(headers, bearer),
			body: body ? JSON.stringify(body) : undefined,
			signal,
		}),
	);
	if (!res.ok) throw await failure(res, path);
	const text = await res.text();
	return text ? JSON.parse(text) : ({} as T);
}

/** `cache` defaults to `no-store`, which is right for a thumbnail that is replaced in place. A
 *  content-addressed resource (a type image) passes `default`: its name never means other bytes,
 *  and the server marks it immutable. */
async function requestBlob(
	method: string,
	path: string,
	auth = false,
	cache: RequestCache = "no-store",
): Promise<Blob> {
	const res = await send(auth, (bearer) =>
		fetch(`/api/v2${path}`, {
			method,
			headers: withBearer(headersFor(method), bearer),
			cache,
		}),
	);
	if (!res.ok) throw await failure(res, path);
	return res.blob();
}

async function requestText(method: string, path: string, auth = false): Promise<string> {
	const res = await send(auth, (bearer) =>
		fetch(`/api/v2${path}`, { method, headers: withBearer(headersFor(method), bearer) }),
	);
	if (!res.ok) throw await failure(res, path);
	return res.text();
}

/** Like `request`, for a `FormData` body. */
async function requestMultipart<T = void>(
	method: string,
	path: string,
	data: FormData,
	auth = false,
): Promise<T> {
	const res = await send(auth, (bearer) =>
		fetch(`/api/v2${path}`, {
			method,
			headers: withBearer(headersFor(method), bearer),
			body: data,
		}),
	);
	if (!res.ok) throw await failure(res, path);
	const text = await res.text();
	return text ? JSON.parse(text) : ({} as T);
}

// ── Account API ──

export async function getProfile(): Promise<UserProfile> {
	return request<UserProfile>("GET", "/account", undefined, true);
}

export async function updateProfile(data: {
	language?: string;
	color?: string;
}): Promise<UserProfile> {
	return request<UserProfile>("PUT", "/account", data, true);
}

export interface PatGrant {
	layer_kind: "project" | "dataspace";
	layer_id: string;
	entitlement: string;
}

export interface PatListGrant extends PatGrant {
	layer_label: string;
}

export interface PatListItem {
	id: string;
	name: string;
	created_at: string;
	/** `null`: never expires. */
	expires_at: string | null;
	revoked_at: string | null;
	status: "active" | "expired" | "revoked";
	grants: PatListGrant[];
}

export interface PatListResponse {
	pats: PatListItem[];
}

export interface GrantableLayer {
	layer_kind: "project" | "dataspace";
	layer_id: string;
	layer_label: string;
	/** A project layer's own id; for a dataspace, the id of its owning project. */
	project_id: string;
	/**
	 * Omitted when the caller may not see that project; the client then shows
	 * a generic "Unnamed project".
	 */
	project_label?: string;
	entitlements: string[];
}

export interface GrantableResponse {
	layers: GrantableLayer[];
}

export interface PatCreateRequest {
	name: string;
	expires_in_days?: number;
	/** No expiry; needs `system:mint_permanent_pats`. Leave `expires_in_days` out with it. */
	never_expires?: boolean;
	grants: PatGrant[];
}

export interface PatCreateResponse {
	id: string;
	name: string;
	/** Shown once: the server never stores or returns it again. */
	secret: string;
	/** `null`: never expires. */
	expires_at: string | null;
	grants: PatGrant[];
}

export async function listPats(): Promise<PatListResponse> {
	return request<PatListResponse>("GET", "/account/pats", undefined, true);
}

/** Layers and entitlements the user may currently put on a new token. */
export async function listGrantablePats(): Promise<GrantableResponse> {
	return request<GrantableResponse>("GET", "/account/pats/grantable", undefined, true);
}

export async function createPat(data: PatCreateRequest): Promise<PatCreateResponse> {
	return request<PatCreateResponse>("POST", "/account/pats", data, true);
}

export async function revokePat(id: string): Promise<void> {
	await request<unknown>("DELETE", `/account/pats/${encodeURIComponent(id)}`, undefined, true);
}

// ── Projects types ──

export interface ProjectRoleResponse {
	id: number;
	name: string;
	entitlements: string[];
	is_system: boolean;
}

export interface ProjectResponse {
	id: string;
	created_by: string;
	role: string;
	effective_entitlements: string[];
	created_at: string;
	updated_at: string;
	name: string | null;
	version: string | null;
	term_id: string | null;
	languages: string[];
	types_count: number;
	dataobjects_count: number;
	activities_count: number;
	processes_count: number;
	member_count: number;
	/** A record with no files on the server: nothing in it opens. Never set for an imported one. */
	files_missing: boolean;
	/**
	 * `local`, or `imported`: known here only from releases imported from another server — no
	 * source, read-only, its releases runnable in a dataspace.
	 */
	source: string;
	/** The department the project belongs to; `null` for a personal one. */
	department: DepartmentRef | null;
	/** What the license takes away from it right now; see `SpaceRestriction`. */
	restriction: SpaceRestriction;
}

/** `ProjectResponse.source` of a project known here only from imported releases. */
export const PROJECT_SOURCE_IMPORTED = "imported";

/** Somebody on a project's member list, resolved: who reaches it — by an explicit membership, through
 *  its department's access, or both — and an explicit membership of a department's project whose
 *  holder is not in the department (`has_access: false`). See `shared/utils/spaceMembers.ts`. */
export interface MemberResponse {
	user_id: string;
	email: string;
	display_name: string;
	/** The explicit membership's role — what the Members settings change and remove — or `null` for
	 *  somebody who reaches the project through its department alone. */
	role: string | null;
	/** The project roles its department's access gives them, lowest first. */
	department_roles: string[];
	/** Whether they reach the project: `false` only for a membership of somebody outside its department. */
	has_access: boolean;
	/** What every role they hold grants together; empty without access. */
	entitlements: string[];
	/** When the explicit membership was made; `null` without one. */
	created_at: string | null;
}

// ── Projects API ──

/**
 * `departmentId` creates it in that department (the caller needs `department:projects:create`
 * there); left out, it is a personal project.
 */
export async function createProject(name: string, departmentId?: string): Promise<ProjectResponse> {
	return request<ProjectResponse>(
		"POST",
		"/projects",
		departmentId ? { name, department_id: departmentId } : { name },
		true,
	);
}

export async function listProjects(): Promise<ProjectResponse[]> {
	return request<ProjectResponse[]>("GET", "/projects", undefined, true);
}

export async function getProject(id: string): Promise<ProjectResponse> {
	return request<ProjectResponse>("GET", `/projects/${id}`, undefined, true);
}

export async function updateProject(id: string, data: { name?: string }): Promise<ProjectResponse> {
	return request<ProjectResponse>("PUT", `/projects/${id}`, data, true);
}

export async function deleteProject(id: string): Promise<void> {
	await request<unknown>("DELETE", `/projects/${id}`, undefined, true);
}

// ── Project Members API ──

export async function listProjectMembers(projectId: string): Promise<MemberResponse[]> {
	return request<MemberResponse[]>("GET", `/projects/${projectId}/members`, undefined, true);
}

/**
 * Adds a member of the project's department to it. Anybody else — a personal project's people, a
 * person in no department of it — is invited: `403 invitation_required`. See `listMemberCandidates`.
 */
export async function addProjectMember(
	projectId: string,
	userId: string,
	role?: string,
): Promise<MemberResponse> {
	return request<MemberResponse>(
		"POST",
		`/projects/${projectId}/members`,
		{ user_id: userId, role },
		true,
	);
}

export async function updateProjectMemberRole(
	projectId: string,
	userId: string,
	role: string,
): Promise<void> {
	await request<unknown>("PUT", `/projects/${projectId}/members/${userId}`, { role }, true);
}

export async function removeProjectMember(projectId: string, userId: string): Promise<void> {
	await request<unknown>("DELETE", `/projects/${projectId}/members/${userId}`, undefined, true);
}

// ── Project Roles ──

export async function listProjectRoles(projectId: string): Promise<ProjectRoleResponse[]> {
	return request<ProjectRoleResponse[]>("GET", `/projects/${projectId}/roles`, undefined, true);
}

export async function createProjectRole(
	projectId: string,
	data: { name: string; entitlements: string[] },
): Promise<ProjectRoleResponse> {
	return request<ProjectRoleResponse>("POST", `/projects/${projectId}/roles`, data, true);
}

export async function updateProjectRole(
	projectId: string,
	roleId: number,
	data: { name?: string; entitlements?: string[] },
): Promise<ProjectRoleResponse> {
	return request<ProjectRoleResponse>(
		"PUT",
		`/projects/${projectId}/roles/${roleId}`,
		data,
		true,
	);
}

export async function deleteProjectRole(projectId: string, roleId: number): Promise<void> {
	await request<unknown>("DELETE", `/projects/${projectId}/roles/${roleId}`, undefined, true);
}

// ── Invitations (all three kinds) ──

/** What an invitation is to: a project, a dataspace or a department. */
export type InvitationKind = "project" | "dataspace" | "department";

/**
 * An invitation waits (`pending`), or its invitee said no from their inbox and it stays in the
 * inviter's list until it is dismissed (`declined`). Revoking, accepting and expiring take it off
 * the list altogether.
 */
export type InvitationStatus = "pending" | "declined";

/**
 * What a create route adds to the invitation it made. `mail_sent` is `false` while invitation mail
 * is switched off in the control center or the server has no mail configured: the invitation exists
 * all the same, and `accept_url` — present only then — is the link the mail would have carried, for
 * the inviter to pass on. It is in this one response and nowhere else: a mailed invitation's token
 * leaves the server by mail alone.
 */
export interface InvitationDelivery {
	mail_sent: boolean;
	accept_url?: string;
}

export interface InvitationResponse {
	id: string;
	project_id: string;
	email: string;
	role: string;
	/** `pending` or `declined` — see `InvitationStatus`. */
	status: string;
	created_at: string;
	expires_at: string;
	invited_by_display_name: string;
}

export type CreatedInvitationResponse = InvitationResponse & InvitationDelivery;

export type InvitationPreviewResponse =
	| {
			kind: "project";
			project_id: string;
			project_name: string;
			email: string;
			role: string;
			inviter_display_name: string;
			expires_at: string;
			requires_sign_in: boolean;
	  }
	| {
			kind: "dataspace";
			dataspace_id: string;
			dataspace_label: string;
			email: string;
			role: string;
			inviter_display_name: string;
			expires_at: string;
			requires_sign_in: boolean;
	  }
	| {
			kind: "department";
			department_id: string;
			department_name: string;
			email: string;
			/** The name of the department role offered, as it is now. */
			role: string;
			inviter_display_name: string;
			expires_at: string;
			requires_sign_in: boolean;
	  };

export type AcceptInvitationResponse =
	| { kind: "project"; project_id: string; role: string }
	| { kind: "dataspace"; dataspace_id: string; role: string }
	| { kind: "department"; department_id: string; role_id: number; role: string };

// ── Project Invitations API (admin-only on the server) ──

export async function listProjectInvitations(projectId: string): Promise<InvitationResponse[]> {
	return request<InvitationResponse[]>(
		"GET",
		`/projects/${projectId}/invitations`,
		undefined,
		true,
	);
}

export async function createProjectInvitation(
	projectId: string,
	data: { email: string; role: string },
): Promise<CreatedInvitationResponse> {
	return request<CreatedInvitationResponse>(
		"POST",
		`/projects/${projectId}/invitations`,
		data,
		true,
	);
}

/** Revokes a pending invitation, or dismisses a declined one. */
export async function cancelProjectInvitation(
	projectId: string,
	invitationId: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/invitations/${invitationId}`,
		undefined,
		true,
	);
}

/** `409 invitation_mail_disabled` while invitation mail is off: nothing is sent and nothing changes. */
export async function resendProjectInvitation(
	projectId: string,
	invitationId: string,
): Promise<InvitationResponse> {
	return request<InvitationResponse>(
		"POST",
		`/projects/${projectId}/invitations/${invitationId}/resend`,
		undefined,
		true,
	);
}

// ── Public Invitation API (token-based) ──

/** Works signed out too, but sends the bearer when there is one: `requires_sign_in` is the server's
 *  answer to whether this caller is signed in, and without the bearer it is always `true`. */
export async function getInvitationPreview(token: string): Promise<InvitationPreviewResponse> {
	return request<InvitationPreviewResponse>(
		"GET",
		`/invitations/${encodeURIComponent(token)}`,
		undefined,
		true,
	);
}

export async function acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
	return request<AcceptInvitationResponse>(
		"POST",
		`/invitations/${encodeURIComponent(token)}/accept`,
		undefined,
		true,
	);
}

// ── The invitations inbox (server/src/routes/invitations/inbox.rs) ──

/** One invitation addressed to the signed-in user. */
export interface InboxInvitation {
	kind: InvitationKind;
	/** The invitation's id, which `acceptInboxInvitation` and `declineInboxInvitation` take with `kind`. */
	id: string;
	/** The project, dataspace or department it is to, by `kind`. */
	space_or_department_id: string;
	/** The project's name, the dataspace's label or the department's name. */
	name: string;
	/** The name of the role it offers. */
	role: string;
	invited_by: string;
	created_at: string;
	expires_at: string;
}

export interface InboxResponse {
	/**
	 * Whether the account provider has verified the caller's email. An invitation binds only to a
	 * verified address, so while this is `false` the list is empty whatever was sent to it.
	 */
	email_verified: boolean;
	/** Every pending invitation that has not expired, of every kind, newest first. */
	invitations: InboxInvitation[];
}

export async function getInbox(): Promise<InboxResponse> {
	return request<InboxResponse>("GET", "/account/invitations", undefined, true);
}

/**
 * Accepts an invitation from the inbox — the same function the mailed link ends in, so the caller
 * becomes a member as by the link, caps included. `403 email_not_verified`, `404`, `410` (expired or
 * already answered) and `409 personal_member_limit` (the invitation stays pending).
 */
export async function acceptInboxInvitation(
	kind: InvitationKind,
	id: string,
): Promise<AcceptInvitationResponse> {
	return request<AcceptInvitationResponse>(
		"POST",
		`/account/invitations/${kind}/${encodeURIComponent(id)}/accept`,
		undefined,
		true,
	);
}

/** Declines an invitation from the inbox: its inviter's list shows it as `declined`. */
export async function declineInboxInvitation(kind: InvitationKind, id: string): Promise<void> {
	await request<unknown>(
		"POST",
		`/account/invitations/${kind}/${encodeURIComponent(id)}/decline`,
		undefined,
		true,
	);
}

/** A non-fatal finding from a write: "this worked, but you should know something about it".
 *  Branch on `code`, NEVER on `message` — the text is display copy and will change. */
export interface DiagnosticResponse {
	severity: "warning" | "info";
	code: string;
	message: string;
}

/** Every write that can create or rename returns the code name the generator RESOLVED. A derived
 *  name comes from the term's English label, so the client cannot predict it — address the entity
 *  by this value from here on. */
export interface ResolvedNameResponse {
	name: string;
	diagnostics: DiagnosticResponse[];
}

// ── Entity origin ──

export type OriginKind = "local" | "predefined" | "dependency" | "override";

/**
 * Who owns an entity. On every list item and single read of a dataobject, type, activity and
 * terminology entry. `alias` is the LOCAL name a designer gave the dependency pin — never a
 * project id — and is present exactly for `dependency` and `override`.
 *
 * `dependency` is read-only here: every write on it is `409 dependency_entity_protected`.
 * `override` exists for terminology only: this project's own label for a term a dependency also
 * defines. It wins wherever a label is read, and deleting it reverts to the dependency's.
 */
export interface OriginResponse {
	kind: OriginKind;
	alias?: string;
}

// ── Generator Types ──

export interface ParsedTypeResponse {
	name: string;
	/** True when the code name was pinned by hand rather than derived from the term. A manual
	 *  name is never touched by the terminology cascade — surface it, or a rename looks broken. */
	manual_name: boolean;
	term_id: string;
	/** This type's OWN literals only. A union's full domain also includes every member's — read
	 *  it from a build's `SCHEMA.types[x].values`, or the `types/{name}/effective-values` route. */
	values: string[];
	/** Names of the types this one absorbs, in declaration order. Empty for a flat enum. */
	members: string[];
	value_term_ids: string[];
	value_colors: string[];
	/** Index-aligned with `values`, with `"none"` for a value without an image. EMPTY for a type
	 *  none of whose values has one — read a missing index as `"none"`. See `typeValueImage`. */
	value_images: string[];
	origin: OriginResponse;
}

/** The image name of own value `index` of `type`, or `undefined` — reading the `"none"` sentinel
 *  and the empty array alike. What a save must send back as `TypeValueRequest.image`. */
export function typeValueImage(type: ParsedTypeResponse, index: number): string | undefined {
	const image = type.value_images?.[index];
	return image && image !== "none" ? image : undefined;
}

/** The colour of own value `index` of `type`, or `undefined` — `value_colors` carries the same
 *  `"none"` sentinel for "no colour" that `value_images` does, so an empty string is not the only
 *  way for a value to have none. */
export function typeValueColor(type: ParsedTypeResponse, index: number): string | undefined {
	const color = type.value_colors?.[index];
	return color && color !== "none" ? color : undefined;
}

export interface TypeValueRequest {
	/** The value's code name. EMPTY asks the generator to derive it from `term_id`'s label — what a
	 *  new value sends. An existing value sends back the string it has: a value is renamed only
	 *  through its term (the terminology cascade), never by typing a new string here. */
	value: string;
	term_id: string;
	color?: string;
	/** The value's image file name, as read. OMITTING IT CLEARS THE IMAGE, like `color` — so a save
	 *  must send back what it read (`typeValueImage`). A new picture goes through
	 *  `setTypeValueImage`, never through this field. */
	image?: string;
}

export interface TypeConfigRequest {
	/** `null`/omitted asks the generator to DERIVE the code name from the term — the normal case.
	 *  A value here pins the name manually, and the terminology cascade then never renames it. */
	name?: string | null;
	term_id: string;
	/** This type's OWN literals. Every union member's are added on top — see `members`. */
	values: TypeValueRequest[];
	/** Names of other types this one absorbs. Omit or send `[]` for an ordinary flat enum. */
	members: string[];
}

export interface ParsedDataobjectSummary {
	name: string;
	/** True when the code name was pinned by hand rather than derived from the term. A manual
	 *  name is never touched by the terminology cascade — surface it, or a rename looks broken. */
	manual_name: boolean;
	term_id: string;
	color: string;
	origin: OriginResponse;
	/** The target of each of its relations, one per relation — which dataobjects it relates to,
	 *  without a full read of each. */
	relation_targets: string[];
}

export interface ParsedAttributeResponse {
	name: string;
	/** True when the code name was pinned by hand rather than derived from the term. A manual
	 *  name is never touched by the terminology cascade — surface it, or a rename looks broken. */
	manual_name: boolean;
	type: string;
	optional?: boolean;
	term_id?: string;
	axiom?: AxiomConfigResponse;
}

export interface AttributeTreeNodeResponse {
	name: string;
	type: string;
	optional?: boolean;
	term_id?: string;
	is_array: boolean;
	children?: AttributeTreeNodeResponse[];
}

/** One field of a built-in payload (`GET /projects/{id}/native-dataobjects`). */
export interface NativeAttributeResponse {
	/** What a person writes in a path (`session_id`); always an identifier. */
	name: string;
	/** What the runtime payload holds it under: `name`, or the kebab-case key an Alternis stream uses. */
	key: string;
	/** `string`, `number`, `boolean`, `unknown` or another native's name, each maybe followed by `[]`. */
	type: string;
	/** The payload may leave it out, or carry `null`. */
	optional: boolean;
}

/** A built-in payload — a SIP call, an Alternis message, a process's answer — as the generator describes it. */
export interface NativeDataobjectResponse {
	name: string;
	/** What a person reads it as. */
	label: string;
	/** What it is and which output carries it. */
	description: string;
	attributes: NativeAttributeResponse[];
}

export interface ParsedRelationResponse {
	name: string;
	/** True when the code name was pinned by hand rather than derived from the term. A manual
	 *  name is never touched by the terminology cascade — surface it, or a rename looks broken. */
	manual_name: boolean;
	target: string;
	term_id?: string;
}

export interface GraphlyFillEntry {
	key: string;
	attribute: string;
	// Payload-field type from the template's JSON schema ("string" | "number" |
	// "integer" | "boolean" | "enum"). Lets the generator emit the default and
	// attribute read as the right JSON kind. Optional: legacy configs omit it.
	type?: string;
	default: string;
}

export interface GraphlyConfigResponse {
	shape: string;
	fill: GraphlyFillEntry[];
}

// ── Axiom ──
// Mirrors AxiomConfig/AxiomPremise/AxiomCaseConfig/PathSegmentConfig/PathSelectConfig 1:1 (see
// server's condition_dto for the shared ConditionConfigRequest/Response these reuse verbatim —
// axiom case/guard/find conditions address premises by name instead of attributes, same vocabulary).

export interface PathSegmentConfigRequest {
	name: string;
	select?: PathSelectConfigRequest;
}

export type PathSelectConfigRequest =
	| { kind: "nth"; index: number }
	| { kind: "find"; condition: ConditionConfigRequest };

export interface AxiomPremiseRequest {
	name: string;
	path: PathSegmentConfigRequest[];
}

export type AxiomCaseConfigRequest =
	| { kind: "compare"; condition: ConditionConfigRequest; target_value: string }
	| {
			kind: "formula";
			condition?: ConditionConfigRequest;
			forward: string;
			inverses: Record<string, string>;
	  };

export interface AxiomConfigRequest {
	premises: AxiomPremiseRequest[];
	cases: AxiomCaseConfigRequest[];
}

export interface PathSegmentConfigResponse {
	name: string;
	select?: PathSelectConfigResponse;
}

export type PathSelectConfigResponse =
	| { kind: "nth"; index: number }
	| { kind: "find"; condition: ConditionConfigResponse };

export interface AxiomPremiseResponse {
	name: string;
	path: PathSegmentConfigResponse[];
}

export type AxiomCaseConfigResponse =
	| { kind: "compare"; condition: ConditionConfigResponse; target_value: string }
	| {
			kind: "formula";
			condition?: ConditionConfigResponse;
			forward: string;
			inverses: Record<string, string>;
	  };

export interface AxiomConfigResponse {
	premises: AxiomPremiseResponse[];
	cases: AxiomCaseConfigResponse[];
}

export interface ParsedDataobjectResponse {
	name: string;
	/** True when the code name was pinned by hand rather than derived from the term. A manual
	 *  name is never touched by the terminology cascade — surface it, or a rename looks broken. */
	manual_name: boolean;
	term_id: string;
	color: string;
	attributes: ParsedAttributeResponse[];
	relations: ParsedRelationResponse[];
	graphly: GraphlyConfigResponse | null;
	origin: OriginResponse;
}

export interface DataobjectConfigRequest {
	/** `null`/omitted asks the generator to DERIVE the code name from the term — the normal case.
	 *  A value here pins the name manually, and the terminology cascade then never renames it. */
	name?: string | null;
	term_id: string;
	/** `null`/omitted keeps the dataobject's colour on an update. Never send `""`: the server
	 *  treats it as `null`, but older servers wrote it as an empty line that cost the dataobject
	 *  its graphly reference. */
	color?: string | null;
}

export interface AttributeConfigRequest {
	/** `null`/omitted asks the generator to DERIVE the code name from the term — the normal case.
	 *  A value here pins the name manually, and the terminology cascade then never renames it. */
	name?: string | null;
	type: string;
	optional?: boolean;
	term_id?: string;
}

export interface RelationConfigRequest {
	/** `null`/omitted asks the generator to DERIVE the code name from the term — the normal case.
	 *  A value here pins the name manually, and the terminology cascade then never renames it. */
	name?: string | null;
	target: string;
	term_id?: string;
}

export interface GraphlyConfigRequest {
	shape: string;
	fill: GraphlyFillEntry[];
}

export interface DataobjectViewResponse {
	id: string;
	name: string;
	description: string;
	tags: string[];
	dataobjects: string[];
	positions: string[];
}

export interface DataobjectViewConfigRequest {
	name: string;
	description?: string;
	tags?: string[];
}

export interface ViewDataobjectRequest {
	dataobject_id: string;
	position: string;
}

export interface ActionChannelRequest {
	channel: string;
	type: string;
}

export interface ActionInputRequest {
	name: string;
	type: string;
	sources?: string[];
}

export interface ActionAttributeRequest {
	name: string;
	type: string;
}

export interface ActionConditionRequest {
	source: string;
	function: string;
	argument?: string;
}

export interface ConditionConfigRequest {
	operator: string;
	conditions: ActionConditionRequest[];
}

/**
 * One test of an answer: a condition function applied to the answer, and what it is compared with.
 * There is no subject — it is the answer — which is why this is not an `ActionConditionRequest`.
 */
export interface ValidationCheck {
	/** One of the condition functions (`isDefined`, `isGreaterThan`, …). */
	function: string;
	/** A variable source (`lit:`, `env:`, `attr:`, `inp:`; never `tpl:`); absent for a function that
	 *  takes none. */
	argument?: string | null;
}

/**
 * A group of checks and the words said when an answer fails it. The rules of an input are checked in
 * order, and the first the answer fails refuses it. Request and response are the same shape.
 */
export interface ValidationRule {
	/** How the checks combine: AND (all must hold) or OR (one must). */
	operator: "AND" | "OR";
	/** At least one. */
	checks: ValidationCheck[];
	/** Template text (`${attr:name}` / `${inp:name}`), shown on an answer this rule refuses. The
	 *  Designer asks for one; the server takes an empty one, which refuses in silence. */
	message: string;
}

export interface QuestionInputKindRequest {
	type: string;
	/** Choices for the "select" kind — a WHITELIST narrowing `datatype`'s domain, or empty/absent
	 *  for the whole domain. Every entry must be a value the datatype actually admits. */
	options?: string[];
	/** Name of the DATATYPE whose domain a "select" admits. Required on write for a normal-mode
	 *  select. An `attribute_assigned` select whose attribute is typed by a Type leaves it out, and
	 *  the generator takes it from the attribute; one it does send must admit only values the
	 *  attribute's Type admits. Legacy on-disk activities may still parse without one. */
	datatype?: string;
	/** How a "select" is shown. Absent means "dropdown". */
	display?: SelectDisplay;
	/** A "select"'s values to show first, in this order; every other option follows in the order it
	 *  already has. ABSENT MEANS EMPTY: a save without it drops the order, so send back what was
	 *  read. Each entry is a value of the datatype's flattened domain, listed once. */
	order?: string[];
	/** A "select"'s own colour and picture per value, over the datatype's. Sparse, keyed by value.
	 *  ABSENT MEANS EMPTY, like `order` — and dropping an override drops its picture. */
	value_overrides?: Record<string, SelectValueOverride>;
	js_action?: string;
	dataobject_type?: string;
}

export type SelectDisplay = "dropdown" | "buttons";

/**
 * How one select shows one value, instead of the way its datatype does. An unset field falls back
 * to the datatype's (`TypeValue` colour or image), never to "none". The server drops an override
 * that sets nothing, so `{}` is never read back.
 */
export interface SelectValueOverride {
	/** A CSS hex colour: `#rgb`, `#rrggbb` or `#rrggbbaa`, stored as sent. */
	color?: string;
	/** The picture's file name, `<sha256>.<ext>`. A save sends back what it read; a new picture
	 *  goes through `setQuestionValueImage`, never through this field. */
	image?: string;
}

/** What the suggestion orchestrator does for one question input — see `QuestionInputConfigRequest`. */
export type SuggestionSource = "auto" | "provider" | "llm" | "none";

export const SUGGESTION_SOURCES: readonly SuggestionSource[] = ["auto", "provider", "llm", "none"];

export interface QuestionInputConfigRequest {
	name: string;
	label: string;
	description: string;
	placeholder: string;
	kind: QuestionInputKindRequest;
	value?: string;
	/** What an answer must satisfy, in order. A save sends back what it read: leaving it out clears
	 *  the rules. A button takes none. */
	validations?: ValidationRule[];
	disable_suggestions?: boolean;
	history_size?: number;
	/** Whether a voicebot must verbally confirm a suggestion before answering. Defaults to true when absent. Only affects voicebot behavior; ignored by the UI otherwise. */
	confirm?: boolean;
	/** One of "normal" (default) or "attribute_assigned". */
	mode?: string;
	/** Required when `mode` is "attribute_assigned" — the target attribute path. */
	attribute?: string;
	/** Whether the input still takes answers once its question is complete. Absent means false:
	 *  a completed question is recorded in its history and locked, except for these inputs. */
	editable_after_completion?: boolean;
	/** How a voicebot should ask and confirm this one field. Composed text like `description`.
	 *  A save sends back what it read: leaving it out clears the hint. */
	voice_hint?: string | null;
	/** Where the suggestion system gets this input's value: `"auto"` (absent means this), `"provider"`
	 *  (the registered provider or nothing, never the LLM — a `dataobject` input only), `"llm"` or
	 *  `"none"`. */
	suggestion_source?: SuggestionSource;
	/** On a `dataobject` input only: the attributes of its type an answer must hold to be complete.
	 *  An answer that lacks one is partial — the input's value, with the input still open. A save
	 *  sends back what it read: leaving it out clears the list. */
	required_fields?: string[];
	/** On a `Location` input only: the region the location service filters this input's answer by,
	 *  over the process's — a name exactly as the service has it (`Saarland`), or `"*"` for none.
	 *  Absent or `null`: the process's region. A save sends back what it read: leaving it out
	 *  clears it. */
	location_region?: string | null;
}

export interface ActionConfigRequest {
	type: string;
	pos: string;
	name: string;
	description?: string;
	color?: string;
	inputs?: ActionInputRequest[];
	/** The outputs' types, in order. For EMIT_QUESTION, an `attribute_assigned` input's output
	 *  type is derived by the generator from the attribute and replaces whatever is sent. */
	outputs?: string[];
	/** Optional, index-aligned with `outputs`. Purely descriptive labels; `null` leaves an output unlabeled. */
	output_labels?: (string | null)[];
	/** Optional, index-aligned with `outputs`: each existing output's channel, which it keeps, and
	 *  `null` for a new output, which the server names. Omitted, the stored channels are kept by
	 *  position. The names assigned come back in the response. */
	output_channels?: (string | null)[];
	attribute?: ActionAttributeRequest;
	event?: ActionAttributeRequest;
	body?: string;
	conditions?: ConditionConfigRequest;
	/** EMIT_QUESTION only: conditions that remove the question once they hold — on firing (it is
	 *  then not shown) and whenever an attribute they read changes. `attr:` sources only, each a
	 *  plain attribute; arguments `lit:`, `env:` or `attr:`. Combined by OR with the
	 *  `remove_when_assigned` interactable var. Omitted clears it, as `conditions`. */
	remove_when?: ConditionConfigRequest;
	alternis_vars?: Record<string, string>;
	interactable_vars?: Record<string, string>;
	question_inputs?: QuestionInputConfigRequest[];
	lifecycle_hook?: string;
	/** ATTRIBUTE_WRITER only: whether a rejected write also auto-emits a notification. Defaults to true when absent. */
	notify_on_failure?: boolean;
}

export interface ActionChannelResponse {
	channel: string;
	type: string;
	/** Purely descriptive, non-functional label a user assigned to this output. */
	label?: string;
}

export interface ActionInputResponse {
	name: string;
	type: string;
	sources: string[];
}

export interface ActionAttributeResponse {
	name: string;
	type: string;
}

export interface ActionConditionResponse {
	source: string;
	function: string;
	argument?: string;
}

export interface ConditionConfigResponse {
	operator: string;
	conditions: ActionConditionResponse[];
}

export interface QuestionInputKindResponse {
	type: string;
	/** Choices for the "select" kind — a WHITELIST narrowing `datatype`'s domain, or empty/absent
	 *  for the whole domain. Every entry must be a value the datatype actually admits. */
	options?: string[];
	/** Name of the DATATYPE whose domain a "select" admits. For an `attribute_assigned` select
	 *  saved without one, the datatype the generator took from the attribute. Legacy on-disk
	 *  activities may still parse without one. */
	datatype?: string;
	/** "dropdown" or "buttons" on a select; absent on every other kind. */
	display?: SelectDisplay;
	/** A select's values to show first — see `QuestionInputKindRequest.order`. Present, possibly
	 *  empty, on every select the server answers with; `null` on every other kind. */
	order?: string[] | null;
	/** A select's own colour and picture per value — see `QuestionInputKindRequest.value_overrides`.
	 *  Present, possibly empty, on every select the server answers with; `null` on every other kind. */
	value_overrides?: Record<string, SelectValueOverride> | null;
	js_action?: string;
	dataobject_type?: string;
}

export interface QuestionInputConfigResponse {
	name: string;
	label: string;
	description: string;
	placeholder: string;
	kind: QuestionInputKindResponse;
	value?: string;
	validations: ValidationRule[];
	disable_suggestions: boolean;
	history_size?: number;
	/** Whether a voicebot must verbally confirm a suggestion before answering. Only affects voicebot behavior; ignored by the UI otherwise. */
	confirm: boolean;
	/** "normal" or "attribute_assigned". */
	mode: string;
	/** Present only when `mode` is "attribute_assigned". */
	attribute?: string;
	editable_after_completion: boolean;
	/** How a voicebot asks and confirms this field; `null` when it has no hint. */
	voice_hint?: string | null;
	suggestion_source: SuggestionSource;
	/** The attributes a `dataobject` input's answer must hold; empty when it requires none. Absent
	 *  from a server older than the field. */
	required_fields?: string[];
	/** A `Location` input's own region for the location service, `"*"` for none; `null` when it
	 *  takes the process's. Absent from a server older than the field. */
	location_region?: string | null;
}

export interface ParsedActionResponse {
	id: string;
	type: string;
	pos: string;
	name: string;
	description: string;
	color: string;
	inputs: ActionInputResponse[];
	outputs: ActionChannelResponse[];
	attribute?: ActionAttributeResponse;
	event?: ActionAttributeResponse;
	body?: string;
	conditions?: ConditionConfigResponse;
	/** EMIT_QUESTION only — see `ActionConfigRequest.remove_when`. `null` (or absent, from a server
	 *  older than the field) when it has none. */
	remove_when?: ConditionConfigResponse | null;
	alternis_vars?: Record<string, string>;
	interactable_vars?: Record<string, string>;
	question_inputs?: QuestionInputConfigResponse[];
	lifecycle_hook?: string;
	notify_on_failure?: boolean;
	/**
	 * The id of the question that owns this action — the ATTRIBUTE_WRITER an `attribute_assigned`
	 * question input gets. Such an action cannot be deleted, and only its position, name,
	 * description and colour can change; anything else is `409 action_managed_by_dependency`.
	 * Server-owned: never sent back.
	 */
	depends_on?: string;
}

export interface ParsedActivityResponse {
	name: string;
	dataobject: string;
	label: string;
	description: string;
	tags: string[];
	actions: ParsedActionResponse[];
	/** A `dependency` activity is read-only: it and its actions read normally, and every write on
	 *  either is `409 dependency_entity_protected`. */
	origin: OriginResponse;
}

/**
 * One row of `GET /activities?include=dependencies`: enough to draw an activity in a list or tree
 * without a `GET /activities/{id}` per row. `name` is the activity's id, the same string the
 * default (local-only) list returns. Read it through `listActivityItems`.
 */
export interface ActivityListItemResponse {
	name: string;
	origin: OriginResponse;
	dataobject: string;
	label: string;
	description: string;
	tags: string[];
	action_count: number;
	/** What each of its DATAOBJECT_OPERATIONs names, as the generator reads it to refuse a delete. */
	dataobject_operations: DataobjectOperationRef[];
}

/** The literal names one DATAOBJECT_OPERATION holds; null where it sets none, or sets one by expression. */
export interface DataobjectOperationRef {
	action: string;
	concept: string | null;
	owner_concept: string | null;
	relation: string | null;
}

export interface ActivityConfigRequest {
	dataobject: string;
	label: string;
	description?: string;
	tags?: string[];
}

export interface CardinalityRequest {
	relation: string;
	min: number;
	max: number;
}

export interface CardinalityResponse {
	relation: string;
	min: number;
	max: number;
}

export interface EmbeddedActivityConfigRequest {
	/** Dot-separated attribute path through embedded (singular or array) dataobjects, e.g.
	 * "address" or "address.geo". */
	path: string;
	activity: string;
}

export interface EmbeddedActivityConfigResponse {
	path: string;
	activity: string;
}

export interface ProcessDataobjectConfigRequest {
	kind: string;
	dataobject: string;
	template: string;
	term_id: string;
	pos: string;
	activities?: string[];
	embedded_activities?: EmbeddedActivityConfigRequest[];
	cardinalities?: CardinalityRequest[];
	parent?: string;
	relation?: string;
}

export interface ParsedProcessDataobjectResponse {
	id: string;
	kind: string;
	pos: string;
	dataobject: string;
	template: string;
	term_id: string;
	activities: string[];
	embedded_activities: EmbeddedActivityConfigResponse[];
	cardinalities: CardinalityResponse[];
	parent?: string;
	relation?: string;
}

/**
 * What a process may do to other processes of its dataspace while it runs. `archive` also covers
 * bringing an archived instance back. There is no wildcard: "select all" is this whole list.
 */
export type ProcessOperation =
	| "spawn"
	| "send_event"
	| "list"
	| "stop"
	| "restart"
	| "archive"
	| "delete";

/** Every operation, in the order the model keeps them. */
export const PROCESS_OPERATIONS: readonly ProcessOperation[] = [
	"spawn",
	"send_event",
	"list",
	"stop",
	"restart",
	"archive",
	"delete",
];

/**
 * A grant: the process may do `operations` to the instances of the process definition `target` (its
 * code name, `p_…`). A grant on the process's own definition lets an instance act on its siblings
 * and spawn another of its own kind; acting on oneself needs none.
 */
export interface ProcessGrant {
	target: string;
	operations: ProcessOperation[];
}

export interface ParsedProcessResponse {
	name: string;
	label: string;
	description: string;
	tags: string[];
	/** The layout the process runs with. A process with none stored reads as `legacy`. */
	layout: ProcessLayout;
	/** What it may do to other processes while it runs. Changed only by `updateProcessGrants`. */
	grants: ProcessGrant[];
	dataobjects: ParsedProcessDataobjectResponse[];
}

export interface ProcessConfigRequest {
	label: string;
	description?: string;
	tags?: string[];
	/**
	 * How the operator is laid out. On create, absent is `explicit`. On update, absent KEEPS the
	 * stored one — send it only when the user changed it, so an edit of the label cannot turn a
	 * process that never chose into one that did.
	 */
	layout?: ProcessLayout;
}

export interface ParsedProcessSummary {
	name: string;
	label: string;
	description: string;
	tags: string[];
	dataobjects_count: number;
	/** The layout it runs with; a process with none stored reads as `legacy`. */
	layout: ProcessLayout;
	/** The dataobjects it holds, by code name: the root first, then the rest in the process's
	 *  order, each once. */
	dataobjects: string[];
	/** Every activity the process attaches, directly or on an embedded path: code names, sorted,
	 *  each once. */
	activities: string[];
}

export interface TerminologyLang {
	lang: string;
	term: string;
	synonyms: string[];
	description: string;
	unverified_fields: TerminologyGenerateField[];
	verified_by?: string;
	verified_at?: string;
}

export interface TerminologyEntryResponse {
	id: string;
	global_reference?: string;
	terminology: TerminologyLang[];
	/** `local`, `override` (this project's own label for a term `alias` also defines) or
	 *  `dependency` (only under `include=dependencies`; read-only until relabelled with a PUT). */
	origin: OriginResponse;
}

/**
 * What a terminology DELETE did. Reverting an override removes this project's entry but the term
 * LIVES ON in the dependency's label, so "deleted" and "reverted" are different outcomes: a client
 * that drops the id from its merged list on any delete would lose a term still in use.
 */
export interface TermDeleteResponse {
	/** True when the entry was an override and the dependency's label is now in effect. */
	reverted: boolean;
	/** The entry now in effect (the dependency's, tagged `dependency`) when `reverted`, else null. */
	entry: TerminologyEntryResponse | null;
}

export interface TerminologyConfigRequest {
	id?: string;
	global_reference?: string;
	terminology: TerminologyLang[];
}

export type TerminologyGenerateField = "term" | "description" | "synonyms";

export interface TerminologyGenerateSource {
	lang: string;
	term: string;
	synonyms: string[];
	description: string;
}

export interface TerminologyGenerateExisting {
	term: string;
	synonyms: string[];
	description: string;
}

/** A neighbouring concept (owning dataobject or relation endpoint), by its term + description. */
export interface TerminologyRelatedConcept {
	term: string;
	description: string;
}

/** The term's place in the information model, grounding generation on structure. */
export type TerminologyGenerateContext =
	| { kind: "dataobject" }
	| { kind: "attribute"; owner: TerminologyRelatedConcept }
	| { kind: "relation"; from: TerminologyRelatedConcept; to: TerminologyRelatedConcept }
	| { kind: "type_value"; owner: TerminologyRelatedConcept };

export interface GenerateTerminologyRequest {
	target_lang: string;
	fields: TerminologyGenerateField[];
	source_entry?: TerminologyGenerateSource;
	current_target?: TerminologyGenerateExisting;
	model_context?: TerminologyGenerateContext;
	fallback_name?: string;
}

export interface GeneratedTerminology {
	term?: string;
	description?: string;
	synonyms?: string[];
}

export interface MetadataResponse {
	term_id?: string;
	version?: string;
	languages: string[];
	/** What a failing build typecheck costs. Always one of the three — a project that never
	    configured one reads as `"warn"`, the default. */
	typecheck: TypecheckSeverity;
}

export interface MetadataConfigRequest {
	term_id?: string;
	/** Refused with `422 use_release`: a version is set by making a release (`createRelease`). */
	version?: string;
	languages?: string[];
	/** Anything outside the union is rejected with 400. */
	typecheck?: TypecheckSeverity;
}

/** One commit of the project's history — every change made in the Designer is one. */
export interface GitLogEntryResponse {
	/** The full commit id — what `after` takes back. */
	id: string;
	short_id: string;
	/** First line of the message: for a Designer change, a conventional commit. */
	summary: string;
	/** The rest of the message; empty for a one-line one. */
	body: string;
	author_name: string;
	author_email: string;
	/** ISO 8601 with the author's own UTC offset. */
	authored_at: string;
}

export interface GitLogResponse {
	/** Newest first. */
	entries: GitLogEntryResponse[];
	/** The `after` of the next, older page; null on the last one. */
	next_after: string | null;
}

export interface GitRemoteRequest {
	name: string;
	url: string;
}

export interface GitCommitRequest {
	message: string;
	name: string;
	email: string;
}

export interface BuildResultResponse {
	files: string[];
	duration_ms: number;
	success: boolean;
	version: string;
	build_path: string;
	/** Compiler output from a typecheck that failed WITHOUT failing the build — the project's
	    `typecheck` severity is `"warn"`, the default. `success` is still true and a bundle was
	    still produced; this is a diagnostic to show, not a failure. */
	typecheck: string | null;
	error: string | null;
}

export interface TypecheckResponse {
	/** `tsc --noEmit` output, or `null` when the project typechecks cleanly. */
	diagnostics: string | null;
}

/** The three `typecheck` severities the metadata route accepts. */
export const TYPECHECK_SEVERITIES = ["error", "warn", "off"] as const;
export type TypecheckSeverity = (typeof TYPECHECK_SEVERITIES)[number];

// ── Project Types API ──

/**
 * The `?include=dependencies` switch, on the four list routes.
 *
 * Every list is LOCAL-ONLY by default, on purpose: codenames, the project summary and terminology
 * coverage are computed from the same reads, and none of them should start counting a dependency's
 * entities because a route learned to show them. A surface that wants them says so here.
 */
export interface ListOptions {
	includeDependencies?: boolean;
}

function includeQuery(options: ListOptions): string {
	return options.includeDependencies ? "?include=dependencies" : "";
}

export async function listProjectTypes(
	projectId: string,
	options: ListOptions = {},
): Promise<ParsedTypeResponse[]> {
	return request<ParsedTypeResponse[]>(
		"GET",
		`/projects/${projectId}/types${includeQuery(options)}`,
		undefined,
		true,
	);
}

export async function createProjectType(
	projectId: string,
	data: TypeConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>("POST", `/projects/${projectId}/types`, data, true);
}

export async function updateProjectType(
	projectId: string,
	name: string,
	data: TypeConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>(
		"PUT",
		`/projects/${projectId}/types/${encodeURIComponent(name)}`,
		data,
		true,
	);
}

export async function deleteProjectType(projectId: string, name: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/types/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

function typeValueImagePath(projectId: string, typeName: string, value: string): string {
	return `/projects/${projectId}/types/${encodeURIComponent(typeName)}/values/${encodeURIComponent(value)}/image`;
}

/** Sets or replaces own value `value`'s image, and returns the new image's file name. The server
 *  reads the format from the bytes: PNG, GIF, JPEG, WebP or a static SVG, at most 1 MiB. */
export async function setTypeValueImage(
	projectId: string,
	typeName: string,
	value: string,
	file: Blob,
): Promise<string> {
	const form = new FormData();
	form.append("file", file);
	const res = await requestMultipart<{ image: string }>(
		"PUT",
		typeValueImagePath(projectId, typeName, value),
		form,
		true,
	);
	return res.image;
}

export async function clearTypeValueImage(
	projectId: string,
	typeName: string,
	value: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		typeValueImagePath(projectId, typeName, value),
		undefined,
		true,
	);
}

/** A project's type image, by file name. A Blob, not a URL, because the route needs the bearer
 *  token an `<img src>` cannot send — show it through `URL.createObjectURL`. */
export async function getTypeImage(projectId: string, image: string): Promise<Blob> {
	return requestBlob(
		"GET",
		`/projects/${projectId}/type-images/${encodeURIComponent(image)}`,
		true,
		"default",
	);
}

// ── Project Dependencies API ──

/**
 * One dependency pin. The pin itself is portable — it names a project and a version, never a
 * path — and everything else (the `file:` specifier, the re-export blocks, process import paths)
 * is re-derived from it, which is why `syncDependencies` repairs a moved store.
 */
export interface ParsedDependencyResponse {
	name: string;
	project_id: string;
	version: string;
	package_name: string;
	/** The source project's commit this pin was resolved against. */
	resolved?: string;
	/** Only ever true for a `wip` pin whose source moved. A released version cannot go stale. */
	stale: boolean;
	/** `false` when the package could not be read. The pin still lists and contributes nothing. */
	surface_available: boolean;
	/**
	 * Set only for a `wip` pin whose source moved in a way this project cannot absorb unreviewed.
	 * While it is set, every write that re-renders code is refused with `400
	 * dependency_impact_rejected` (and NO `details`) until `syncDependencies` adopts the move.
	 */
	pending: PendingDependencyResponse | null;
}

/** A moved `wip` surface held for review: what adopting it would break here. */
export interface PendingDependencyResponse {
	/** The commit the store's package now reports. */
	resolved?: string;
	/** Local references the new surface no longer satisfies. */
	impact: DependencyImpactResponse;
	/** Names the new surface exports that this project, or another dependency, already claims.
	 *  Unresolvable from this side — a source project has to rename — and `sync` refuses them. */
	collisions: string[];
}

export interface DependencyConfigRequest {
	/** `[a-z0-9_-]{1,64}`, unique in the project. Not editable — changing it is remove-then-add. */
	name: string;
	/** Not editable either, for the same reason. */
	project_id: string;
	/** An exact semver version or the literal `wip`. NEVER a range — always offer a picker. */
	version: string;
}

/**
 * The lists naming every local reference a change would orphan or break, by kind. One DTO for
 * the dry run, `pending.impact`, a forced apply, a `sync`, and `error.details.impact` of a refused
 * `update`/`remove` (read that one through `impactFromError`).
 *
 * The first six lists predate the rest.
 */
export interface DependencyImpactResponse {
	/** `"Ticket.reporter"` */
	attributes: string[];
	/** `"Ticket.watchers"` */
	relations: string[];
	/** `"p_abc/d_def"` */
	process_entries: string[];
	/** `"p_abc/d_def"`, or `"p_abc/d_def#address.geo"` for an embedded binding. */
	activity_bindings: string[];
	/** `"Ticket.priority"` */
	axioms: string[];
	/** `"a_abc#t_def"` — a best-effort TEXT SCAN over arbitrary user TypeScript. Worth a human
	 *  look, not exhaustive. */
	custom_action_bodies: string[];
	/** `"a_abc"` — a LOCAL activity built on a removed dataobject. */
	activities: string[];
	/** `"a_abc#t_def"` — an action whose input, output, attribute or event type, `dataobject`-kind
	 *  question input, or `select` datatype names a removed entity. */
	action_types: string[];
	/** `"Combined"` — a local union type spreading a removed type. */
	union_members: string[];
	/** `"v_abc"` — a view listing a removed dataobject. Stripped when applied forced. */
	views: string[];
	/** `"a_abc#t_def: reporter.given_name no longer resolves"` — an attribute path running into an
	 *  entity that SURVIVES but lost or retyped the attribute. */
	attribute_paths: string[];
	/** `"a_abc#t_def: Status.closed"` — a `select` option or condition literal naming a value the
	 *  surviving type no longer admits. */
	type_values: string[];
	/** `"term-team"` — a local relabelling of a term no remaining surface has and no local entity
	 *  uses. Stripped when applied. */
	terminology_overrides: string[];
	/** Whether every list is empty — the "safe to proceed" test. */
	is_empty: boolean;
	/** How many references the lists name in total. */
	count: number;
}

/** The lists of `DependencyImpactResponse` that hold references, in the order a dialog shows them. */
export const DEPENDENCY_IMPACT_LISTS = [
	"attributes",
	"relations",
	"process_entries",
	"activity_bindings",
	"axioms",
	"custom_action_bodies",
	"activities",
	"action_types",
	"union_members",
	"views",
	"attribute_paths",
	"type_values",
	"terminology_overrides",
] as const;
export type DependencyImpactList = (typeof DEPENDENCY_IMPACT_LISTS)[number];

/**
 * One place in the model that still uses what a remove would take away — a row of
 * `error.details.references` on a `409 attribute_in_use | type_in_use | type_value_in_use |
 * process_dataobject_in_use_by_action`. `site` is where, and what a link opens; `kind` is what the
 * site does with it. Read through `referencesFromError`; shown by `InUseReferencesList`.
 */
export interface InUseReference {
	site: InUseSite;
	kind: InUseKind;
}

export type InUseSite =
	/** An action: `activity` is its code name and `action` the id the activity editor opens on. */
	| {
			type: "action";
			activity: string;
			activity_label: string;
			action: string;
			action_label: string;
	  }
	/** An attribute: one typed by the type, the attribute whose axiom holds the value, or the one a
	 *  graphly field reads. */
	| { type: "attribute"; dataobject: string; attribute: string }
	/** Another type, which spreads the one being removed. */
	| { type: "type"; name: string }
	| { type: "process"; name: string };

export type InUseKind =
	| "writer"
	| "listener"
	| "var"
	| "condition"
	| "question_text"
	| "suggestion"
	| "graphly_fill"
	| "attribute_type"
	| "annotation"
	| "union_member"
	| "select_datatype"
	| "axiom_literal"
	| "operation_template";

const IN_USE_CODES: readonly string[] = [
	API_ERROR_CODES.attributeInUse,
	API_ERROR_CODES.typeInUse,
	API_ERROR_CODES.typeValueInUse,
	API_ERROR_CODES.processDataobjectInUseByAction,
];

/**
 * The places a refused remove says are still in the way, or `[]` when `e` is not one of the four
 * in-use refusals — or is one without a list, which an older server sends.
 */
export function referencesFromError(e: unknown): InUseReference[] {
	if (!(e instanceof ApiCallError) || !e.code || !IN_USE_CODES.includes(e.code)) return [];
	const details = e.details as { references?: InUseReference[] } | undefined;
	return Array.isArray(details?.references) ? details.references : [];
}

/**
 * The impact report a refused `update`/`remove` carries in `error.details.impact`, or `null` when
 * `e` is not that refusal — or is the refusal WITHOUT a report, which is what a held `wip` move
 * looks like (`dependency_impact_rejected` with no details: there, `pending` says what).
 */
export function impactFromError(e: unknown): DependencyImpactResponse | null {
	if (!(e instanceof ApiCallError) || e.code !== API_ERROR_CODES.dependencyImpactRejected)
		return null;
	const details = e.details as { impact?: DependencyImpactResponse } | undefined;
	return details?.impact ?? null;
}

export interface DependencyTypeResponse {
	name: string;
	manual_name: boolean;
	term_id: string;
	/** Own literals only — a union's members contribute on top. */
	values: string[];
	members: string[];
	/** Index-aligned with `values`: each value's term. */
	value_term_ids: string[];
	/** Index-aligned with `values`, `"none"` for a value without a colour. */
	value_colors: string[];
	/** Index-aligned with `values`, `"none"` for a value without an image. EMPTY for a type none of
	 *  whose values has one. */
	value_images: string[];
}

export interface DependencyDataobjectResponse {
	name: string;
	manual_name: boolean;
	term_id: string;
	color: string;
}

export interface DependencyActivityResponse {
	id: string;
	name: string;
	dataobject: string;
	label: string;
	description: string;
	/** How many actions the activity declares. The action list itself is not returned — a picker
	 *  only needs to know whether it does anything — but "0 actions" and "we did not look" are
	 *  different claims. */
	action_count: number;
}

/** One dependency's contributed entities, tagged with the alias that owns them. */
export interface DependencySurfaceResponse {
	alias: string;
	project_id: string;
	version: string;
	package_name: string;
	resolved?: string;
	stale: boolean;
	surface_available: boolean;
	pending: PendingDependencyResponse | null;
	types: DependencyTypeResponse[];
	dataobjects: DependencyDataobjectResponse[];
	activities: DependencyActivityResponse[];
	/** The dependency's OWN labels, each `origin: dependency`, before any local override. What
	 *  applies in this project is `listTerminology(…, { includeDependencies: true })`. */
	terminology: TerminologyEntryResponse[];
}

/** What a dependency `update`/`remove` returns when it applied: the impact it applied over (empty
 *  unless forced) at the top level, plus whatever the generator found worth saying. */
export type DependencyApplyResponse = DependencyImpactResponse & {
	diagnostics: DiagnosticResponse[];
};

/** What `addDependency` returns (it used to return nothing). */
export interface DependencyAddedResponse {
	diagnostics: DiagnosticResponse[];
}

/** `dependencies:changed`, either cause. The old shape carries only the first two fields — the
 *  echo of this project's own pin write; the rest arrive when a project this one DEPENDS ON was
 *  rebuilt at `wip` or released, sent to every socket in this project's room. */
export interface DependenciesChangedEvent {
	actor_id?: string;
	project_id?: string;
	source_project_id?: string;
	change?: "wip_rebuilt" | "released";
	version?: string;
}

/** `project:diagnostics`: findings raised where no response can carry them — a project reopened
 *  after its dependency's `wip` package moved, or a `sync`. `actor_id` only when a user's request
 *  produced them. */
export interface ProjectDiagnosticsEvent {
	project_id: string;
	actor_id?: string;
	diagnostics: DiagnosticResponse[];
}

export interface SyncImpactEntry {
	alias: string;
	impact: DependencyImpactResponse;
}

export interface ParsedNpmDependencyResponse {
	name: string;
	/** A semver RANGE (`^4.1.0`), unlike a project dependency's exact version. */
	version: string;
	dev: boolean;
}

export async function listDependencies(projectId: string): Promise<ParsedDependencyResponse[]> {
	return request<ParsedDependencyResponse[]>(
		"GET",
		`/projects/${projectId}/dependencies`,
		undefined,
		true,
	);
}

/**
 * The entities every dependency contributes.
 *
 * `listDataobjects`/`listProjectTypes`/`listActivities` stay LOCAL-ONLY by design, so no view
 * gains dependency entities by accident — every surface that wants them opts in here.
 */
export async function getDependencySurface(
	projectId: string,
): Promise<DependencySurfaceResponse[]> {
	return request<DependencySurfaceResponse[]>(
		"GET",
		`/projects/${projectId}/dependencies/surface`,
		undefined,
		true,
	);
}

/**
 * Needs `designer:build:read` on the SOURCE project as well as write here. A source the caller
 * cannot see is a `404`, indistinguishable from one that does not exist; a member of it who may not
 * build-read it gets a `403`. A `401` is only ever a session problem.
 */
export async function addDependency(
	projectId: string,
	data: DependencyConfigRequest,
): Promise<DependencyAddedResponse> {
	return request<DependencyAddedResponse>(
		"POST",
		`/projects/${projectId}/dependencies`,
		data,
		true,
	);
}

/** Dry run: pass a config for a prospective version change, or `null` for a removal. */
export async function getDependencyImpact(
	projectId: string,
	alias: string,
	config: DependencyConfigRequest | null,
): Promise<DependencyImpactResponse> {
	return request<DependencyImpactResponse>(
		"POST",
		`/projects/${projectId}/dependencies/${encodeURIComponent(alias)}/impact`,
		config,
		true,
	);
}

/**
 * Rejects with `dependency_impact_rejected` unless the impact is empty or `force` — and that
 * refusal carries the report (`impactFromError`). On success returns the impact it applied over.
 */
export async function updateDependency(
	projectId: string,
	alias: string,
	data: DependencyConfigRequest,
	force = false,
): Promise<DependencyApplyResponse> {
	return request<DependencyApplyResponse>(
		"PUT",
		`/projects/${projectId}/dependencies/${encodeURIComponent(alias)}?force=${force}`,
		data,
		true,
	);
}

/** See `updateDependency` for the `force` semantics. */
export async function removeDependency(
	projectId: string,
	alias: string,
	force = false,
): Promise<DependencyApplyResponse> {
	return request<DependencyApplyResponse>(
		"DELETE",
		`/projects/${projectId}/dependencies/${encodeURIComponent(alias)}?force=${force}`,
		undefined,
		true,
	);
}

/**
 * Re-resolves every pin, moving `wip` pins to their source's current content — and adopts a moved
 * `wip` surface held as `pending`, which is the review's conclusion. Refuses a name collision with
 * `409 dependency_name_collision`. Its diagnostics arrive as a `project:diagnostics` event: the
 * response stays a bare array.
 */
export async function syncDependencies(projectId: string): Promise<SyncImpactEntry[]> {
	return request<SyncImpactEntry[]>(
		"POST",
		`/projects/${projectId}/dependencies/sync`,
		undefined,
		true,
	);
}

export async function listNpmDependencies(
	projectId: string,
): Promise<ParsedNpmDependencyResponse[]> {
	return request<ParsedNpmDependencyResponse[]>(
		"GET",
		`/projects/${projectId}/dependencies/npm`,
		undefined,
		true,
	);
}

/** Validated OFFLINE only. A package that does not exist surfaces at build time. */
export async function addNpmDependency(
	projectId: string,
	data: { name: string; version: string; dev: boolean },
): Promise<void> {
	await request<unknown>("POST", `/projects/${projectId}/dependencies/npm`, data, true);
}

export async function updateNpmDependency(
	projectId: string,
	data: { name: string; version: string; dev: boolean },
): Promise<void> {
	await request<unknown>("PUT", `/projects/${projectId}/dependencies/npm`, data, true);
}

export async function removeNpmDependency(projectId: string, name: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/dependencies/npm/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

/**
 * The versions a project has built, newest first with `wip` at the front.
 *
 * `projectId` is the SOURCE project, and the call is authorized against ITS `designer:build:read`
 * — so this 403s for a project the user cannot see, by design.
 */
export async function listLibraryVersions(projectId: string): Promise<string[]> {
	return request<string[]>("GET", `/projects/${projectId}/library/versions`, undefined, true);
}

// ── Project Utilities API ──

/**
 * `utilities.ts` — the one entirely hand-written file in a project. Every activity imports it as
 * `UTILITIES`, so anything exported is callable from any CUSTOM_ACTION body.
 *
 * Project-PRIVATE: it is never shared through a dependency, even though the file travels inside
 * the package.
 */
export async function getUtilities(projectId: string): Promise<string> {
	const res = await request<{ content: string }>(
		"GET",
		`/projects/${projectId}/utilities`,
		undefined,
		true,
	);
	return res.content;
}

/** Replaces the file wholesale. Returns the STORED contents — the server appends an
 *  `export {};` marker when nothing is exported, and an editor holding the un-marked text would
 *  otherwise send it straight back on the next save. */
export async function updateUtilities(projectId: string, content: string): Promise<string> {
	const res = await request<{ content: string }>(
		"PUT",
		`/projects/${projectId}/utilities`,
		{ content },
		true,
	);
	return res.content;
}

/** Restores the scaffold, discarding whatever was there. */
export async function resetUtilities(projectId: string): Promise<string> {
	const res = await request<{ content: string }>(
		"DELETE",
		`/projects/${projectId}/utilities`,
		undefined,
		true,
	);
	return res.content;
}

// ── Project Landing API ──

/**
 * A project's start page ("Startseite"): one HTML file a build ships, which the operator shows as
 * the entry of a dataspace running the project. `enabled` is whether a build ships it.
 *
 * Every write answers with the page AS STORED, and a client adopts that rather than what it sent:
 * switching a page on that was never written also writes the scaffold, so the answer is what a
 * build will ship.
 */
export interface LandingPage {
	html: string;
	enabled: boolean;
}

/** The generator's cap on a page, in bytes; a larger one is a 400. Checked here too, so the editor
 *  can say so before sending a megabyte. */
export const MAX_LANDING_HTML_BYTES = 1024 * 1024;

export async function getLanding(projectId: string): Promise<LandingPage> {
	return request<LandingPage>("GET", `/projects/${projectId}/landing`, undefined, true);
}

/** Replaces the page's HTML. Does not switch it on or off. */
export async function updateLandingHtml(projectId: string, html: string): Promise<LandingPage> {
	return request<LandingPage>("PUT", `/projects/${projectId}/landing/html`, { html }, true);
}

export async function updateLandingEnabled(
	projectId: string,
	enabled: boolean,
): Promise<LandingPage> {
	return request<LandingPage>("PUT", `/projects/${projectId}/landing/enabled`, { enabled }, true);
}

/** Discards the page: back to the scaffold, switched off. */
export async function resetLanding(projectId: string): Promise<LandingPage> {
	return request<LandingPage>("DELETE", `/projects/${projectId}/landing`, undefined, true);
}

// ── Project Code Names API ──

/** Which kind of entity a derived name is for — the naming style differs per kind. */
export type CodenameKind = "dataobject" | "type" | "attribute" | "relation" | "type_value";

/** One change the code-name cleanup makes: a rename, a released pin, a new term, or several. */
export interface CodenameChangeResponse {
	kind: CodenameKind;
	/** The dataobject an attribute/relation belongs to, or the type a value belongs to. */
	owner?: string;
	from: string;
	/** The derived name; equal to `from` when only the flags below change something. */
	to: string;
	/** It had no usable term: one is made with `from` as its English label, marked unverified. */
	creates_term: boolean;
	/** Its name was set by hand, and is released: terminology edits rename it again. */
	releases_manual: boolean;
}

/**
 * The code name `label` would derive to, resolved against this project.
 *
 * Always ask the server. Derivation reads the term's ENGLISH label regardless of the language
 * being edited, applies a per-kind naming style and disambiguates against the project's existing
 * vocabulary — none of which is reproducible client-side.
 *
 * `owner` narrows what the name is disambiguated against: for a `type_value`, the type it belongs
 * to (its values are the namespace); for a `type`, that type's own current name, so an existing
 * type does not collide with itself. Other kinds ignore it.
 */
export async function previewCodename(
	projectId: string,
	label: string,
	kind: CodenameKind,
	owner?: string,
): Promise<string> {
	const res = await request<{ name: string }>(
		"POST",
		`/projects/${projectId}/codenames/preview`,
		owner ? { label, kind, owner } : { label, kind },
		true,
	);
	return res.name;
}

/** Every rename the derived-name rules want, without applying any. */
export async function previewNormalizeCodenames(
	projectId: string,
): Promise<CodenameChangeResponse[]> {
	return request<CodenameChangeResponse[]>(
		"GET",
		`/projects/${projectId}/codenames/normalize`,
		undefined,
		true,
	);
}

/** Applies every change the dry run listed, and returns the same list. */
export async function normalizeCodenames(projectId: string): Promise<CodenameChangeResponse[]> {
	return request<CodenameChangeResponse[]>(
		"POST",
		`/projects/${projectId}/codenames/normalize`,
		undefined,
		true,
	);
}

// ── Project Dataobjects API ──

export async function listDataobjects(
	projectId: string,
	options: ListOptions = {},
): Promise<ParsedDataobjectSummary[]> {
	return request<ParsedDataobjectSummary[]>(
		"GET",
		`/projects/${projectId}/dataobjects${includeQuery(options)}`,
		undefined,
		true,
	);
}

export async function getDataobject(
	projectId: string,
	name: string,
): Promise<ParsedDataobjectResponse> {
	return request<ParsedDataobjectResponse>(
		"GET",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

export async function createDataobject(
	projectId: string,
	data: DataobjectConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>("POST", `/projects/${projectId}/dataobjects`, data, true);
}

export async function updateDataobject(
	projectId: string,
	name: string,
	data: DataobjectConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(name)}`,
		data,
		true,
	);
}

export async function deleteDataobject(projectId: string, name: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

// ── Dataobject Attributes API ──

export async function listAttributes(
	projectId: string,
	doName: string,
): Promise<ParsedAttributeResponse[]> {
	return request<ParsedAttributeResponse[]>(
		"GET",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes`,
		undefined,
		true,
	);
}

export async function getAttributeTree(
	projectId: string,
	doName: string,
): Promise<AttributeTreeNodeResponse[]> {
	return request<AttributeTreeNodeResponse[]>(
		"GET",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/tree`,
		undefined,
		true,
	);
}

/** Every built-in payload an action's input or output may be typed with. The same for every project. */
export async function getNativeDataobjects(projectId: string): Promise<NativeDataobjectResponse[]> {
	return request<NativeDataobjectResponse[]>(
		"GET",
		`/projects/${projectId}/native-dataobjects`,
		undefined,
		true,
	);
}

export async function addAttribute(
	projectId: string,
	doName: string,
	data: AttributeConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>(
		"POST",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes`,
		data,
		true,
	);
}

export async function updateAttribute(
	projectId: string,
	doName: string,
	attrName: string,
	data: AttributeConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/${encodeURIComponent(attrName)}`,
		data,
		true,
	);
}

export async function deleteAttribute(
	projectId: string,
	doName: string,
	attrName: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/${encodeURIComponent(attrName)}`,
		undefined,
		true,
	);
}

export async function addAxiom(
	projectId: string,
	doName: string,
	attrName: string,
	data: AxiomConfigRequest,
): Promise<void> {
	await request<unknown>(
		"POST",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/${encodeURIComponent(attrName)}/axiom`,
		data,
		true,
	);
}

export async function updateAxiom(
	projectId: string,
	doName: string,
	attrName: string,
	data: AxiomConfigRequest,
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/${encodeURIComponent(attrName)}/axiom`,
		data,
		true,
	);
}

export async function removeAxiom(
	projectId: string,
	doName: string,
	attrName: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/${encodeURIComponent(attrName)}/axiom`,
		undefined,
		true,
	);
}

/** Reorder attributes. `order` must be an exact permutation of the current attribute names. */
export async function reorderAttributes(
	projectId: string,
	doName: string,
	order: string[],
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/attributes/reorder`,
		{ order },
		true,
	);
}

// ── Dataobject Relations API ──

export async function listRelations(
	projectId: string,
	doName: string,
): Promise<ParsedRelationResponse[]> {
	return request<ParsedRelationResponse[]>(
		"GET",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/relations`,
		undefined,
		true,
	);
}

export async function addRelation(
	projectId: string,
	doName: string,
	data: RelationConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>(
		"POST",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/relations`,
		data,
		true,
	);
}

export async function updateRelation(
	projectId: string,
	doName: string,
	relName: string,
	data: RelationConfigRequest,
): Promise<ResolvedNameResponse> {
	return request<ResolvedNameResponse>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/relations/${encodeURIComponent(relName)}`,
		data,
		true,
	);
}

export async function deleteRelation(
	projectId: string,
	doName: string,
	relName: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/relations/${encodeURIComponent(relName)}`,
		undefined,
		true,
	);
}

/** Reorder relations. `order` must be an exact permutation of the current relation names. */
export async function reorderRelations(
	projectId: string,
	doName: string,
	order: string[],
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/relations/reorder`,
		{ order },
		true,
	);
}

// ── Dataobject Graphly API ──

export async function getGraphly(
	projectId: string,
	doName: string,
): Promise<GraphlyConfigResponse | null> {
	return request<GraphlyConfigResponse | null>(
		"GET",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/graphly`,
		undefined,
		true,
	);
}

export async function setGraphly(
	projectId: string,
	doName: string,
	data: GraphlyConfigRequest,
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/dataobjects/${encodeURIComponent(doName)}/graphly`,
		data,
		true,
	);
}

// ── Dataobject Views API ──

export async function listViews(projectId: string): Promise<DataobjectViewResponse[]> {
	return request<DataobjectViewResponse[]>(
		"GET",
		`/projects/${projectId}/views`,
		undefined,
		true,
	);
}

export async function getView(projectId: string, viewId: string): Promise<DataobjectViewResponse> {
	return request<DataobjectViewResponse>(
		"GET",
		`/projects/${projectId}/views/${viewId}`,
		undefined,
		true,
	);
}

export async function createView(
	projectId: string,
	data: DataobjectViewConfigRequest,
): Promise<DataobjectViewResponse> {
	return request<DataobjectViewResponse>("POST", `/projects/${projectId}/views`, data, true);
}

export async function updateView(
	projectId: string,
	viewId: string,
	data: DataobjectViewConfigRequest,
): Promise<void> {
	await request<unknown>("PUT", `/projects/${projectId}/views/${viewId}`, data, true);
}

export async function deleteView(projectId: string, viewId: string): Promise<void> {
	await request<unknown>("DELETE", `/projects/${projectId}/views/${viewId}`, undefined, true);
}

export async function addViewDataobject(
	projectId: string,
	viewId: string,
	data: ViewDataobjectRequest,
): Promise<void> {
	await request<unknown>(
		"POST",
		`/projects/${projectId}/views/${viewId}/dataobjects`,
		data,
		true,
	);
}

export async function updateViewDataobjectPosition(
	projectId: string,
	viewId: string,
	doId: string,
	position: string,
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/views/${viewId}/dataobjects/${doId}`,
		{ position },
		true,
	);
}

export async function removeViewDataobject(
	projectId: string,
	viewId: string,
	doId: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/views/${viewId}/dataobjects/${doId}`,
		undefined,
		true,
	);
}

// ── Thumbnails API (generic per-entity) ──

export type ThumbnailKind = "infmodel" | "process" | "activity";

function thumbnailKey(kind: ThumbnailKind, entityId: string): string {
	return `${kind}-${entityId}`;
}

export async function getThumbnail(
	projectId: string,
	kind: ThumbnailKind,
	entityId: string,
	theme: "light" | "dark" = "light",
	cacheBust: number = Date.now(),
): Promise<Blob> {
	const key = thumbnailKey(kind, entityId);
	return requestBlob(
		"GET",
		`/projects/${projectId}/thumbnails/${key}?theme=${theme}&cb=${cacheBust}`,
		true,
	);
}

export async function uploadThumbnail(
	projectId: string,
	kind: ThumbnailKind,
	entityId: string,
	thumbnail: Blob,
	theme: "light" | "dark" = "light",
): Promise<void> {
	const key = thumbnailKey(kind, entityId);
	const form = new FormData();
	form.append("theme", theme);
	form.append("file", thumbnail, `${key}.png`);
	await requestMultipart("PUT", `/projects/${projectId}/thumbnails/${key}`, form, true);
}

export async function deleteThumbnail(
	projectId: string,
	kind: ThumbnailKind,
	entityId: string,
	theme: "light" | "dark" = "light",
): Promise<void> {
	const key = thumbnailKey(kind, entityId);
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/thumbnails/${key}?theme=${theme}`,
		undefined,
		true,
	);
}

// ── Activities API ──

/** This project's OWN activity ids. For dependency activities too, use `listActivityItems`. */
export async function listActivities(projectId: string): Promise<string[]> {
	return request<string[]>("GET", `/projects/${projectId}/activities`, undefined, true);
}

/**
 * Every activity in scope — this project's own, then each dependency's — as origin-tagged items.
 *
 * `GET /activities` answers in one of two shapes chosen by the REQUEST: bare ids by default
 * (unchanged, because every older caller reads a string array) and item objects under
 * `?include=dependencies`. That union is confined to this one function, which always asks for the
 * second, so nothing else has to narrow it.
 */
export async function listActivityItems(projectId: string): Promise<ActivityListItemResponse[]> {
	return request<ActivityListItemResponse[]>(
		"GET",
		`/projects/${projectId}/activities?include=dependencies`,
		undefined,
		true,
	);
}

export async function getActivity(
	projectId: string,
	name: string,
): Promise<ParsedActivityResponse> {
	return request<ParsedActivityResponse>(
		"GET",
		`/projects/${projectId}/activities/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

export async function createActivity(
	projectId: string,
	data: ActivityConfigRequest,
): Promise<ParsedActivityResponse> {
	return request<ParsedActivityResponse>("POST", `/projects/${projectId}/activities`, data, true);
}

export async function updateActivity(
	projectId: string,
	name: string,
	data: ActivityConfigRequest,
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/activities/${encodeURIComponent(name)}`,
		data,
		true,
	);
}

export async function deleteActivity(projectId: string, name: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/activities/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

export async function listActions(
	projectId: string,
	activityName: string,
): Promise<ParsedActionResponse[]> {
	return request<ParsedActionResponse[]>(
		"GET",
		`/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions`,
		undefined,
		true,
	);
}

/** What an action write answers: the action, and every output's channel, in order. */
export interface ActionChannelsResponse {
	output_channels: string[];
}

export async function addAction(
	projectId: string,
	activityName: string,
	data: ActionConfigRequest,
): Promise<{ id: string } & ActionChannelsResponse> {
	return request<{ id: string } & ActionChannelsResponse>(
		"POST",
		`/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions`,
		data,
		true,
	);
}

export async function updateAction(
	projectId: string,
	activityName: string,
	actionId: string,
	data: ActionConfigRequest,
): Promise<ActionChannelsResponse> {
	return request<ActionChannelsResponse>(
		"PUT",
		`/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions/${actionId}`,
		data,
		true,
	);
}

export async function deleteAction(
	projectId: string,
	activityName: string,
	actionId: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions/${actionId}`,
		undefined,
		true,
	);
}

/**
 * Reorder an EMIT_QUESTION action's outputs (and paired question inputs). `order` is a permutation
 * of the current 0-based output indices (`order[newPosition] = oldIndex`). The server also remaps
 * every subscriber whose wiring referenced a moved output, so refetch the activity afterwards.
 */
export async function reorderQuestionOutputs(
	projectId: string,
	activityName: string,
	actionId: string,
	order: number[],
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions/${actionId}/outputs/reorder`,
		{ order },
		true,
	);
}

function questionValueImagePath(
	projectId: string,
	activityName: string,
	actionId: string,
	input: string,
	value: string,
): string {
	return `/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions/${actionId}/question-inputs/${encodeURIComponent(input)}/values/${encodeURIComponent(value)}/image`;
}

/**
 * Sets or replaces the picture question `actionId` shows for `value` on its select input `input`
 * (the input's `name`), over the one the datatype gives the value, and returns its file name —
 * what the select's `value_overrides[value].image` now says. The same checks as a type value's
 * picture: PNG, GIF, JPEG, WebP or a static SVG, at most 1 MiB, read from the bytes.
 */
export async function setQuestionValueImage(
	projectId: string,
	activityName: string,
	actionId: string,
	input: string,
	value: string,
	file: Blob,
): Promise<string> {
	const form = new FormData();
	form.append("file", file);
	const res = await requestMultipart<{ image: string }>(
		"PUT",
		questionValueImagePath(projectId, activityName, actionId, input, value),
		form,
		true,
	);
	return res.image;
}

/** Takes the question's own picture off `value`, which then shows the datatype's again. */
export async function clearQuestionValueImage(
	projectId: string,
	activityName: string,
	actionId: string,
	input: string,
	value: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		questionValueImagePath(projectId, activityName, actionId, input, value),
		undefined,
		true,
	);
}

export interface ActionContextResponse {
	prefix: string;
	suffix: string;
	// Same split, bounded to just this action's own doc comment through its own
	// function's matching closing brace — the editor's default (non-debug) view.
	isolated_prefix: string;
	isolated_suffix: string;
	/** For a `dependency` activity the source is read from the dependency's package, so the body
	 *  must be shown read-only. */
	origin: OriginResponse;
}

// Fetch the generated activity source split around a CUSTOM_ACTION's body, so the
// editor can show the whole file as read-only context with only the body editable.
export async function getActionContext(
	projectId: string,
	activityName: string,
	actionId: string,
): Promise<ActionContextResponse> {
	return request<ActionContextResponse>(
		"GET",
		`/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions/${actionId}/context`,
		undefined,
		true,
	);
}

// Fetch the combined TypeScript editor type declarations (engine globals plus the
// project's own Types/Dataobjects namespaces) for Monaco IntelliSense. The response
// is raw TypeScript source (not JSON) — register it via `addExtraLib` under a `.ts`
// virtual path, not `.d.ts`: the Types/Dataobjects namespaces carry real method
// bodies, which a strict ambient `.d.ts` context forbids.
export async function getEditorTypes(projectId: string): Promise<string> {
	return requestText("GET", `/projects/${projectId}/build/editor-types`, true);
}

export interface AssistChatMessage {
	role: "user" | "assistant";
	content: string;
}

export interface AssistRequest {
	messages: AssistChatMessage[];
	context: string;
	current_body: string;
}

export interface AssistResponse {
	/** Proposed body, or null when no code change is needed (e.g. a question). */
	body: string | null;
	explanation: string;
}

// Callbacks invoked with the model's streamed output as it arrives.
export interface AssistStreamHandlers {
	/** A chunk of the model's reasoning ("thinking"). */
	onReasoning?: (text: string) => void;
	/** A chunk of the human-readable explanation. */
	onDelta?: (text: string) => void;
}

// Parse one SSE event block ("event: …\n data: …" lines). Multiple `data:` lines
// are joined with newlines per the SSE spec; keep-alive comment lines yield null.
function parseSseEvent(raw: string): { event: string; data: string } | null {
	let event = "message";
	const data: string[] = [];
	for (const line of raw.split("\n")) {
		if (line.startsWith("event:")) event = line.slice(6).trim();
		else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
	}
	return data.length ? { event, data: data.join("\n") } : null;
}

// Ask the server-proxied LLM gateway to write/modify a CUSTOM_ACTION body. The
// gateway credentials live server-side. The reply streams back as SSE: reasoning
// and explanation chunks fire the handlers as they arrive; the resolved value is
// the authoritative parsed body + explanation (from the terminal `done` event).
export async function assistActionCode(
	projectId: string,
	activityName: string,
	actionId: string,
	data: AssistRequest,
	handlers: AssistStreamHandlers = {},
): Promise<AssistResponse> {
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	const sid = getSocketId();
	if (sid) headers["X-Socket-Id"] = sid;

	const res = await send(true, (bearer) =>
		fetch(
			`/api/v2/projects/${projectId}/activities/${encodeURIComponent(activityName)}/actions/${actionId}/assist`,
			{ method: "POST", headers: withBearer(headers, bearer), body: JSON.stringify(data) },
		),
	);
	if (!res.ok || !res.body) throw await failure(res, `/projects/${projectId}/assist`);

	const reader = res.body.getReader();
	const decoder = new TextDecoder();
	let buf = "";
	let result: AssistResponse | null = null;
	for (;;) {
		const { value, done } = await reader.read();
		if (done) break;
		buf += decoder.decode(value, { stream: true });
		let sep: number;
		while ((sep = buf.indexOf("\n\n")) >= 0) {
			const ev = parseSseEvent(buf.slice(0, sep));
			buf = buf.slice(sep + 2);
			if (!ev) continue;
			if (ev.event === "reasoning") handlers.onReasoning?.(ev.data);
			else if (ev.event === "delta") handlers.onDelta?.(ev.data);
			else if (ev.event === "done") result = JSON.parse(ev.data) as AssistResponse;
		}
	}
	if (!result) throw new ApiCallError("Assist stream ended without a result", 502);
	return result;
}

// ── Processes API ──

export async function listProcesses(projectId: string): Promise<ParsedProcessSummary[]> {
	return request<ParsedProcessSummary[]>(
		"GET",
		`/projects/${projectId}/processes`,
		undefined,
		true,
	);
}

export async function getProcess(projectId: string, name: string): Promise<ParsedProcessResponse> {
	return request<ParsedProcessResponse>(
		"GET",
		`/projects/${projectId}/processes/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

export async function createProcess(
	projectId: string,
	data: ProcessConfigRequest,
): Promise<ParsedProcessResponse> {
	return request<ParsedProcessResponse>("POST", `/projects/${projectId}/processes`, data, true);
}

export async function updateProcess(
	projectId: string,
	name: string,
	data: ProcessConfigRequest,
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/processes/${encodeURIComponent(name)}`,
		data,
		true,
	);
}

/**
 * Replaces a process's grants as a whole: what is not in `grants` is revoked, `[]` revokes
 * everything. Answers with the grants as stored — one per target, operations in the model's order,
 * a grant of nothing dropped — which is not always what was sent. An operation the server does not
 * know is `400 invalid_grant_operation`; a target that is not a process of the project is a 400.
 */
export async function updateProcessGrants(
	projectId: string,
	name: string,
	grants: ProcessGrant[],
): Promise<ProcessGrant[]> {
	const response = await request<{ grants: ProcessGrant[] }>(
		"PUT",
		`/projects/${projectId}/processes/${encodeURIComponent(name)}/grants`,
		{ grants },
		true,
	);
	return response.grants;
}

export async function deleteProcess(projectId: string, name: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/processes/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

export async function listProcessDataobjects(
	projectId: string,
	processName: string,
): Promise<ParsedProcessDataobjectResponse[]> {
	return request<ParsedProcessDataobjectResponse[]>(
		"GET",
		`/projects/${projectId}/processes/${encodeURIComponent(processName)}/dataobjects`,
		undefined,
		true,
	);
}

export async function addProcessDataobject(
	projectId: string,
	processName: string,
	data: ProcessDataobjectConfigRequest,
): Promise<{ id: string }> {
	return request<{ id: string }>(
		"POST",
		`/projects/${projectId}/processes/${encodeURIComponent(processName)}/dataobjects`,
		data,
		true,
	);
}

export async function updateProcessDataobject(
	projectId: string,
	processName: string,
	doId: string,
	data: ProcessDataobjectConfigRequest,
): Promise<void> {
	await request<unknown>(
		"PUT",
		`/projects/${projectId}/processes/${encodeURIComponent(processName)}/dataobjects/${doId}`,
		data,
		true,
	);
}

export async function deleteProcessDataobject(
	projectId: string,
	processName: string,
	doId: string,
): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/processes/${encodeURIComponent(processName)}/dataobjects/${doId}`,
		undefined,
		true,
	);
}

// ── Terminology API ──

/**
 * The default list is this project's own `terminology.json` — including an entry that overrides a
 * dependency's label (`origin.kind: "override"`). `includeDependencies` adds the dependency terms
 * nothing here overrides, tagged `dependency`: one entry per id, the local one winning.
 */
export async function listTerminology(
	projectId: string,
	options: ListOptions = {},
): Promise<TerminologyEntryResponse[]> {
	return request<TerminologyEntryResponse[]>(
		"GET",
		`/projects/${projectId}/terminology${includeQuery(options)}`,
		undefined,
		true,
	);
}

export async function getTerm(
	projectId: string,
	termId: string,
): Promise<TerminologyEntryResponse> {
	return request<TerminologyEntryResponse>(
		"GET",
		`/projects/${projectId}/terminology/${termId}`,
		undefined,
		true,
	);
}

export async function createTerm(
	projectId: string,
	data: TerminologyConfigRequest,
): Promise<TerminologyEntryResponse> {
	return request<TerminologyEntryResponse>(
		"POST",
		`/projects/${projectId}/terminology`,
		data,
		true,
	);
}

/**
 * Returns the entry now in effect. On a term only a DEPENDENCY defines this writes a local
 * override instead of failing, and the returned `origin` is then `override` — which is how a
 * client learns that its first edit of a dependency's label created one.
 */
export async function updateTerm(
	projectId: string,
	termId: string,
	data: TerminologyConfigRequest,
): Promise<TerminologyEntryResponse> {
	return request<TerminologyEntryResponse>(
		"PUT",
		`/projects/${projectId}/terminology/${termId}`,
		data,
		true,
	);
}

export async function verifyTerm(
	projectId: string,
	termId: string,
	lang?: string,
): Promise<TerminologyEntryResponse> {
	return request<TerminologyEntryResponse>(
		"POST",
		`/projects/${projectId}/terminology/${termId}/verify`,
		lang ? { lang } : {},
		true,
	);
}

/**
 * On an `override` this REVERTS to the dependency's label: `reverted: true` and `entry` is that
 * label. On a plain local term it deletes (`reverted: false`, `entry: null`). On a term only a
 * dependency defines it is `409 dependency_entity_protected` — there is nothing local to delete.
 */
export async function deleteTerm(projectId: string, termId: string): Promise<TermDeleteResponse> {
	return request<TermDeleteResponse>(
		"DELETE",
		`/projects/${projectId}/terminology/${termId}`,
		undefined,
		true,
	);
}

export async function generateTerminology(
	projectId: string,
	data: GenerateTerminologyRequest,
	/** Aborting drops the request, and with it the gateway call the server is waiting on. */
	signal?: AbortSignal,
): Promise<GeneratedTerminology> {
	return request<GeneratedTerminology>(
		"POST",
		`/projects/${projectId}/terminology/generate`,
		data,
		true,
		signal,
	);
}

/** A type image from the build a dataspace runs, by the file name its schema carries. See
 *  `getTypeImage` for why this is a Blob. */
export async function getDataspaceImage(dataspaceId: string, image: string): Promise<Blob> {
	return requestBlob(
		"GET",
		`/dataspaces/${dataspaceId}/images/${encodeURIComponent(image)}`,
		true,
		"default",
	);
}

// ── The Operator's map ──
// Tiles and addresses come through the server, never from the tile server or the location
// service directly: see `server/src/geo.rs` and `client/docs/privacy-storage.md`.

export interface GeoConfigResponse {
	/** The map is offered at all: a tile server is set and the map is not turned off. */
	enabled: boolean;
	/** Address search is offered: a location service is set. */
	geocoding: boolean;
	/** The address at a pin is offered (location service 1.2.0 and later). */
	reverse: boolean;
	/** What the map must show about its tiles, as their licence requires. */
	attribution: string;
}

/**
 * How exactly a place is where its coordinates say: a building, somewhere on a street (the
 * service knows one point per street), or a town with no point at all.
 */
export type GeoPrecision = "poi" | "street" | "city";

/** A place, in the predefined Location's own fields — what a Location input is answered with. */
export interface GeoPlaceResponse {
	/** Absent for a town, and for every place from a location service older than 1.2.0. */
	latitude?: number;
	longitude?: number;
	street?: string;
	house_number?: string;
	postal_code?: string;
	city?: string;
	country?: string;
	poi_name?: string;
	formatted_address: string;
	/** How to draw it; not a Location field, and never sent back as an answer. */
	precision: GeoPrecision;
}

/** A Location's address fields, as `lookupDataspaceGeo` takes them. */
export interface GeoLookupFields {
	street?: string;
	house_number?: string;
	postal_code?: string;
	city?: string;
	poi_name?: string;
	formatted_address?: string;
}

export async function getDataspaceGeoConfig(dataspaceId: string): Promise<GeoConfigResponse> {
	return request<GeoConfigResponse>("GET", `/dataspaces/${dataspaceId}/geo`, undefined, true);
}

/** One map tile. A Blob, because the route needs the bearer; cached as the server says (a day). */
export async function getDataspaceGeoTile(
	dataspaceId: string,
	z: number,
	x: number,
	y: number,
): Promise<Blob> {
	return requestBlob(
		"GET",
		`/dataspaces/${dataspaceId}/geo/tiles/${z}/${x}/${y}`,
		true,
		"default",
	);
}

export async function searchDataspaceGeo(
	dataspaceId: string,
	query: string,
	signal?: AbortSignal,
): Promise<GeoPlaceResponse[]> {
	return request<GeoPlaceResponse[]>(
		"GET",
		`/dataspaces/${dataspaceId}/geo/search?q=${encodeURIComponent(query)}`,
		undefined,
		true,
		signal,
	);
}

/** Where a Location's address is, best first — asked by its fields, not by its text. */
export async function lookupDataspaceGeo(
	dataspaceId: string,
	fields: GeoLookupFields,
): Promise<GeoPlaceResponse[]> {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(fields)) {
		if (typeof value === "string" && value.trim() !== "") query.set(key, value.trim());
	}
	return request<GeoPlaceResponse[]>(
		"GET",
		`/dataspaces/${dataspaceId}/geo/lookup?${query.toString()}`,
		undefined,
		true,
	);
}

/**
 * The address at a point. 404 `no_address_here` where nothing is near enough; 503
 * `reverse_unsupported` from a location service older than 1.2.0.
 */
export async function reverseDataspaceGeo(
	dataspaceId: string,
	latitude: number,
	longitude: number,
): Promise<GeoPlaceResponse> {
	return request<GeoPlaceResponse>(
		"GET",
		`/dataspaces/${dataspaceId}/geo/reverse?lat=${latitude}&lon=${longitude}`,
		undefined,
		true,
	);
}

export async function getDataspaceTerminology(
	dataspaceId: string,
): Promise<TerminologyEntryResponse[]> {
	return request<TerminologyEntryResponse[]>(
		"GET",
		`/dataspaces/${dataspaceId}/terminology`,
		undefined,
		true,
	);
}

// ── Metadata API ──

export async function getProjectMetadata(projectId: string): Promise<MetadataResponse> {
	return request<MetadataResponse>("GET", `/projects/${projectId}/metadata`, undefined, true);
}

export async function updateProjectMetadata(
	projectId: string,
	data: MetadataConfigRequest,
): Promise<void> {
	await request<unknown>("PUT", `/projects/${projectId}/metadata`, data, true);
}

// ── Activity tag groups API ──

/** A named set of tags the activity overview groups its activities by. */
export interface ActivityTagGroup {
	name: string;
	tags: string[];
}

/** The project's activity tag groups, in the order the overview shows them. */
export async function getActivityTagGroups(projectId: string): Promise<ActivityTagGroup[]> {
	const res = await request<{ groups: ActivityTagGroup[] }>(
		"GET",
		`/projects/${projectId}/activity-tag-groups`,
		undefined,
		true,
	);
	return res.groups;
}

/**
 * Replaces the project's activity tag groups with `groups`, and answers them as stored (trimmed, a
 * repeated tag kept once). Refused with `tag_group_name_empty` / `tag_group_name_taken`.
 */
export async function saveActivityTagGroups(
	projectId: string,
	groups: ActivityTagGroup[],
): Promise<ActivityTagGroup[]> {
	const res = await request<{ groups: ActivityTagGroup[] }>(
		"PUT",
		`/projects/${projectId}/activity-tag-groups`,
		{ groups },
		true,
	);
	return res.groups;
}

// ── Git API ──

export async function gitInit(projectId: string): Promise<void> {
	await request<unknown>("POST", `/projects/${projectId}/git/init`, undefined, true);
}

/**
 * One page of the project's history, newest first. Continue with the previous page's `next_after`
 * as `after` — a commit id, not an offset, because every edit adds a commit at the top and an
 * offset would shift under a reader still on the first page.
 */
export async function getGitLog(
	projectId: string,
	options: { limit?: number; after?: string } = {},
): Promise<GitLogResponse> {
	const query = new URLSearchParams();
	if (options.limit !== undefined) query.set("limit", String(options.limit));
	if (options.after) query.set("after", options.after);
	const qs = query.size > 0 ? `?${query}` : "";
	return request<GitLogResponse>("GET", `/projects/${projectId}/git/log${qs}`, undefined, true);
}

// ── The push remote, and cloning ─────────────────────────────────────────────

/** How the server authenticates against a remote. */
export type GitAuthKind = "none" | "token" | "ssh";

/** A project's push remote. Its secret is never sent back — only whether one is stored. */
export interface GitPushRemoteResponse {
	name: string;
	url: string;
	branch: string;
	auth_kind: GitAuthKind;
	username: string | null;
	secret_set: boolean;
	last_push_at: string | null;
	/** `ok` or `failed`, once a push has run. */
	last_push_status: "ok" | "failed" | null;
	last_push_error: string | null;
	updated_by: string | null;
	updated_at: string;
}

export interface GitPushRemoteRequest {
	url: string;
	branch?: string;
	auth_kind: GitAuthKind;
	username?: string | null;
	/** Omitted or `null` keeps the stored secret; `""` removes it. */
	secret?: string | null;
}

export interface GitPushResponse {
	ok: boolean;
	error: string | null;
	pushed_at: string;
}

export interface CloneProjectRequest {
	url: string;
	branch?: string;
	auth_kind?: GitAuthKind;
	username?: string | null;
	secret?: string | null;
}

export async function getGitPushRemote(projectId: string): Promise<GitPushRemoteResponse | null> {
	return request<GitPushRemoteResponse | null>(
		"GET",
		`/projects/${projectId}/git/remote`,
		undefined,
		true,
	);
}

export async function setGitPushRemote(
	projectId: string,
	data: GitPushRemoteRequest,
): Promise<GitPushRemoteResponse> {
	return request<GitPushRemoteResponse>("PUT", `/projects/${projectId}/git/remote`, data, true);
}

export async function removeGitPushRemote(projectId: string): Promise<void> {
	await request<unknown>("DELETE", `/projects/${projectId}/git/remote`, undefined, true);
}

/** Pushes now. Resolves with `ok: false` and git's reason when the push itself failed. */
export async function pushProject(projectId: string): Promise<GitPushResponse> {
	return request<GitPushResponse>("POST", `/projects/${projectId}/git/push`, undefined, true);
}

/** A new project, cloned from a repository; the caller is its admin. */
export async function cloneProject(data: CloneProjectRequest): Promise<ProjectResponse> {
	return request<ProjectResponse>("POST", "/projects/clone", data, true);
}

export async function gitAddRemote(projectId: string, data: GitRemoteRequest): Promise<void> {
	await request<unknown>("POST", `/projects/${projectId}/git/remotes`, data, true);
}

export async function gitRemoveRemote(projectId: string, name: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/projects/${projectId}/git/remotes/${encodeURIComponent(name)}`,
		undefined,
		true,
	);
}

export async function gitCommit(projectId: string, data: GitCommitRequest): Promise<void> {
	await request<unknown>("POST", `/projects/${projectId}/git/commit`, data, true);
}

// ── Build API ──

export async function runBuild(projectId: string): Promise<BuildResultResponse> {
	return request<BuildResultResponse>("POST", `/projects/${projectId}/build`, undefined, true);
}

/** Whether the project is building right now — see the server's `BuildStatusResponse`. */
export interface BuildStatusResponse {
	building: boolean;
	/** `wip`, or the version a release is building; null when idle. */
	version: string | null;
	/** An automatic `wip` build is waiting out its debounce. */
	pending: boolean;
}

/** For a session opening a project mid-build, which missed the `build:started` that began it. */
export async function getBuildStatus(projectId: string): Promise<BuildStatusResponse> {
	return request<BuildStatusResponse>(
		"GET",
		`/projects/${projectId}/build/status`,
		undefined,
		true,
	);
}

/** Runs the build's typecheck step alone — no install, no bundle. */
export async function runTypecheck(projectId: string): Promise<TypecheckResponse> {
	return request<TypecheckResponse>(
		"POST",
		`/projects/${projectId}/build/typecheck`,
		undefined,
		true,
	);
}

// ── Project Releases API (server/src/routes/projects/releases.rs) ──

/** What a release's version moved by. */
export type ReleaseBump = "patch" | "minor" | "major";

/** One entry of a release's changelog: what kind of thing, which one, and how a reader names it. */
export interface ChangelogEntryResponse {
	/** The commit's scope: `dataobject`, `attribute`, `type`, `terminology`, `process`, … */
	kind: string;
	/** The code name (or, for terminology, the term id) as the commit names it. */
	name: string;
	/** The term's label where there is one, else the name. */
	label: string;
}

export interface ChangelogResponse {
	added: ChangelogEntryResponse[];
	changed: ChangelogEntryResponse[];
	removed: ChangelogEntryResponse[];
}

/** Why a commit counts as what it counts as. */
export interface BumpReasonResponse {
	commit_id: string;
	/** The commit's first line. */
	summary: string;
	bump: ReleaseBump;
	/** `breaking` (marked by hand), `removal`, `rename`, `addition` or `change`. */
	rule: string;
}

export interface VersionCandidatesResponse {
	patch: string;
	minor: string;
	major: string;
}

/** What the next release would be — the commits since the previous one, judged. */
export interface ReleaseDraftResponse {
	/** The latest release, or `null` before the first. */
	previous_version: string | null;
	/** The commit the previous release was made from, where it is known. */
	previous_commit: string | null;
	/** The bump the history calls for — already turned from `major` into `minor` below 1.0.0. */
	bump: ReleaseBump;
	suggested_version: string;
	/** The commits that decided `bump`, newest first, each with its rule. */
	reasons: BumpReasonResponse[];
	candidates: VersionCandidatesResponse;
	changelog: ChangelogResponse;
	/** How many commits since the previous release (release and metadata chores not counted). */
	commits: number;
}

export interface CreateReleaseRequest {
	/** Semver, greater than the latest release. Usually one of the draft's candidates. */
	version: string;
	title?: string;
	/** The release notes: the body of the release commit, and what the release list shows. */
	notes: string;
}

export interface ReleaseDataspaceResponse {
	id: string;
	label: string;
}

export interface ReleaseResponse {
	version: string;
	title: string | null;
	notes: string;
	changelog: ChangelogResponse;
	/** `major`, `minor`, `patch`, or `custom` for none of the three; null when imported. */
	bump: string | null;
	commit_id: string | null;
	previous_version: string | null;
	/** `local`, or `imported` from another server's export. */
	origin: string;
	archive_sha256: string | null;
	/** The user id of whoever made (or imported) it. */
	created_by: string | null;
	created_at: string;
	imported_at: string | null;
	imported_from: string | null;
	/** Its build is in `builds/` and can be run. */
	build_available: boolean;
	/** The dataspaces that run this version. */
	dataspaces: ReleaseDataspaceResponse[];
}

/** A version a dataspace can run. */
export interface ProjectVersionResponse {
	/** `wip` or a released version. */
	version: string;
	/** `wip` or `release`. */
	kind: string;
	title: string | null;
	created_at: string | null;
}

/** `details` of a `422 release_build_failed`: the build's own output. */
export interface ReleaseBuildFailedDetails {
	error: string | null;
	typecheck: string | null;
}

/** The draft of the next release: the bump the history calls for, its reasons and a changelog. */
export async function getReleaseDraft(projectId: string): Promise<ReleaseDraftResponse> {
	return request<ReleaseDraftResponse>(
		"GET",
		`/projects/${projectId}/releases/draft`,
		undefined,
		true,
	);
}

/**
 * Makes a release: builds it, then commits, packages and tags it. Answers once the build is done,
 * so it can take minutes; a failed build is `422 release_build_failed` with its output in
 * `details`, and nothing is released.
 */
export async function createRelease(
	projectId: string,
	data: CreateReleaseRequest,
): Promise<ReleaseResponse> {
	return request<ReleaseResponse>("POST", `/projects/${projectId}/releases`, data, true);
}

/** A project's releases, newest version first. Answers for an imported project too. */
export async function listReleases(projectId: string): Promise<ReleaseResponse[]> {
	return request<ReleaseResponse[]>("GET", `/projects/${projectId}/releases`, undefined, true);
}

/** The versions a dataspace can run: `wip` first when it is built, then releases with a build. */
export async function listProjectVersions(projectId: string): Promise<ProjectVersionResponse[]> {
	return request<ProjectVersionResponse[]>(
		"GET",
		`/projects/${projectId}/versions`,
		undefined,
		true,
	);
}

// ── Dataspace types ──

export interface DataspaceResponse {
	id: string;
	label: string;
	description: string | null;
	project_id: string;
	project_name: string;
	project_version: string;
	created_by: string;
	role: string;
	effective_entitlements: string[];
	member_count: number;
	created_at: string;
	updated_at: string;
	/** The department the dataspace belongs to; `null` for a personal one. */
	department: DepartmentRef | null;
	/** What the Operator's URLs name it by instead of its id; `null` for none. */
	alias: string | null;
	/** What the license takes away from it right now: `locked` while it is closed to the Operator,
	 *  `designer` always `null`. See `SpaceRestriction`. */
	restriction: SpaceRestriction;
	/** How many of its processes the caller was given one by one, beyond their role
	 *  (`ProcessAccessEntry`): processes they can open though their role reads none. */
	granted_processes: number;
	/** Whether the caller sees any of its processes — by their role, in any scope, or a grant. Holds
	 *  while the dataspace is closed too, when `effective_entitlements` leaves out taking part. */
	sees_processes: boolean;
}

/** Somebody on a dataspace's member list, resolved: who reaches it — by an explicit membership, through
 *  its department's access, or both — and an explicit membership of a department's dataspace whose
 *  holder is not in the department (`has_access: false`). See `shared/utils/spaceMembers.ts`. */
export interface DataspaceMemberResponse {
	user_id: string;
	email: string;
	display_name: string;
	/** The explicit membership's role — what the Members settings change and remove — or `null` for
	 *  somebody who reaches the dataspace through its department alone. */
	role: string | null;
	/** The dataspace roles its department's access gives them, lowest first. */
	department_roles: string[];
	/** Whether they reach the dataspace: `false` only for a membership of somebody outside its department. */
	has_access: boolean;
	/** What every role they hold grants together; empty without access. */
	entitlements: string[];
	/** When the explicit membership was made; `null` without one. */
	created_at: string | null;
}

export interface DataspaceRoleResponse {
	id: number;
	name: string;
	entitlements: string[];
	is_system: boolean;
}

export interface DataspaceInvitationResponse {
	id: string;
	dataspace_id: string;
	email: string;
	role: string;
	/** `pending` or `declined` — see `InvitationStatus`. */
	status: string;
	created_at: string;
	expires_at: string;
	invited_by_display_name: string;
}

export type CreatedDataspaceInvitationResponse = DataspaceInvitationResponse & InvitationDelivery;

/** What the server hands a process for a variable the dataspace gives no value — read-only. */
export interface EnvDefault {
	/** Masked when `secret`. */
	value: string;
	secret: boolean;
	/** The control-center setting it comes from (`location.url`). */
	setting: string;
	source: SettingSource;
}

export interface EnvVarEntry {
	key: string;
	/** The dataspace's own value; empty inherits `default`, unless `overrides_default`. */
	value: string;
	/** Server-set on responses: false means the key is in the DB but no longer in build metadata. */
	in_metadata?: boolean;
	/** With an empty value: the server's default is refused, and a process gets no value. */
	overrides_default?: boolean;
	/** Server-set on responses, for a declared variable the server has a default for. */
	default?: EnvDefault;
	/**
	 * Server-set on responses: the server has a default — the gateway token — but keeps it from this
	 * dataspace, whose own gateway URL names another gateway. `default` is absent then.
	 */
	default_withheld?: boolean;
}

export interface EnvVarsResponse {
	vars: EnvVarEntry[];
}

// ── Dataspaces API ──

export async function createDataspace(data: {
	label: string;
	description?: string;
	project_id: string;
	project_version?: string;
	/**
	 * Three states: a department's id puts it there (`department:dataspaces:create` needed),
	 * `null` makes it personal, and absent leaves the server to choose — the project's department
	 * when the caller may create there. The key is dropped by `JSON.stringify` only when
	 * `undefined`, never for `null`.
	 */
	department_id?: string | null;
	/** Stored normalized; `invalid_alias` (400) or `alias_taken` (409) when it cannot be. */
	alias?: string;
}): Promise<DataspaceResponse> {
	return request<DataspaceResponse>("POST", "/dataspaces", data, true);
}

export async function listDataspaces(): Promise<DataspaceResponse[]> {
	return request<DataspaceResponse[]>("GET", "/dataspaces", undefined, true);
}

export async function getDataspace(id: string): Promise<DataspaceResponse> {
	return request<DataspaceResponse>("GET", `/dataspaces/${id}`, undefined, true);
}

/** `alias: null` (or blank) removes it; left out, it stays. */
export async function updateDataspace(
	id: string,
	data: {
		label?: string;
		description?: string | null;
		project_version?: string;
		alias?: string | null;
	},
): Promise<DataspaceResponse> {
	return request<DataspaceResponse>("PUT", `/dataspaces/${id}`, data, true);
}

/**
 * The id of the dataspace an alias names, for the Operator's route. A 404 — the same for an alias
 * nobody has and for one whose dataspace the caller does not reach — says it names nothing.
 */
export async function resolveDataspaceAlias(alias: string): Promise<{ id: string }> {
	return request<{ id: string }>(
		"GET",
		`/dataspaces/resolve/${encodeURIComponent(alias)}`,
		undefined,
		true,
	);
}

export async function deleteDataspace(id: string): Promise<void> {
	await request<unknown>("DELETE", `/dataspaces/${id}`, undefined, true);
}

// ── The people of a dataspace ──

/** What one person may do in one process: by their role, or a grant on it. */
export interface UserProcessAccess {
	read: boolean;
	/** Take part: answer its questions, set its attributes. */
	write: boolean;
	/** The grant they hold on it beside their role, if any. */
	grant: ProcessAccessGrant | null;
}

/** Somebody who reaches a dataspace — explicitly or through its department. */
export interface DataspaceUserResponse {
	user_id: string;
	email: string;
	display_name: string;
	color: string;
	/** Only when asked for a process (`processId`): what they may do in it. */
	access?: UserProcessAccess;
}

/**
 * Everybody who reaches the dataspace, by name: whom a process can be shared with, and what a
 * `NotitiaUser` is chosen from. Not `listDataspaceMembers`, which is the explicit memberships alone.
 * With `processId`, each says what they may do in that process.
 */
export async function listDataspaceUsers(
	id: string,
	processId?: string,
): Promise<DataspaceUserResponse[]> {
	const query = processId ? `?process_id=${encodeURIComponent(processId)}` : "";
	return request<DataspaceUserResponse[]>(
		"GET",
		`/dataspaces/${id}/users${query}`,
		undefined,
		true,
	);
}

// ── Dataspace Members API ──

export async function listDataspaceMembers(id: string): Promise<DataspaceMemberResponse[]> {
	return request<DataspaceMemberResponse[]>("GET", `/dataspaces/${id}/members`, undefined, true);
}

/**
 * Adds a member of the dataspace's department to it. Anybody else — a personal dataspace's people, a
 * person in no department of it — is invited: `403 invitation_required`. See `listMemberCandidates`.
 */
export async function addDataspaceMember(
	id: string,
	userId: string,
	role?: string,
): Promise<DataspaceMemberResponse> {
	return request<DataspaceMemberResponse>(
		"POST",
		`/dataspaces/${id}/members`,
		{ user_id: userId, role },
		true,
	);
}

export async function updateDataspaceMemberRole(
	id: string,
	userId: string,
	role: string,
): Promise<void> {
	await request<unknown>("PUT", `/dataspaces/${id}/members/${userId}`, { role }, true);
}

export async function removeDataspaceMember(id: string, userId: string): Promise<void> {
	await request<unknown>("DELETE", `/dataspaces/${id}/members/${userId}`, undefined, true);
}

// ── Dataspace Roles API ──

export async function listDataspaceRoles(id: string): Promise<DataspaceRoleResponse[]> {
	return request<DataspaceRoleResponse[]>("GET", `/dataspaces/${id}/roles`, undefined, true);
}

export async function createDataspaceRole(
	id: string,
	data: { name: string; entitlements: string[] },
): Promise<DataspaceRoleResponse> {
	return request<DataspaceRoleResponse>("POST", `/dataspaces/${id}/roles`, data, true);
}

export async function updateDataspaceRole(
	id: string,
	roleId: number,
	data: { name?: string; entitlements?: string[] },
): Promise<DataspaceRoleResponse> {
	return request<DataspaceRoleResponse>("PUT", `/dataspaces/${id}/roles/${roleId}`, data, true);
}

export async function deleteDataspaceRole(id: string, roleId: number): Promise<void> {
	await request<unknown>("DELETE", `/dataspaces/${id}/roles/${roleId}`, undefined, true);
}

// ── Dataspace Invitations API ──

export async function listDataspaceInvitations(id: string): Promise<DataspaceInvitationResponse[]> {
	return request<DataspaceInvitationResponse[]>(
		"GET",
		`/dataspaces/${id}/invitations`,
		undefined,
		true,
	);
}

export async function createDataspaceInvitation(
	id: string,
	data: { email: string; role: string },
): Promise<CreatedDataspaceInvitationResponse> {
	return request<CreatedDataspaceInvitationResponse>(
		"POST",
		`/dataspaces/${id}/invitations`,
		data,
		true,
	);
}

/** Revokes a pending invitation, or dismisses a declined one. */
export async function cancelDataspaceInvitation(id: string, invId: string): Promise<void> {
	await request<unknown>("DELETE", `/dataspaces/${id}/invitations/${invId}`, undefined, true);
}

/** `409 invitation_mail_disabled` while invitation mail is off: nothing is sent and nothing changes. */
export async function resendDataspaceInvitation(
	id: string,
	invId: string,
): Promise<DataspaceInvitationResponse> {
	return request<DataspaceInvitationResponse>(
		"POST",
		`/dataspaces/${id}/invitations/${invId}/resend`,
		undefined,
		true,
	);
}

// ── Dataspace Env Vars API ──

export async function getDataspaceEnvVars(id: string): Promise<EnvVarsResponse> {
	return request<EnvVarsResponse>("GET", `/dataspaces/${id}/env`, undefined, true);
}

export async function setDataspaceEnvVars(
	id: string,
	vars: EnvVarEntry[],
): Promise<EnvVarsResponse> {
	return request<EnvVarsResponse>("PUT", `/dataspaces/${id}/env`, { vars }, true);
}

// ── Dataspace SIP Trunk API ──

/** The registration of a dataspace's trunk on its coms system. */
export interface SipRegistrationStatus {
	/** `registering`, `registered` or `failed`. */
	state: string;
	/** Why the last attempt failed: the coms system's status, or the network error. */
	error: string | null;
	/** When the current registration runs out, RFC 3339. */
	expires_at: string | null;
}

/** What the server's SIP endpoint says of a dataspace's trunk right now. Read-only. */
export interface SipTrunkStatus {
	/** The server is a SIP endpoint at all (`SIP_ENABLED`). Off, a saved trunk does nothing. */
	endpoint_enabled: boolean;
	/** Where a coms system sends this dataspace's calls: `sip:<address>:<port>`. */
	sip_uri: string | null;
	/** Why the trunk as stored cannot be used; calls are refused until it is saved again. */
	error: string | null;
	/** Absent when the trunk has no registrar. */
	registration: SipRegistrationStatus | null;
	active_calls: number;
	/** Processes of this dataspace that take calls. */
	routers: number;
}

/** The two ways Alternis gets a call: the coms system does it, or Notitia does. */
export type SipAlternisIntegration = "coms_system" | "notitia";
export type SipFallbackAction = "reject" | "redirect";

/** A dataspace's SIP trunk, as stored. A password is never sent: only whether one is set. */
export interface SipTrunkResponse {
	/** A trunk is stored; `false` is what a new one starts with. */
	configured: boolean;
	enabled: boolean;
	inbound_username: string | null;
	inbound_password_set: boolean;
	allowed_sources: string[];
	numbers: string[];
	registrar: string | null;
	registration_username: string | null;
	registration_auth_username: string | null;
	registration_password_set: boolean;
	registration_expires: number;
	outbound_proxy: string | null;
	caller_id: string | null;
	alternis_integration: SipAlternisIntegration;
	alternis_rtp_address: string | null;
	claim_timeout_ms: number;
	fallback_action: SipFallbackAction;
	fallback_target: string | null;
	reject_status: number;
	max_calls: number;
	advertised_address: string | null;
	updated_by: string | null;
	updated_at: string | null;
	status: SipTrunkStatus;
}

/**
 * The trunk as it is saved: the whole of it, since a save replaces it. A password left out (or
 * `null`) keeps the stored one, an empty string removes it, a value seals and replaces it.
 */
export interface SipTrunkRequest {
	enabled: boolean;
	inbound_username: string | null;
	inbound_password?: string | null;
	allowed_sources: string[];
	numbers: string[];
	registrar: string | null;
	registration_username: string | null;
	registration_auth_username: string | null;
	registration_password?: string | null;
	registration_expires: number;
	outbound_proxy: string | null;
	caller_id: string | null;
	alternis_integration: SipAlternisIntegration;
	alternis_rtp_address: string | null;
	claim_timeout_ms: number;
	fallback_action: SipFallbackAction;
	fallback_target: string | null;
	reject_status: number;
	max_calls: number;
	advertised_address: string | null;
}

/** Needs `dataspace:telephony:manage`. A dataspace with no trunk answers what a new one has. */
export async function getDataspaceSipTrunk(id: string): Promise<SipTrunkResponse> {
	return request<SipTrunkResponse>("GET", `/dataspaces/${id}/sip-trunk`, undefined, true);
}

/**
 * Saves the trunk. `400` with an `invalid_<field>` code names a field the server refuses;
 * `409 inbound_username_taken` is a digest username another dataspace has.
 */
export async function setDataspaceSipTrunk(
	id: string,
	trunk: SipTrunkRequest,
): Promise<SipTrunkResponse> {
	return request<SipTrunkResponse>("PUT", `/dataspaces/${id}/sip-trunk`, trunk, true);
}

/**
 * Registers the trunk on its coms system again with the stored credentials, whatever state the last
 * registration is in. Nothing is saved; the answer is the trunk with its status straight after.
 * `409 registration_not_configured` is a trunk that is off or registers nowhere,
 * `409 sip_endpoint_disabled` a server that is no SIP endpoint.
 */
export async function reregisterDataspaceSipTrunk(id: string): Promise<SipTrunkResponse> {
	return request<SipTrunkResponse>(
		"POST",
		`/dataspaces/${id}/sip-trunk/register`,
		undefined,
		true,
	);
}

/** Removes the trunk, and with it both stored passwords. */
export async function deleteDataspaceSipTrunk(id: string): Promise<void> {
	await request<unknown>("DELETE", `/dataspaces/${id}/sip-trunk`, undefined, true);
}

// ── Process interactable types ───────────────────────────────────────────────

// Mirrors the engine's `engine/src/engine/interactable.ts` and `question_history.ts`
// (@livereader/notitia-engine 0.4.0). Every field a newer engine added is optional here, so an
// older running process still type-checks as what it sends.

export type QuestionInputKind =
	| { type: "text" }
	| { type: "number" }
	| { type: "boolean" }
	/** `options` is always the concrete list to show — already whitelisted, already in the order
	 *  the question gives them (never re-sort it), and filtered against the attribute's current
	 *  domain for an attribute_assigned input. `datatype` names the type the values come from, for
	 *  their terminology, colours and images in `SCHEMA.types`; `value_overrides` is this
	 *  question's own colour or picture for a value, over those, and absent when it has none. */
	| {
			type: "select";
			options: string[];
			datatype?: string;
			display?: SelectDisplay;
			value_overrides?: { [value: string]: SelectValueOverride | undefined };
	  }
	| { type: "button"; js_action?: string }
	/** The answer is a whole dataobject snapshot, not a scalar. */
	| { type: "dataobject"; dataobject_type: string };

/** Where an attribute lives: the root dataobject's id and the attribute's root-relative path —
 *  the pair `attribute_changed` reports. */
export interface AttributeAddress {
	dataobject_id: string;
	attribute: string;
}

export interface QuestionInput {
	name: string;
	label: string;
	description: string;
	error_message: string;
	placeholder: string;
	kind: QuestionInputKind;
	value?: unknown;
	disable_suggestions?: boolean;
	history_size?: number;
	confirm?: boolean;
	/** Whether this input still takes answers once its question is `completed`. Show the input
	 *  locked when `completed && !editable_after_completion`. Absent means false: locked. */
	editable_after_completion?: boolean;
	/** The attribute an attribute_assigned input writes; absent on a normal one. */
	bound_attribute?: AttributeAddress;
	/** On a `dataobject` input: the fields of its type an answer must hold. A value that lacks one
	 *  is PARTIAL — it is the input's value, and the input is still open (`isInputOpen`). */
	required_fields?: string[];
}

/** The question tab every question without a `tab_id` is under. Always exists, never removed. */
export const DEFAULT_QUESTION_TAB_ID = "default";

/** A `group_id` shown above every other group, whatever its questions' priorities. */
export const ON_TOP_QUESTION_GROUP_ID = "on-top";

export interface QuestionInteractable extends InteractableStamp {
	kind: "question";
	body: string;
	inputs?: QuestionInput[];
	/** Questions with the same `group_id` are shown together. 0.4.0 defaults it to the id of the
	 *  dataobject the question's activity runs on, so it is always set. */
	group_id?: string;
	/** The `question_tab` this question is under; absent means `DEFAULT_QUESTION_TAB_ID`. */
	tab_id?: string;
	/** Effective priority. Higher first, absent is 0, and equals keep the order they were first
	 *  shown in. A group is ordered by its highest-priority question. */
	priority?: number;
	/** Every input has been answered. Only `editable_after_completion` inputs still take one. */
	completed?: boolean;
	color?: string;
	icon?: string;
}

/** A tab questions are sorted under, by their `tab_id`. Id `question_tab::<tab_id>`. Unrelated
 *  to `TabInteractable`, which is a content-viewer panel. */
export interface QuestionTabInteractable {
	kind: "question_tab";
	tab_id: string;
	title: string;
	color?: string;
}

export interface QuestionSuggestionInteractable extends InteractableStamp {
	kind: "question_suggestion";
	question_id: string;
	input_index: number;
	value: unknown;
	alternatives: unknown[];
	/** Where `value` came from. Absent on an older engine, which only had LLM suggestions. */
	source?: "llm" | "provider" | "attribute_suggestion";
	/** The attribute suggestion's own explanation, when `source` is "attribute_suggestion". */
	explanation?: string;
	/** The id of the `attribute_suggestion` interactable `value` came from, likewise. */
	attribute_suggestion_id?: string;
}

export type QuestionHistoryStatus = "completed" | "removed";
export type QuestionHistoryCause = "completed" | "answered" | "history_edit" | "removed";

/** An immutable record of a question as answered: emitted once, never updated or removed. A later
 *  change appends a revision naming this entry as `parent_entry_id`; only the newest revision of a
 *  chain (its head) is editable. Id `question_history::<entry_id>`. */
export interface QuestionHistoryInteractable extends InteractableStamp {
	kind: "question_history";
	entry_id: string;
	question_id: string;
	/** Tells two showings of the same question id apart. */
	instance_id: string;
	root_entry_id: string;
	parent_entry_id: string | null;
	revision: number;
	status: QuestionHistoryStatus;
	cause: QuestionHistoryCause;
	/** Which input a revision changed; absent on the first of a chain. */
	edited_input_index?: number;
	recorded_at: number;
	body: string;
	group_id?: string;
	tab_id?: string;
	priority?: number;
	color?: string;
	icon?: string;
	inputs: QuestionInput[];
	answered: boolean[];
	/** Inputs this entry allows editing — while it is the head of its chain. Empty when removed. */
	editable_input_indices: number[];
}

/** The inbound channel an edit of history entry `entryId`'s input `index` is sent on, as a
 *  `channel_event` (see `sendDataspaceProcessChannelEvent`). */
export function questionHistoryEditChannel(entryId: string, index: number): string {
	return `question_history::${entryId}::${index}`;
}

export interface GraphlyNodeInteractable {
	kind: "graphly_node";
	dataobject_id: string;
	shape: string;
	fill: Record<string, unknown>;
	/** `null` until the process first sets one (see the engine's SUBPROCESS-API.md); (0, 0) is a
	 *  position like any other. */
	position: { x: number; y: number } | null;
	links: string[];
}

/**
 * What a viewer closing an interactable comes to — the engine's `dismiss`, on a tab, a
 * notification, an attribute suggestion and a panel. `remove`: the engine removes it for every
 * viewer. `output`: the process decides, and it stays shown until the process removes it. `none`:
 * not closable. Absent on an interactable from an engine that predates the field, which also cannot
 * hear a dismissal: such a client keeps hiding it locally.
 */
export type DismissPolicy = "remove" | "output" | "none";

/**
 * Every interactable carries the `seq` the engine stamps: the order it was FIRST emitted in. A
 * snapshot of a process's interactables is an unordered map, so a client that arrives late orders
 * tabs and side panels by this. Absent from an engine that predates it.
 */
export interface InteractableStamp {
	seq?: number;
	/**
	 * Whom its model bound its acting parts to — answering, pressing, closing, a chat's controls: user
	 * ids or emails. Absent: anybody who may take part in the process. Empty: nobody, yet. The server
	 * refuses anybody else on the channels in `actor_channels` (`403 not_an_actor`).
	 */
	actors?: string[];
	/** The channels the binding holds, as the engine names them. Present with `actors`. */
	actor_channels?: string[];
}

export interface NotificationInteractable extends InteractableStamp {
	kind: "notification";
	/** A heading over the body: what it is about, in a few words. Absent from an older engine, and
	 *  from a notification that sets none — the body then stands alone. Plain text. */
	title?: string;
	/** Markdown. */
	body: string;
	severity?: number;
	color?: string;
	icon?: string;
	dismiss?: DismissPolicy;
}

export interface TabInteractable extends InteractableStamp {
	kind: "tab";
	title: string;
	address: string;
	display_kind: string;
	/** Where it sits in the process view; absent, a tab's own place (`shared/utils/panelPlacement.ts`). */
	column?: PanelColumn;
	align?: PanelAlign;
	dismiss?: DismissPolicy;
}

/**
 * A value the process suggests for an attribute — EMIT_ATTRIBUTE_SUGGESTION. `dataobject_id` is the
 * dataobject the emitting activity runs on, which may be an EMBEDDED instance, with `attribute`
 * relative to it; a write goes to its root (`utils/process/suggestions.ts`, SLOTS). Where a question
 * input is bound to the same attribute the engine offers it on that input, as a `question_suggestion`
 * naming it in `attribute_suggestion_id`; the suggestion pool shows it there, and on its own only
 * where no question asks for the attribute. Absent `dismiss` means `"remove"`.
 */
export interface AttributeSuggestionInteractable extends InteractableStamp {
	kind: "attribute_suggestion";
	dataobject_id: string;
	attribute: string;
	value: unknown;
	explanation: string;
	dismiss?: DismissPolicy;
}

/** Which part of the operator a panel is. A kind this client does not know is skipped. */
export type PanelKind = "graphly" | "lumina" | "question_list" | "suggestion_pool";

/**
 * The corner a `suggestion_pool` of a build before `column` and `align` names, which the Operator reads
 * LITERALLY — `top-left` is left/top — when the pool carries neither (`resolvePlacement`). No engine
 * since sends it; the type is kept for the releases still running in dataspaces.
 */
export type PoolPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/**
 * A part of the operator the process says it has: the Graphly diagram, the Lumina view, the question
 * list or a suggestion pool. The panel is only the view — `graphly_node`s are emitted whether or not a
 * `graphly` panel exists. Absent `dismiss` means `"none"`.
 */
export interface PanelInteractable extends InteractableStamp {
	kind: "panel";
	panel: PanelKind;
	title?: string;
	/** Where it sits in the process view; absent, its kind's own place (`shared/utils/panelPlacement.ts`). */
	column?: PanelColumn;
	align?: PanelAlign;
	/** A pool of an older build only — see `PoolPosition`. */
	position?: PoolPosition;
	/** On a `suggestion_pool` only: the cards it keeps whether or not a suggestion exists, in the
	 *  order it shows them. Absent when the pool has none. */
	entries?: PoolEntry[];
	dismiss?: DismissPolicy;
}

/** What a persistent card of a suggestion pool stands for. */
export type PoolEntryTarget =
	| { kind: "attribute"; attribute: string }
	| { kind: "question"; question: string; input: string };

/** One persistent card of a suggestion pool — the engine's `PoolEntry`. */
export interface PoolEntry {
	target: PoolEntryTarget;
	/** Shown, empty, before a suggestion exists for it. */
	before: boolean;
	/** Kept, frozen, once its value was taken. */
	after: boolean;
	/** Replaces the card's heading. */
	title?: string;
	/** An MDI class name (`mdi-home`). */
	icon?: string;
}

/** The channel a viewer's dismissal of `id` is sent on, as a `channel_event` — the engine consumes
 *  it rather than broadcasting it, so no route has to know it. */
export function interactableDismissChannel(id: string): string {
	return `interactable::${id}::dismiss`;
}

/*
  An Alternis chat, and the four ops the Operator sends into it. The engine
  (notitia-engine `engine/src/engine/alternis_chat_session.ts` and `voicebot/voicebot.ts`)
  and this client are written against one contract; these types are its text. The
  Operator's reading of them is `operator/utils/process/alternisChat.ts`.
*/

export interface AlternisChatInteractable extends InteractableStamp {
	kind: "alternis_chat";
	/** Raw Alternis `Message` objects of this session, oldest first. `system` messages (language
	 *  set / detected, dividers) ARE included — they are shown as dividers. (They stay excluded
	 *  from what question suggestions and the voicebot's LLM read.) */
	messages: unknown[];
	/** Legacy: manual mode only. Projects built today have no manual mode (#306), but a release
	 *  built before it may still be running in a dataspace, so the Operator keeps showing one. */
	webrtc?: { url: string; token: string };
	/** Session mode and the voicebot. `null` while no session is configured, or until the session
	 *  has been fetched once. Absent in a legacy manual-mode chat. */
	session?: AlternisChatSession | null;
	/** The languages this Alternis server processes (`GET /api/v1/languages`, Alternis `Language`
	 *  codes). `[]` until loaded, or when that fetch failed. Session mode / voicebot only. */
	languages?: string[];
	/** Present only when `features.prefabs` is on: the device's prefab groups
	 *  (`GET /api/v1/prefabs/groups`, then `GET /api/v1/prefabs?group_id=` per group). */
	prefab_groups?: AlternisPrefabGroup[];
	/** Which of the chat's controls the action enables. Session mode / voicebot only. */
	features?: AlternisChatFeatures;
	/** What the author calls each Alternis role number, and its icon. Absent when the action names
	 *  none; a role without an entry is worded by the Operator. Kept on a frozen chat. */
	roles?: AlternisChatRole[];
	/** How the Alternis server's administrator wants spoken messages shown (Alternis 1.4.0's
	 *  `GET /api/v1/display-settings`, which the engine reads, since it holds the device token).
	 *  Session mode / voicebot only. Absent from an older engine, from an Alternis before 1.4.0,
	 *  until the first read has answered, and on a chat the engine no longer serves (frozen) — every
	 *  line is then shown as it comes, as before. A process that stopped without freezing its chat
	 *  saved it with them, so the Operator ignores them on a process that is not running. */
	display_settings?: AlternisDisplaySettings;
	/** Where it sits in the process view; absent, a chat's own place (`shared/utils/panelPlacement.ts`). */
	column?: PanelColumn;
	align?: PanelAlign;
	/** What the action calls the chat, its tab's label where it shares its place; absent, the
	 *  Operator names it after its session. */
	title?: string;
	/** Whether a viewer may remove it; absent means `"none"`, as on every chat before it. */
	dismiss?: DismissPolicy;
}

/** One role number's label and icon in an `alternis_chat`. */
export interface AlternisChatRole {
	role: number;
	label: string;
	/** An MDI class name (`mdi-headset`). */
	icon?: string;
	/** A hex colour for the role's messages and language picker; absent, the palette's for its number. */
	color?: string;
}

/** One RTP stream of the call: Alternis `rtp-registrations`, mapped. */
export interface AlternisRtpRegistration {
	role: number;
	ssrc: number;
	/** An Alternis `RtpLanguage`: `"unknown"`, `"soft-locked-<lang>"` (detected; detection may
	 *  change it) or `"hard-locked-<lang>"` (set by a person). */
	language: string;
}

export interface AlternisChatSession {
	id: string;
	label: string;
	/** Alternis `SessionState`: active = Live, passive = Passive, inactive = Ended. */
	state: "inactive" | "passive" | "active";
	/** Unix seconds (Alternis `created`). */
	created: number;
	/** Alternis `caller` (a phone number), or null. */
	caller: string | null;
	/** Alternis `summary` (markdown from an LLM — untrusted), or null. */
	summary: string | null;
	/** Alternis `rtp-registrations`, mapped. Role 0 is the dispatcher. */
	registrations: AlternisRtpRegistration[];
}

export interface AlternisPrefabGroup {
	id: string;
	title: string;
	prefabs: AlternisPrefab[];
}

/** Alternis `Prefab` minus `audio-files` (never needed in the browser), keys snake_cased. */
export interface AlternisPrefab {
	id: string;
	title: string;
	/** The prefab's text, in `input_lang`. */
	input: string;
	input_lang: string;
	/** language code -> text. */
	translations: Record<string, string>;
}

/** Which of the chat's controls the action enables. Unset: `summary` on, the other four off. */
export interface AlternisChatFeatures {
	/** Role language pickers can change a role's language (and its lock). */
	languages: boolean;
	/** The composer's free-text tab. */
	composer: boolean;
	/** The composer's prefab tabs. */
	prefabs: boolean;
	/** The dispatcher messages' play-synthesis button. */
	play: boolean;
	/** The summary banner. */
	summary: boolean;
}

/** Alternis `DisplaySettings`, keys snake_cased. Alternis transcribes and translates a spoken
 *  message live either way; these only say whether a reader sees it before it is final. */
export interface AlternisDisplaySettings {
	/** The transcript while it is spoken. Off: only once `progress.speech` is `ended` and
	 *  `progress.stt` is no longer `processing`. */
	live_transcription: boolean;
	/** Each translation while it is spoken. Off: only once it is one of the final transcript —
	 *  that is final, and the language's `progress.trl` is no longer `processing`. */
	live_translation: boolean;
}

/** What the Operator sends on `alternis_chat::<interactable id>`, as a channel event. */
export type AlternisChatOp =
	| { op: "set_language"; request_id: string; role: number; language: string }
	| { op: "send_text"; request_id: string; text: string; language: string }
	| { op: "send_prefab"; request_id: string; prefab_id: string }
	| { op: "emit_synthesis"; request_id: string; message_id: string };

export type AlternisChatErrorCode =
	| "feature_disabled"
	| "no_session"
	| "invalid_request"
	| "alternis_failed";

/** What the engine answers each op with, on `alternis_chat::<interactable id>::result`. */
export type AlternisChatResult =
	| { request_id: string; ok: true }
	| {
			request_id: string;
			ok: false;
			/** `message` is English, for a log or a toast's detail — never shown as the only text. */
			error: { code: AlternisChatErrorCode; message: string };
	  };

/**
 * JavaScript the process asks the Operator to run in the viewer's own window — a CLIENT_SCRIPT
 * action, under `client_script::<action id>::<dataobject id>` (notitia-engine
 * `engine/src/engine/client_script.ts`). The Operator's reading of it is
 * `operator/utils/process/clientScripts.ts`; what the script is handed is `shared/utils/clientScript.ts`.
 */
export interface ClientScriptInteractable extends InteractableStamp {
	kind: "client_script";
	code: string;
	/** The labels of the action's outputs the script may emit on. */
	outputs: string[];
}

/** The channel what a client script emits is sent on, as a `channel_event` with `{ output, value }`:
 *  the engine hands `value` to the action's output labelled `output`. */
export function clientScriptEmitChannel(id: string): string {
	return `client_script::${id}::emit`;
}

export type AnyInteractable =
	| QuestionInteractable
	| QuestionSuggestionInteractable
	| QuestionTabInteractable
	| QuestionHistoryInteractable
	| GraphlyNodeInteractable
	| NotificationInteractable
	| TabInteractable
	| AttributeSuggestionInteractable
	| PanelInteractable
	| AlternisChatInteractable
	| ClientScriptInteractable
	| { kind: string; [key: string]: unknown };

// ── Process log stream ────────────────────────────────────────────────────────

export type LogLevel = "debug" | "info" | "warn" | "error";

/** One entry of a running process's log stream (the engine's `log` NDJSON `entry`). */
export interface LogEntry {
	timestamp: number;
	level: LogLevel;
	message: string;
	/** Pre-formatted `ISO [LEVEL] message` line, convenient for direct display. */
	line: string;
}

// ── Process schema types ─────────────────────────────────────────────────────

export interface ProcessAttributeSchema {
	term_id: string | null;
	type: string; // "string" | "number" | "boolean" | "reference" | "Types.<name>"
	optional: boolean;
	array: boolean;
}

export interface ProcessRelationSchema {
	term_id: string | null;
	target: string;
}

/** One relation pointing AT this concept, keyed `<Source>_<relation>` — the mirror of
 *  `ProcessConceptSchema.relations`, so both directions of an edge are drawable from the schema
 *  alone. `source` names the concept that DECLARES the relation, `relation` its own name there. */
export interface ProcessInverseRelationSchema {
	term_id: string | null;
	source: string;
	relation: string;
}

export interface ProcessConceptSchema {
	term_id: string | null;
	color: string | null;
	attributes: Record<string, ProcessAttributeSchema>;
	relations: Record<string, ProcessRelationSchema>;
	/** Relations declared by OTHER concepts that target this one, computed over every reachable
	 *  concept including dependencies'. A dependency's or predefined concept's relation appears
	 *  here too, even though no typed accessor is generated for it. Read-only and derived. */
	inverse_relations: Record<string, ProcessInverseRelationSchema>;
}

export interface ProcessTypeValueSchema {
	value: string;
	term_id: string;
	color: string | null;
	/** The value's image file name, `<sha256>.<ext>` — never a URL. Fetch it with
	 *  `getDataspaceImage`. Absent when the value has no image, or on an older engine. */
	image?: string;
}

export interface ProcessTypeSchema {
	term_id: string;
	/** The FLATTENED domain: this type's own literals plus every union member's, transitively and
	 *  deduplicated. A client that only validates a value needs no union awareness. */
	values: ProcessTypeValueSchema[];
	/** Names of the types this one unions in, in declaration order — empty for a flat enum.
	 *  Purely descriptive: every member's values are already in `values`. */
	members: string[];
}

/** How the operator is laid out for a process: `legacy` always has the Graphly diagram, the Lumina
 *  view and the question list; `explicit` has only the panels the process emits. */
export type ProcessLayout = "legacy" | "explicit";

export interface ProcessSchemaData {
	process_id: string;
	concepts: Record<string, ProcessConceptSchema>;
	types: Record<string, ProcessTypeSchema>;
	/**
	 * Absent when the process runs on an engine that predates layouts. That is not `legacy`: it is
	 * an engine that cannot emit panels or hear a dismissal, so a client shows the layout it always
	 * showed and hides a closed tab or notification locally only (`utils/process/layout.ts`).
	 */
	layout?: ProcessLayout;
}

// ── Dataspace Processes ──────────────────────────────────────────────────────

/**
 * Whether a running process has the environment a launch would get now — a process keeps the one it
 * started with (`server/src/process_env.rs`). The variables are named, never their values, and only
 * to someone who may manage the dataspace's environment; anyone else gets empty lists.
 */
export interface ProcessEnvStatus {
	/** `false`: started before its environment changed; a restart takes the new values. */
	current: boolean;
	changed: string[];
	added: string[];
	removed: string[];
}

/**
 * What a restart of a process will do with its saved state (`server/src/routes/dataspaces/process_service.rs`,
 * `resume_view`). The server says the first of these that holds, in this order.
 */
export type ProcessResumeState =
	/** Built before processes could resume: a restart begins from scratch, and nothing is kept. */
	| "unsupported"
	/** Not taken back by itself — `blocked` says why. A restart by hand tries again. */
	| "blocked"
	/** The running launch was handed a state and began from scratch regardless. Only while it runs. */
	| "lost"
	/** A state is stored, but a newer one could not be: a restart takes an older one. */
	| "stale"
	/** A state is stored, and a restart continues from it. */
	| "saved"
	/** Nothing is stored yet. */
	| "none";

/**
 * The reasons a process is `blocked` that the Operator has words for: the engine's own
 * `restore_failed` codes, and two the server sets — `resume_state_missing` (its stored state is gone
 * or damaged) and `unstable` (it exited three times in a row soon after taking its state back).
 * `ProcessResume.blocked` is a plain string all the same: a newer engine may say more, and the
 * Operator words a code it does not know by the code itself.
 */
export type ProcessResumeBlockedCode =
	| "format_unsupported"
	| "snapshot_invalid"
	| "schema_mismatch"
	| "dataobject_unrestorable"
	| "restore_error"
	| "restore_failed"
	| "resume_state_missing"
	| "unstable";

export interface ProcessResume {
	state: ProcessResumeState;
	/** RFC 3339: when a state was last stored. Absent while none was, and when `unsupported`. */
	saved_at?: string;
	/** With `state: "blocked"`, the reason: see `ProcessResumeBlockedCode`. */
	blocked?: string;
	/** Present while the running launch took its state back best-effort: `dropped` items no longer fit. */
	migrated?: { dropped: number };
}

export interface DataspaceProcessResponse {
	id: string;
	dataspace_id: string;
	process_name: string;
	process_label: string;
	name: string;
	description: string | null;
	/** What the Operator's URLs name it by instead of its id; `null` for none. */
	alias: string | null;
	status: string;
	error: string | null;
	/** The user it is attributed to: whoever started it, or whoever its spawner is attributed to. A
	 *  role that takes part in what its holder started takes part in this one when it is them. */
	created_by: string;
	created_by_display_name: string;
	created_at: string;
	updated_at: string;
	restart_rule: string;
	/** The process instance that spawned this one; `null` for one a person started. Its creator is
	 *  the user it is attributed to either way: a child takes its spawner's. */
	spawned_by: string | null;
	/** What the process that spawned it is called now; `null` for one a person started, and for one
	 *  whose spawner was deleted. A list shows a spawned process as started by it. */
	spawned_by_name: string | null;
	/** The version the process runs: the release it was created on, which it keeps however the
	 *  dataspace moves, or `wip`, which follows the project's work in progress. */
	project_version: string;
	/** The dataspace is on another version than the process. `upgradeDataspaceProcess` brings it along. */
	outdated: boolean;
	/** Whether, and how well, a restart continues where the process stopped. */
	resume: ProcessResume;
	/** `null` when nothing of it runs to compare, and in the answer of a start, stop or restart. Only
	 *  the list, the single read and a restart rule's change say it. */
	env?: ProcessEnvStatus | null; /** Who may reach it beyond their role, in the order they were given it. What a role grants is not
	 *  listed. `process:access:changed` says it again whenever it moves. */
	accessible_for: ProcessAccessEntry[];
}

/** `read`: the process is theirs to see. `read_write`: and to take part in. */
export type ProcessAccessGrant = "read" | "read_write";

/** The grants a dialog offers, in order. */
export const PROCESS_ACCESS_GRANTS: readonly ProcessAccessGrant[] = ["read", "read_write"];

/** One user's access to one process beyond their role (`server/src/access/process.rs`). */
export interface ProcessAccessEntry {
	user_id: string;
	email: string;
	display_name: string;
	color: string;
	grant: ProcessAccessGrant;
	/** When it was given or last changed (RFC 3339). */
	granted_at: string;
	/** The person who gave it last; `null` when a process did, or that person is gone. */
	granted_by: string | null;
	/** The process that gave it, over its own host requests; `null` when a person did. */
	granted_by_process_id: string | null;
}

/** Gives `userId` `grant` on the process, replacing what they held. Needs
 *  `dataspace:processes:access`; `400 user_not_in_dataspace` for somebody who does not reach it. */
export async function setDataspaceProcessAccess(
	id: string,
	processId: string,
	userId: string,
	grant: ProcessAccessGrant,
): Promise<ProcessAccessEntry[]> {
	const answer = await request<{ accessible_for: ProcessAccessEntry[] }>(
		"PUT",
		`/dataspaces/${id}/processes/${processId}/access/${encodeURIComponent(userId)}`,
		{ grant },
		true,
	);
	return answer.accessible_for;
}

/** Takes `userId`'s grant on the process away; their role's rights stay. */
export async function removeDataspaceProcessAccess(
	id: string,
	processId: string,
	userId: string,
): Promise<ProcessAccessEntry[]> {
	const answer = await request<{ accessible_for: ProcessAccessEntry[] }>(
		"DELETE",
		`/dataspaces/${id}/processes/${processId}/access/${encodeURIComponent(userId)}`,
		undefined,
		true,
	);
	return answer.accessible_for;
}

/**
 * What `GET /dataspaces/{id}/processes` can be asked (`server/src/routes/dataspaces/process_query.rs`).
 * Every field narrows, and none given is the whole list, newest first. A range is half-open: `from`
 * is in it, `to` is not.
 */
export interface DataspaceProcessFilter {
	/** Any of these: `starting`, `running`, `stopped`, `failed`, `archived`. */
	status?: string[];
	/** Any of these definitions — a row's `process_name`. */
	definition?: string[];
	/** `true`: only the archived. `false`: only the others. */
	archived?: boolean;
	/** Any of these users, by id. */
	createdBy?: string[];
	/** Any of these process instances, by id: the instances that one of them spawned. */
	spawnedBy?: string[];
	/** RFC 3339, in any offset. */
	createdFrom?: string;
	createdTo?: string;
	updatedFrom?: string;
	updatedTo?: string;
	/** Words that must each be found in a process's name, description, definition label or id, or its alias. */
	search?: string;
	/** `created_at` (newest first unless `order` says otherwise), `updated_at`, `name`, `status`, `definition`. */
	sort?: "created_at" | "updated_at" | "name" | "status" | "definition";
	order?: "asc" | "desc";
	/** 0 to 1000. */
	limit?: number;
	offset?: number;
}

/** The query string of a process list: empty for a filter that asks for nothing. */
function dataspaceProcessQuery(filter: DataspaceProcessFilter): string {
	const query = new URLSearchParams();
	const list = (name: string, values: string[] | undefined) => {
		if (values?.length) query.set(name, values.join(","));
	};
	const one = (name: string, value: string | number | boolean | undefined) => {
		if (value !== undefined && value !== "") query.set(name, String(value));
	};
	list("status", filter.status);
	list("definition", filter.definition);
	one("archived", filter.archived);
	list("created_by", filter.createdBy);
	list("spawned_by", filter.spawnedBy);
	one("created_from", filter.createdFrom);
	one("created_to", filter.createdTo);
	one("updated_from", filter.updatedFrom);
	one("updated_to", filter.updatedTo);
	one("search", filter.search?.trim());
	one("sort", filter.sort);
	one("order", filter.order);
	one("limit", filter.limit);
	one("offset", filter.offset);
	const text = query.toString();
	return text ? `?${text}` : "";
}

export async function listDataspaceProcesses(
	id: string,
	filter: DataspaceProcessFilter = {},
): Promise<DataspaceProcessResponse[]> {
	return request<DataspaceProcessResponse[]>(
		"GET",
		`/dataspaces/${id}/processes${dataspaceProcessQuery(filter)}`,
		undefined,
		true,
	);
}

export async function getDataspaceProcess(
	id: string,
	processId: string,
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"GET",
		`/dataspaces/${id}/processes/${processId}`,
		undefined,
		true,
	);
}

export interface ProcessStateDataobject {
	dataobject_id: string;
	concept: string;
	template: string | null;
	template_term_id: string | null;
	attributes: Record<string, unknown>;
}

export interface ProcessStateSnapshotResponse {
	schema: ProcessSchemaData | null;
	dataobjects: ProcessStateDataobject[];
	interactables: Record<string, AnyInteractable>;
	logs: LogEntry[];
	saved_at: string;
}

// Saved when a process instance last stopped, failed, or was archived — 404 if
// it never reached one of those points (still running, or never started).
export async function getDataspaceProcessState(
	id: string,
	processId: string,
): Promise<ProcessStateSnapshotResponse> {
	return request<ProcessStateSnapshotResponse>(
		"GET",
		`/dataspaces/${id}/processes/${processId}/state`,
		undefined,
		true,
	);
}

export async function createDataspaceProcess(
	id: string,
	data: {
		process_name: string;
		name: string;
		description?: string;
		restart_rule?: string;
		/** Stored normalized; `invalid_alias` (400) or `alias_taken` (409) when it cannot be. */
		alias?: string;
		/** Whom it is shared with from the start. Needs `dataspace:processes:access`;
		 *  `user_not_in_dataspace` (400) for somebody who does not reach the dataspace. */
		accessible_for?: { user_id: string; grant: ProcessAccessGrant }[];
	},
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>("POST", `/dataspaces/${id}/processes`, data, true);
}

/**
 * What a process is called: its name, description and alias, archived or not. A field left out
 * stays; `null` (or blank) clears the description or the alias. Told to the room as `process:updated`.
 */
export async function updateDataspaceProcess(
	id: string,
	processId: string,
	data: { name?: string; description?: string | null; alias?: string | null },
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"PATCH",
		`/dataspaces/${id}/processes/${processId}`,
		data,
		true,
	);
}

/** The id of the process of a dataspace an alias names, for the Operator's route; 404 for none. */
export async function resolveProcessAlias(id: string, alias: string): Promise<{ id: string }> {
	return request<{ id: string }>(
		"GET",
		`/dataspaces/${id}/processes/resolve/${encodeURIComponent(alias)}`,
		undefined,
		true,
	);
}

export async function deleteDataspaceProcess(id: string, processId: string): Promise<void> {
	return request<void>("DELETE", `/dataspaces/${id}/processes/${processId}`, undefined, true);
}

export async function archiveDataspaceProcess(
	id: string,
	processId: string,
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"PATCH",
		`/dataspaces/${id}/processes/${processId}/archive`,
		undefined,
		true,
	);
}

export async function stopDataspaceProcess(
	id: string,
	processId: string,
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/stop`,
		undefined,
		true,
	);
}

/**
 * Brings a process to the version its dataspace is on now, best-effort: it is stopped so its state is
 * saved, pinned to that version and started from its build, taking the state back as far as it still
 * fits and dropping the rest. Answers at once with the process `starting` on its new version.
 *
 * Refused, with the code to branch on: `restart_not_allowed` (400, its restart rule is `no`),
 * `wrong_state` (409, it is archived), `already_current` (409), `version_not_built` (422, the
 * dataspace's version has no build to run); and a 403 or 404 as everywhere.
 */
export async function upgradeDataspaceProcess(
	id: string,
	processId: string,
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/upgrade`,
		undefined,
		true,
	);
}

export async function updateDataspaceProcessRestartRule(
	id: string,
	processId: string,
	restartRule: string,
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"PATCH",
		`/dataspaces/${id}/processes/${processId}/restart_rule`,
		{ restart_rule: restartRule },
		true,
	);
}

export async function restartDataspaceProcess(
	id: string,
	processId: string,
): Promise<DataspaceProcessResponse> {
	return request<DataspaceProcessResponse>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/restart`,
		undefined,
		true,
	);
}

/**
 * Restarts the running processes of `processIds` that are still on the previous environment —
 * whatever their restart rule says. Those no longer behind it are left alone, so a list that has
 * moved on since it was shown restarts nothing nobody confirmed.
 */
export async function restartOutdatedDataspaceProcesses(
	id: string,
	processIds: string[],
): Promise<{ restarted: DataspaceProcessResponse[] }> {
	return request<{ restarted: DataspaceProcessResponse[] }>(
		"POST",
		`/dataspaces/${id}/processes/restart-outdated`,
		{ process_ids: processIds },
		true,
	);
}

export async function setDataspaceProcessAttribute(
	id: string,
	processId: string,
	dataobjectId: string,
	attribute: string,
	value: unknown,
	origin?: "user" | "reasoning" | "external",
): Promise<void> {
	return request<void>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/set_attribute`,
		{ dataobject_id: dataobjectId, attribute, value, origin },
		true,
	);
}

export async function createDataspaceProcessEmbeddedDataobject(
	id: string,
	processId: string,
	dataobjectId: string,
	attribute: string,
): Promise<void> {
	return request<void>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/create_embedded_dataobject`,
		{ dataobject_id: dataobjectId, attribute },
		true,
	);
}

export async function removeDataspaceProcessEmbeddedDataobject(
	id: string,
	processId: string,
	dataobjectId: string,
	attribute: string,
	embeddedId?: string,
): Promise<void> {
	return request<void>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/remove_embedded_dataobject`,
		{ dataobject_id: dataobjectId, attribute, embedded_id: embeddedId },
		true,
	);
}

export async function sendDataspaceProcessChannelEvent(
	id: string,
	processId: string,
	channel: string,
	data?: unknown,
): Promise<void> {
	return request<void>(
		"POST",
		`/dataspaces/${id}/processes/${processId}/channel_event`,
		{ channel, data },
		true,
	);
}

// ── Dataspace available processes ──

export interface AvailableProcessResponse {
	id: string;
	name: string;
	description: string;
}

/** The definitions the caller may start — none is an empty list, not a refusal. */
export async function listDataspaceAvailableProcesses(
	id: string,
): Promise<AvailableProcessResponse[]> {
	return request<AvailableProcessResponse[]>(
		"GET",
		`/dataspaces/${id}/processes/available`,
		undefined,
		true,
	);
}

/** One process definition of a dataspace's build, as the roles editor offers it. */
export interface ProcessDefinitionResponse {
	/** What a role's per-definition rights name it by, and an instance keeps as `process_name`. */
	id: string;
	name: string;
	description: string;
}

/** Every definition of the dataspace's build, whatever the caller may do with each. Needs
 *  `dataspace:roles:manage`. */
export async function listDataspaceProcessDefinitions(
	id: string,
): Promise<ProcessDefinitionResponse[]> {
	return request<ProcessDefinitionResponse[]>(
		"GET",
		`/dataspaces/${id}/process-definitions`,
		undefined,
		true,
	);
}

// ── Dataspace builds ──

export interface BuildProcessInfo {
	id: string;
	name: string;
	description: string;
	/** The process's layout as the build says it. Absent on a build made before layouts. */
	layout?: ProcessLayout;
}

/** What `metadata.json` says about a build's start page. */
export interface BuildLanding {
	/** Where the page is inside the build. The server reads only `landing/index.html`. */
	entry: string;
	/** The environment variables the page reads, and so the only ones a viewer is handed. */
	"environment-variables": string[];
}

export interface BuildMetadata {
	id: string;
	version: string;
	/**
	 * The names the build declares, which a dataspace's environment editor accepts keys from.
	 * Spelled as the server writes it: this field used to be `environment_variables`, which the
	 * server never sent, so it read `undefined` wherever it was used.
	 */
	"environment-variables": string[];
	processes: BuildProcessInfo[];
	/** Present only when the build ships a start page. Absent on every build made before they existed. */
	landing?: BuildLanding;
}

export interface BuildSummaryResponse {
	version: string;
	metadata: BuildMetadata;
}

/** A dataspace's start page, and the environment values it may read. */
export interface DataspaceLanding {
	html: string;
	/** The page's declared variables that have a value. A name with none is absent, so the page
	 *  reads it as `undefined`, like any unset `process.env.X`. */
	env: Record<string, string>;
}

/**
 * The start page of the build a dataspace runs. A 404 with code `landing_disabled` means the build
 * ships none; callers branch on that, not on the message.
 */
export async function getDataspaceLanding(id: string): Promise<DataspaceLanding> {
	return request<DataspaceLanding>("GET", `/dataspaces/${id}/landing`, undefined, true);
}

export async function listDataspaceBuilds(id: string): Promise<BuildSummaryResponse[]> {
	return request<BuildSummaryResponse[]>("GET", `/dataspaces/${id}/builds`, undefined, true);
}

// ── Departments (`/departments`, server/src/routes/departments) ──

/**
 * What the license server last said about a department's key. Anything but `granted` records the
 * failure and starts no countdown of its own: the 30 days run from the last confirmation, and a
 * hash the license server did not answer for is `unconfirmed`, never a revocation.
 */
export type DepartmentLicenseAnswer =
	| "granted"
	| "disabled"
	| "bound-elsewhere"
	| "unknown"
	| "invalid"
	| "unconfirmed";

/** A department's license as the server last confirmed it. The key itself is never sent. */
export interface DepartmentLicenseResponse {
	status: LicenseStatus;
	/** Whether the license is in force: confirmed within the grace period. */
	licensed: boolean;
	answer: DepartmentLicenseAnswer;
	license_id: string | null;
	label: string | null;
	/** Floating seats, shared by however many people are active at once. */
	floating_seats: number | null;
	/** Seats held by named people. */
	named_seats: number | null;
	/** Whether the department's members may use the Designer. */
	designer: boolean | null;
	/** The key, masked to its first and last four characters. */
	key_hint: string | null;
	last_check: string | null;
	grace_ends_at: string | null;
	grace_remaining_days: number | null;
	/** The last check that did not confirm the license, until one does; a `department_license_*` code. */
	last_error: LicenseCheckFailure | null;
}

export interface DepartmentListItem {
	id: string;
	name: string;
	description: string | null;
	/** The name of the caller's role in it. */
	my_role: string;
	/** `false`: the membership the default department gives everyone, which is no row. */
	explicit: boolean;
	is_default: boolean;
	license: { status: LicenseStatus; label: string | null };
}

export interface DepartmentResponse {
	id: string;
	name: string;
	description: string | null;
	is_default: boolean;
	/** Explicit members: what the member list shows. */
	member_count: number;
	project_count: number;
	dataspace_count: number;
	license: DepartmentLicenseResponse;
	my_role: string;
	explicit: boolean;
	/** Everything the caller may do to the department, sorted. */
	effective_entitlements: string[];
	created_at: string;
	updated_at: string;
}

export interface DepartmentMemberResponse {
	user_id: string;
	display_name: string;
	email: string;
	color: string;
	role_id: number;
	role: string;
	/** Since when they hold a named seat, if they do. */
	named_seat_since: string | null;
	created_at: string;
}

export interface DepartmentRoleResponse {
	id: number;
	name: string;
	/** `department:*` entitlements only. */
	entitlements: string[];
	/** `owner` and `member`: not deleted, not renamed; `owner` not changed either. */
	is_system: boolean;
	member_count: number;
	/** The role the default department gives every user. */
	is_default_role: boolean;
}

export interface CreateDepartmentRequest {
	name: string;
	description?: string;
	/** The department's license key, as LiveReader issued it. Sent once; stored sealed. */
	license_key: string;
}

// ── Departments API ──

export async function createDepartment(data: CreateDepartmentRequest): Promise<DepartmentResponse> {
	return request<DepartmentResponse>("POST", "/departments", data, true);
}

export async function listDepartments(): Promise<DepartmentListItem[]> {
	return request<DepartmentListItem[]>("GET", "/departments", undefined, true);
}

export async function getDepartment(id: string): Promise<DepartmentResponse> {
	return request<DepartmentResponse>("GET", `/departments/${id}`, undefined, true);
}

/** `description: ""` clears it; a field left out is left alone. */
export async function updateDepartment(
	id: string,
	data: { name?: string; description?: string },
): Promise<DepartmentResponse> {
	return request<DepartmentResponse>("PATCH", `/departments/${id}`, data, true);
}

export async function deleteDepartment(id: string): Promise<void> {
	await request<unknown>("DELETE", `/departments/${id}`, undefined, true);
}

/** Replaces the key; the license server is asked first, and the old key stays if it refuses. */
export async function rekeyDepartment(id: string, licenseKey: string): Promise<DepartmentResponse> {
	return request<DepartmentResponse>(
		"PUT",
		`/departments/${id}/license`,
		{ license_key: licenseKey },
		true,
	);
}

// ── Department Members API ──

export async function listDepartmentMembers(id: string): Promise<DepartmentMemberResponse[]> {
	return request<DepartmentMemberResponse[]>(
		"GET",
		`/departments/${id}/members`,
		undefined,
		true,
	);
}

export async function updateDepartmentMemberRole(
	id: string,
	userId: string,
	roleId: number,
): Promise<DepartmentMemberResponse> {
	return request<DepartmentMemberResponse>(
		"PUT",
		`/departments/${id}/members/${userId}`,
		{ role_id: roleId },
		true,
	);
}

/** Removes a member — or, for the caller's own id, leaves the department. */
export async function removeDepartmentMember(id: string, userId: string): Promise<void> {
	await request<unknown>("DELETE", `/departments/${id}/members/${userId}`, undefined, true);
}

// ── Department Invitations API (server/src/routes/departments/invitations.rs) ──

/** An invitation to a department, which holds its role by id: a role renamed after is still the same one. */
export interface DepartmentInvitationResponse {
	id: string;
	department_id: string;
	email: string;
	role_id: number;
	/** The role's name as it is now. */
	role: string;
	/** `pending` or `declined` — see `InvitationStatus`. */
	status: string;
	created_at: string;
	expires_at: string;
	invited_by_display_name: string;
}

export type CreatedDepartmentInvitationResponse = DepartmentInvitationResponse & InvitationDelivery;

/** Pending and declined invitations, newest first. Needs `department:invitations:manage`. */
export async function listDepartmentInvitations(
	id: string,
): Promise<DepartmentInvitationResponse[]> {
	return request<DepartmentInvitationResponse[]>(
		"GET",
		`/departments/${id}/invitations`,
		undefined,
		true,
	);
}

/** `roleId` is a role of this department; left out, `member`. */
export async function createDepartmentInvitation(
	id: string,
	data: { email: string; role_id?: number },
): Promise<CreatedDepartmentInvitationResponse> {
	return request<CreatedDepartmentInvitationResponse>(
		"POST",
		`/departments/${id}/invitations`,
		data,
		true,
	);
}

/** Revokes a pending invitation, or dismisses a declined one. */
export async function cancelDepartmentInvitation(id: string, invitationId: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/departments/${id}/invitations/${invitationId}`,
		undefined,
		true,
	);
}

/** `409 invitation_mail_disabled` while invitation mail is off; `410` for a declined one. */
export async function resendDepartmentInvitation(
	id: string,
	invitationId: string,
): Promise<DepartmentInvitationResponse> {
	return request<DepartmentInvitationResponse>(
		"POST",
		`/departments/${id}/invitations/${invitationId}/resend`,
		undefined,
		true,
	);
}

// ── Department Seats API (server/src/routes/departments/seats.rs) ──

/** The seat a person holds in a department: a named one, or a floating one. */
export type SeatKind = "named" | "floating";

/** A member who has been assigned a named seat; the order of these decides who holds one. */
export interface NamedSeatResponse {
	user_id: string;
	display_name: string;
	/** Since when they are assigned the seat (RFC 3339). */
	named_seat_since: string;
	/** Whether they hold a named seat now: the first `named_seats` assigned do. The rest wait. */
	holds_named_seat: boolean;
}

/** Someone who holds a seat right now. */
export interface ActiveSeatResponse {
	user_id: string;
	display_name: string;
	seat: SeatKind;
}

/**
 * A department's seats, from the license as last confirmed. `named` and `active` are empty arrays
 * for a caller without `department:seats:manage`; the numbers are for everyone who reads the
 * department. Who holds a seat changes with every person who opens or leaves a process and is not
 * an event, so it is as old as the read.
 */
export interface DepartmentSeatsResponse {
	/** Floating seats, shared by however many people are active at once. */
	floating_seats: number;
	/** Seats held by named people. */
	named_seats: number;
	/** How many people hold a floating seat now. Over `floating_seats` after the license was cut:
	 *  nobody is evicted, new claims are refused until it drops. */
	floating_in_use: number;
	/** How many members hold a named seat now. */
	named_in_use: number;
	named: NamedSeatResponse[];
	active: ActiveSeatResponse[];
	/** The most people who held a floating seat at once since `counting_since`. */
	peak_floating_users: number;
	/** How often somebody was refused a floating seat since `counting_since`. */
	refused_seat_claims: number;
	/** When this period of counting began (RFC 3339); `null` before the first claim. */
	counting_since: string | null;
}

export async function getDepartmentSeats(id: string): Promise<DepartmentSeatsResponse> {
	return request<DepartmentSeatsResponse>("GET", `/departments/${id}/seats`, undefined, true);
}

/**
 * Gives an explicit member a named seat (`named: true`; they keep the time they were first given
 * one) or takes it away. Never refused for the number of named seats: a member assigned beyond them
 * waits, and nobody is evicted. `409 not_a_member` for an implicit member.
 */
export async function setDepartmentMemberSeat(
	id: string,
	userId: string,
	named: boolean,
): Promise<DepartmentMemberResponse> {
	return request<DepartmentMemberResponse>(
		"PUT",
		`/departments/${id}/members/${userId}/seat`,
		{ named },
		true,
	);
}

// ── Department Roles API ──

export async function listDepartmentRoles(id: string): Promise<DepartmentRoleResponse[]> {
	return request<DepartmentRoleResponse[]>("GET", `/departments/${id}/roles`, undefined, true);
}

export async function createDepartmentRole(
	id: string,
	data: { name: string; entitlements: string[] },
): Promise<DepartmentRoleResponse> {
	return request<DepartmentRoleResponse>("POST", `/departments/${id}/roles`, data, true);
}

export async function updateDepartmentRole(
	id: string,
	roleId: number,
	data: { name?: string; entitlements?: string[] },
): Promise<DepartmentRoleResponse> {
	return request<DepartmentRoleResponse>("PUT", `/departments/${id}/roles/${roleId}`, data, true);
}

export async function deleteDepartmentRole(id: string, roleId: number): Promise<void> {
	await request<unknown>("DELETE", `/departments/${id}/roles/${roleId}`, undefined, true);
}

// ── Spaces in departments (server/src/routes/departments/{spaces,mapping}.rs) ──

/**
 * A department as another resource names the one it belongs to. A space — a project or a
 * dataspace — is personal (`null` where this is used) or in exactly one department.
 */
export interface DepartmentRef {
	id: string;
	name: string;
}

/** A project or a dataspace of a department, for its Spaces list. */
export interface DepartmentSpaceResponse {
	id: string;
	kind: "project" | "dataspace";
	/** A project that has none yet has no name. */
	name: string | null;
	/** Whether the caller reaches it, and so can open it. */
	reachable: boolean;
}

/** Spaces by id. A move names at least one; an id given twice is moved once. */
export interface SpacesRequest {
	project_ids?: string[];
	dataspace_ids?: string[];
}

export interface SpaceIdsResponse {
	project_ids: string[];
	dataspace_ids: string[];
}

export interface MoveSpacesResponse {
	/** The spaces that changed department. */
	moved: SpaceIdsResponse;
	/** The spaces that were where they were being moved to already, left as they were. */
	unchanged: SpaceIdsResponse;
}

/** A role, by id and name. */
export interface RoleRef {
	id: number;
	name: string;
}

/** One row of a space's department access. */
export interface AccessMappingResponse {
	/** The department role it is for; `null` for every member of the department. */
	department_role_id: number | null;
	department_role_name: string | null;
	/** The space's role its holders get. */
	space_role_id: number;
	space_role_name: string;
	/** The department's `owner` role getting the space's `admin`: it cannot be removed or changed. */
	locked: boolean;
}

export interface DepartmentAccessResponse {
	/** The department the space is in; `null` for a personal space, which has no mapping. */
	department: DepartmentRef | null;
	mappings: AccessMappingResponse[];
	/** The department's roles, for a row to name — empty for a personal space. */
	department_roles: RoleRef[];
	/** The space's roles, for a row to give. */
	space_roles: RoleRef[];
}

export interface DepartmentAccessRow {
	/** The key must be present: `null` is every member of the department. */
	department_role_id: number | null;
	space_role_id: number;
}

/** What a space is: a `project` or a `dataspace`. Both have the same routes for what follows. */
export type SpaceKind = "project" | "dataspace";

/** The routes of a space of `kind`: `/projects/{id}` and `/dataspaces/{id}`. */
function spacePath(kind: SpaceKind, id: string): string {
	return kind === "project" ? `/projects/${id}` : `/dataspaces/${id}`;
}

/** The department's projects that are not deleted, then its dataspaces. Needs `department:read`. */
export async function listDepartmentSpaces(id: string): Promise<DepartmentSpaceResponse[]> {
	return request<DepartmentSpaceResponse[]>("GET", `/departments/${id}/spaces`, undefined, true);
}

/**
 * Moves spaces into the department, all or nothing. Needs `department:spaces:move_in` here, the
 * right to delete each space, and — for one in another department — `department:spaces:manage`
 * there. Its department access restarts as the owners' fixed row.
 */
export async function moveSpacesIntoDepartment(
	id: string,
	spaces: SpacesRequest,
): Promise<MoveSpacesResponse> {
	return request<MoveSpacesResponse>("POST", `/departments/${id}/spaces`, spaces, true);
}

/**
 * Makes the department's spaces personal, all or nothing. Needs `department:spaces:manage` and the
 * right to delete each space. One with no explicit admin makes the caller its admin.
 */
export async function makeSpacesPersonal(
	id: string,
	spaces: SpacesRequest,
): Promise<MoveSpacesResponse> {
	return request<MoveSpacesResponse>("DELETE", `/departments/${id}/spaces`, spaces, true);
}

/** A space's department access: its department, and which of its roles each department role gets. */
export async function getDepartmentAccess(
	kind: SpaceKind,
	id: string,
): Promise<DepartmentAccessResponse> {
	return request<DepartmentAccessResponse>(
		"GET",
		`${spacePath(kind, id)}/department-access`,
		undefined,
		true,
	);
}

/** A member of a space's department who is not on the space yet, as the "Add from department" list offers them. */
export interface MemberCandidateResponse {
	user_id: string;
	display_name: string;
	email: string;
	color: string;
}

/**
 * The people the space's administrators may add from a list: the space's department's explicit
 * members who are not on the space yet, by name — never the default department's implicit everyone.
 * Needs `members:manage` on a project, `dataspace:members:manage` on a dataspace;
 * `409 not_in_department` for a personal space, whose people are invited. Adding one is
 * `addProjectMember` / `addDataspaceMember`, which take these people and nobody else
 * (`403 invitation_required`).
 */
export async function listMemberCandidates(
	kind: SpaceKind,
	id: string,
): Promise<MemberCandidateResponse[]> {
	return request<MemberCandidateResponse[]>(
		"GET",
		`${spacePath(kind, id)}/member-candidates`,
		undefined,
		true,
	);
}

/**
 * Replaces every row but the owners' fixed one, which is kept whether it is sent or not. Needs
 * `members:manage` on a project, `dataspace:members:manage` on a dataspace.
 */
export async function putDepartmentAccess(
	kind: SpaceKind,
	id: string,
	mappings: DepartmentAccessRow[],
): Promise<DepartmentAccessResponse> {
	return request<DepartmentAccessResponse>(
		"PUT",
		`${spacePath(kind, id)}/department-access`,
		{ mappings },
		true,
	);
}

// ── Control center types (`/system`, server/src/routes/system) ──

export interface SystemUserResponse {
	id: string;
	email: string;
	email_confirmed: boolean;
	display_name: string;
	color: string;
	system_role: string;
	project_count: number;
	dataspace_count: number;
	created_at: string;
}

export interface SystemRoleResponse {
	name: string;
	entitlements: string[];
	/** Seeded by migration: cannot be deleted. */
	built_in: boolean;
	/** What it grants cannot be changed either (`server_admin`, `banned`). */
	locked: boolean;
	user_count: number;
}

export interface SystemProjectResponse {
	id: string;
	name: string | null;
	version: string | null;
	created_by: string;
	created_by_display_name: string | null;
	created_at: string;
	updated_at: string;
	member_count: number;
	admin_count: number;
	dataspace_count: number;
	/** The caller's own role on it, if they are a member. */
	my_role: string | null;
	/** A record with no files: Maintenance removes it for good. Never set for an imported one. */
	files_missing: boolean;
	/** `local`, or `imported` — known here only from releases imported from another server. */
	source: string;
	/** The department the project belongs to; `null` for a personal one. */
	department: DepartmentRef | null;
}

export interface SystemMemberResponse {
	user_id: string;
	email: string;
	display_name: string;
	role: string;
	created_at: string;
}

export interface SystemProjectDetailResponse {
	project: SystemProjectResponse;
	members: SystemMemberResponse[];
	roles: string[];
	dataspaces: { id: string; label: string; project_version: string }[];
}

export interface SystemDataspaceResponse {
	id: string;
	label: string;
	description: string | null;
	project_id: string;
	project_name: string;
	project_version: string;
	created_at: string;
	updated_at: string;
	member_count: number;
	admin_count: number;
	process_count: number;
	running_count: number;
	my_role: string | null;
	/** The department the dataspace belongs to; `null` for a personal one. */
	department: DepartmentRef | null;
}

export interface SystemDataspaceDetailResponse {
	dataspace: SystemDataspaceResponse;
	members: SystemMemberResponse[];
	roles: string[];
	processes: { id: string; name: string; process_label: string; status: string }[];
}

export interface SystemProcessResponse {
	id: string;
	name: string;
	process_label: string;
	dataspace_id: string;
	dataspace_label: string;
	status: string;
	restart_rule: string;
	running: boolean;
	ready: boolean;
	error: string | null;
	updated_at: string;
}

export interface SystemRuntimeResponse {
	version: string;
	started_at: string | null;
	user_count: number;
	project_count: number;
	dataspace_count: number;
	processes: SystemProcessResponse[];
	builds: { project_id: string; project_name: string | null; version: string }[];
	project_cache: {
		ttl_minutes: number;
		max_entries: number;
		entries: {
			project_id: string;
			project_name: string | null;
			idle_seconds: number;
			stale: boolean;
		}[];
	};
}

// ── Control center API ──

export async function listSystemUsers(): Promise<SystemUserResponse[]> {
	return request<SystemUserResponse[]>("GET", "/system/users", undefined, true);
}

export async function setUserSystemRole(
	userId: string,
	systemRole: string,
): Promise<SystemUserResponse> {
	return request<SystemUserResponse>(
		"PUT",
		`/system/users/${userId}/role`,
		{ system_role: systemRole },
		true,
	);
}

export async function listSystemRoles(): Promise<SystemRoleResponse[]> {
	return request<SystemRoleResponse[]>("GET", "/system/roles", undefined, true);
}

export async function createSystemRole(data: {
	name: string;
	entitlements: string[];
}): Promise<SystemRoleResponse> {
	return request<SystemRoleResponse>("POST", "/system/roles", data, true);
}

export async function updateSystemRole(
	name: string,
	entitlements: string[],
): Promise<SystemRoleResponse> {
	return request<SystemRoleResponse>(
		"PUT",
		`/system/roles/${encodeURIComponent(name)}`,
		{ entitlements },
		true,
	);
}

export async function deleteSystemRole(name: string): Promise<void> {
	await request<unknown>("DELETE", `/system/roles/${encodeURIComponent(name)}`, undefined, true);
}

export async function listSystemProjects(): Promise<SystemProjectResponse[]> {
	return request<SystemProjectResponse[]>("GET", "/system/projects", undefined, true);
}

export async function getSystemProject(id: string): Promise<SystemProjectDetailResponse> {
	return request<SystemProjectDetailResponse>("GET", `/system/projects/${id}`, undefined, true);
}

export async function deleteSystemProject(id: string): Promise<void> {
	await request<unknown>("DELETE", `/system/projects/${id}`, undefined, true);
}

export async function listSystemDataspaces(): Promise<SystemDataspaceResponse[]> {
	return request<SystemDataspaceResponse[]>("GET", "/system/dataspaces", undefined, true);
}

export async function getSystemDataspace(id: string): Promise<SystemDataspaceDetailResponse> {
	return request<SystemDataspaceDetailResponse>(
		"GET",
		`/system/dataspaces/${id}`,
		undefined,
		true,
	);
}

export async function deleteSystemDataspace(id: string): Promise<void> {
	await request<unknown>("DELETE", `/system/dataspaces/${id}`, undefined, true);
}

/** Where a control-center membership lives: `/system/projects/{id}` or `/system/dataspaces/{id}`. */
export type SystemMembershipScope = "projects" | "dataspaces";

export async function addSystemMember(
	scope: SystemMembershipScope,
	id: string,
	userId: string,
	role: string,
): Promise<SystemMemberResponse> {
	return request<SystemMemberResponse>(
		"POST",
		`/system/${scope}/${id}/members`,
		{ user_id: userId, role },
		true,
	);
}

export async function updateSystemMember(
	scope: SystemMembershipScope,
	id: string,
	userId: string,
	role: string,
): Promise<void> {
	await request<unknown>("PUT", `/system/${scope}/${id}/members/${userId}`, { role }, true);
}

export async function removeSystemMember(
	scope: SystemMembershipScope,
	id: string,
	userId: string,
): Promise<void> {
	await request<unknown>("DELETE", `/system/${scope}/${id}/members/${userId}`, undefined, true);
}

export async function getSystemRuntime(): Promise<SystemRuntimeResponse> {
	return request<SystemRuntimeResponse>("GET", "/system/runtime", undefined, true);
}

export async function stopSystemProcess(processId: string): Promise<SystemProcessResponse> {
	return request<SystemProcessResponse>(
		"POST",
		`/system/runtime/processes/${processId}/stop`,
		undefined,
		true,
	);
}

export async function restartSystemProcess(processId: string): Promise<SystemProcessResponse> {
	return request<SystemProcessResponse>(
		"POST",
		`/system/runtime/processes/${processId}/restart`,
		undefined,
		true,
	);
}

// ── Control center: service tokens ──

/*
  A service token's grants reuse `PatGrant` and its grantable layers reuse `GrantableLayer`
  on purpose: the grant shape is the shared one of both token classes (spec FR-023), and the
  mint request/response mirror the account PAT's field by field. The routes are the control
  centre's, so nothing here broadcasts.
*/

export interface ServiceTokenMintRequest {
	name: string;
	/** 1–90. Left out with `never_expires`. */
	expires_in_days?: number;
	/** Offered always; the provider may still refuse it (`service_token_mint_refused`). */
	never_expires?: boolean;
	grants: PatGrant[];
}

export interface ServiceTokenMintResponse {
	id: string;
	name: string;
	/** Shown exactly once, at the mint. The server never stores or returns it again. */
	secret: string;
	/** `null`: never expires. */
	expires_at: string | null;
	/** What the token carries, including the read grant added per selected layer (FR-003). */
	grants: PatGrant[];
}

/** Mints a service token at the provider and records it locally. */
export async function mintServiceToken(
	data: ServiceTokenMintRequest,
): Promise<ServiceTokenMintResponse> {
	return request<ServiceTokenMintResponse>("POST", "/system/service-tokens", data, true);
}

/**
 * Every live layer on the server with the layer-level keys some role at it defines — the
 * union over all roles, not the minting admin's memberships: a service token's grants are
 * not coupled to whoever mints it (FR-002). The account PAT route's `/account/pats/grantable`
 * is the per-user counterpart and cannot serve here.
 */
export async function listGrantableServiceTokenLayers(): Promise<GrantableResponse> {
	return request<GrantableResponse>("GET", "/system/service-tokens/grantable", undefined, true);
}

/**
 * A service token's grant as the list answers it: the shared grant shape plus the layer's
 * *current* label. A layer that no longer exists arrives already labelled `<id> (deleted)` by
 * the server's labelling owner (spec FR-023), so the client shows `layer_label` as it is and
 * never decides deletion itself.
 */
export interface ServiceTokenListGrant extends PatGrant {
	layer_label: string;
}

/**
 * The merged state of the list (data-model R8): the provider's view, with a local revocation
 * winning over it. `deleted` means the provider's client is gone — the record stays listed,
 * permanently unusable.
 */
export type ServiceTokenState = "active" | "suspended" | "expired" | "revoked" | "deleted";

export interface ServiceTokenItem {
	id: string;
	name: string;
	grants: ServiceTokenListGrant[];
	created_at: string;
	/** `null`: never expires. */
	expires_at: string | null;
	/** `null`: never used. */
	last_used_at: string | null;
	state: ServiceTokenState;
	revoked_at: string | null;
	/** Who revoked it — a section admin here (`admin`), or the provider-side view (`client`). */
	revoked_by: "admin" | "client" | null;
}

export interface ServiceTokenListResponse {
	tokens: ServiceTokenItem[];
	/** The provider's bound cut the list — not every token is shown. */
	truncated: boolean;
}

/** The server's own service tokens. Secret-free by construction: the list has no secret field. */
export async function listServiceTokens(): Promise<ServiceTokenListResponse> {
	return request<ServiceTokenListResponse>("GET", "/system/service-tokens", undefined, true);
}

/** Revokes one of the server's tokens; idempotent, the first revocation stands (204, no body). */
export async function revokeServiceToken(id: string): Promise<void> {
	await request<unknown>(
		"DELETE",
		`/system/service-tokens/${encodeURIComponent(id)}`,
		undefined,
		true,
	);
}

// ── Control center: maintenance ──

export interface MaintenanceProjectResponse {
	id: string;
	name: string | null;
	created_by_display_name: string | null;
	updated_at: string;
	member_count: number;
	/** Dataspaces that run it; while there are any, it is not removed. */
	dataspaces: { id: string; label: string }[];
	bytes: number;
}

export interface MaintenanceFileResponse {
	kind: "project" | "build" | "package" | "process_state";
	name: string;
	bytes: number;
}

export interface MaintenanceResponse {
	broken_projects: MaintenanceProjectResponse[];
	deleted_projects: MaintenanceProjectResponse[];
	orphaned_files: MaintenanceFileResponse[];
	expired: { invitations: number };
}

export interface MaintenanceResultResponse {
	removed: string[];
	skipped: { item: string; code: string; message: string }[];
}

export async function getSystemMaintenance(): Promise<MaintenanceResponse> {
	return request<MaintenanceResponse>("GET", "/system/maintenance", undefined, true);
}

/** Removes broken or deleted projects for good. Only those still broken or deleted are removed. */
export async function purgeSystemProjects(ids: string[]): Promise<MaintenanceResultResponse> {
	return request<MaintenanceResultResponse>(
		"POST",
		"/system/maintenance/projects/purge",
		{ ids },
		true,
	);
}

/** Removes orphaned files. Only those a fresh scan still lists are removed. */
export async function removeOrphanedFiles(
	files: { kind: string; name: string }[],
): Promise<MaintenanceResultResponse> {
	return request<MaintenanceResultResponse>(
		"POST",
		"/system/maintenance/files/remove",
		{ files },
		true,
	);
}

export async function removeExpiredRecords(): Promise<{ invitations: number }> {
	return request("POST", "/system/maintenance/expired/remove", undefined, true);
}

// ── Control center: settings ──

/** Where a setting's value comes from: set in the control center, inherited, or the default. */
export type SettingSource = "db" | "env" | "default";

export interface SettingValueResponse {
	/** Null when nothing sets it. A secret is masked (first and last four characters). */
	value: string | null;
	source: SettingSource;
	secret: boolean;
	/** The environment variable it is inherited from. */
	env: string | null;
	updated_by: string | null;
	updated_at: string | null;
}

export interface MailSettingsResponse {
	/** A host and a sender are set: mail is sent at all. */
	enabled: boolean;
	dkim_enabled: boolean;
	host: SettingValueResponse;
	port: SettingValueResponse;
	user: SettingValueResponse;
	password: SettingValueResponse;
	from_name: SettingValueResponse;
	dkim_domain: SettingValueResponse;
	dkim_selector: SettingValueResponse;
	dkim_private_key: SettingValueResponse;
}

export interface InvitationsSettingsResponse {
	/**
	 * An invitation made now is mailed: `send_mail` is not `off` AND mail is configured. Otherwise
	 * it is made all the same and its inviter is handed the link instead.
	 */
	enabled: boolean;
	/** `on` or `off`; `on` unless set. It stops no other mail. */
	send_mail: SettingValueResponse;
}

export interface LlmSettingsResponse {
	enabled: boolean;
	url: SettingValueResponse;
	token: SettingValueResponse;
	model: SettingValueResponse;
	service: SettingValueResponse;
}

export interface MapSettingsResponse {
	/** The Operator offers a map: `switch` is not `off` and a tile server is set. */
	enabled: boolean;
	/** `on` or `off`. */
	switch: SettingValueResponse;
	/** The tile server's base URL, asked by the server, never the browser. A value from before, a
	 *  template with `{z}`, `{x}` and `{y}`, still works. */
	tile_url: SettingValueResponse;
	attribution: SettingValueResponse;
}

export interface LocationSettingsResponse {
	/** A location service is set: address search, and the address at a pin where it has one. */
	enabled: boolean;
	/** Its base URL, asked by the server. */
	url: SettingValueResponse;
	/** Milliseconds per request; 10000 when unset. */
	timeout_ms: SettingValueResponse;
	/** A region it knows, to search in; all of them when unset. */
	region: SettingValueResponse;
}

/** How the gateway URL processes get is found (server/src/process_env.rs). */
export type ProcessUrlStatus = "set" | "derived" | "unset";

/** One NOTITIA_* variable as a process whose build declares it will get it. */
export interface ProcessDefaultRow {
	key: string;
	/** Masked when `secret`; null when the server hands none. */
	value: string | null;
	secret: boolean;
	setting: string | null;
	source: SettingSource | null;
	/** Why it has none. */
	missing: "unset" | "no_token" | "engine_default" | null;
}

export interface ProcessesSettingsResponse {
	/** The gateway's base URL for processes; unset, it is read off the LLM gateway's URL. */
	llm_url: SettingValueResponse;
	/** The processes' own token, never the server's. */
	llm_token: SettingValueResponse;
	/** Unset: the engine's own default. */
	llm_model: SettingValueResponse;
	llm_url_status: ProcessUrlStatus;
	defaults: ProcessDefaultRow[];
}

export interface SystemSettingsResponse {
	mail: MailSettingsResponse;
	invitations: InvitationsSettingsResponse;
	llm: LlmSettingsResponse;
	map: MapSettingsResponse;
	location: LocationSettingsResponse;
	processes: ProcessesSettingsResponse;
	/** Stored secrets the server's key file cannot decrypt; ignored until entered again. */
	unreadable: string[];
}

/**
 * A settings write: a field left out (or `null`) keeps what is stored, `""` removes the stored
 * value so the inherited one applies again, anything else is stored.
 */
export type SettingsUpdate<K extends string> = Partial<Record<K, string | null>>;

export type MailSettingKey = Exclude<keyof MailSettingsResponse, "enabled" | "dkim_enabled">;
export type InvitationsSettingKey = Exclude<keyof InvitationsSettingsResponse, "enabled">;
export type LlmSettingKey = Exclude<keyof LlmSettingsResponse, "enabled">;
export type MapSettingKey = Exclude<keyof MapSettingsResponse, "enabled">;
export type LocationSettingKey = Exclude<keyof LocationSettingsResponse, "enabled">;
export type ProcessesSettingKey = "llm_url" | "llm_token" | "llm_model";

export async function getSystemSettings(): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("GET", "/system/settings", undefined, true);
}

export async function updateMailSettings(
	body: SettingsUpdate<MailSettingKey>,
): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("PUT", "/system/settings/mail", body, true);
}

export async function updateInvitationsSettings(
	body: SettingsUpdate<InvitationsSettingKey>,
): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("PUT", "/system/settings/invitations", body, true);
}

export async function updateLlmSettings(
	body: SettingsUpdate<LlmSettingKey>,
): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("PUT", "/system/settings/llm", body, true);
}

/** Sends a test mail to the caller with the settings as saved. */
export async function testMailSettings(): Promise<{ to: string }> {
	return request("POST", "/system/settings/mail/test", undefined, true);
}

/** Makes a tiny request to the LLM gateway with the settings as saved. */
export async function testLlmSettings(): Promise<{ model: string; latency_ms: number }> {
	return request("POST", "/system/settings/llm/test", undefined, true);
}

export async function updateMapSettings(
	body: SettingsUpdate<MapSettingKey>,
): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("PUT", "/system/settings/map", body, true);
}

/** Fetches one tile with the settings as saved. */
export async function testMapSettings(): Promise<{ tile_latency_ms: number }> {
	return request("POST", "/system/settings/map/test", undefined, true);
}

export async function updateLocationSettings(
	body: SettingsUpdate<LocationSettingKey>,
): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("PUT", "/system/settings/location", body, true);
}

export interface LocationTestResponse {
	latency_ms: number;
	/** Null for a service older than 1.2.0, which names no version. */
	version: string | null;
	/** `coordinates`, `street_score`, `reverse`, `search`. */
	features: string[];
	/** Older than 1.2.0: no points, no alternatives, no address at a pin. */
	outdated: boolean;
}

/** Asks the location service what it offers, with the settings as saved. */
export async function testLocationSettings(): Promise<LocationTestResponse> {
	return request("POST", "/system/settings/location/test", undefined, true);
}

export async function updateProcessesSettings(
	body: SettingsUpdate<ProcessesSettingKey>,
): Promise<SystemSettingsResponse> {
	return request<SystemSettingsResponse>("PUT", "/system/settings/processes", body, true);
}

/** Asks the gateway for a few tokens as a process would, with the defaults as saved. */
export async function testProcessesSettings(): Promise<{
	url: string;
	model: string;
	latency_ms: number;
}> {
	return request("POST", "/system/settings/processes/test", undefined, true);
}

// ── Control center: license ──

export type LicenseStatus = "valid" | "overdue" | "expired" | "missing";

export interface LicenseCheckFailure {
	/** One of the `license_*` codes in `API_ERROR_CODES`. */
	code: string;
	message: string;
	at: string;
}

/** What the license switches on or off (`features` of the license response). */
export interface LicenseFeatures {
	/** Whether the Designer is open on this server. */
	designer: boolean;
	/** Whether people may have spaces of their own, outside any department. */
	personal_spaces: boolean;
	/** How many members a personal space may have, the owner included. */
	personal_member_limit: number;
	/**
	 * Whether the license server sent these. `false`: the license was confirmed before it did, or
	 * by a license server that does not yet, and the three values are the defaults in force.
	 */
	reported: boolean;
}

export interface LicenseResponse {
	/** `overdue`: works, unconfirmed for over a day. `expired` and `missing`: Designer and Operator closed. */
	status: LicenseStatus;
	/** Whether the Designer and the Operator are open. */
	licensed: boolean;
	/** Masked to its first and last four characters. */
	key: string | null;
	license_id: string | null;
	label: string | null;
	/** What the license switches on; `null` while no license is confirmed. */
	features: LicenseFeatures | null;
	last_check: string | null;
	/** When the 30 days after the last confirmation end. */
	grace_ends_at: string | null;
	/** Days left of those 30, rounded up. */
	grace_remaining_days: number | null;
	last_error: LicenseCheckFailure | null;
	server_url: string;
}

export async function getSystemLicense(): Promise<LicenseResponse> {
	return request<LicenseResponse>("GET", "/system/license", undefined, true);
}

/** Enters a license key; the server checks it with the license server before storing it. */
export async function updateSystemLicense(key: string): Promise<LicenseResponse> {
	return request<LicenseResponse>("PUT", "/system/license", { key }, true);
}

/** Checks the stored key with the license server now. */
export async function checkSystemLicense(): Promise<LicenseResponse> {
	return request<LicenseResponse>("POST", "/system/license/check", undefined, true);
}

// ── Control center: releases (server/src/routes/system/releases.rs) ──

/** A release as the control center lists it: the release, and which project it is of. */
export interface SystemReleaseResponse extends ReleaseResponse {
	project_id: string;
	project_name: string | null;
	/** `local`, or `imported` — a project known here only from its releases. */
	project_source: string;
}

export interface ReleaseManifestProject {
	id: string;
	name: string | null;
	term_id: string | null;
	languages: string[];
}

export interface ReleaseManifestPackage {
	/** The npm package name, as the store names its directory. */
	name: string;
	version: string;
}

/** What a `.notitia-release.zip` says it is (`release_archive.rs`). */
export interface ReleaseManifest {
	format: string;
	project: ReleaseManifestProject;
	version: string;
	title: string | null;
	notes: string;
	changelog: ChangelogResponse;
	commit_id: string | null;
	previous_version: string | null;
	/** The server it was exported from (its public URL). */
	source_server: string;
	created_at: string;
	exported_at: string;
	/** `node --version` on the exporting server. */
	node: string | null;
	/** The engine version the build was installed with, where it is known. */
	engine: string | null;
	/** The project's own package's `notitia.dependencies`: the projects it pins, and at what. */
	dependencies: unknown;
	/** Every package in the archive, the project's own first. */
	packages: ReleaseManifestPackage[];
	/** sha256 of every file in the archive but the manifest, by path. */
	files: Record<string, string>;
}

/** What importing an archive does — or, with `imported`, did. */
export interface ImportPreviewResponse {
	manifest: ReleaseManifest;
	/** sha256 of the uploaded file. */
	archive_sha256: string;
	/** This server already has a project with that id. */
	project_exists: boolean;
	/** What the project is called here, when it exists. */
	project_name_here: string | null;
	/** `local` or `imported`, when it exists. */
	project_source_here: string | null;
	/** This server already has this version of the project. */
	version_exists: boolean;
	/** …and it came from this very archive: importing again changes nothing. */
	identical: boolean;
	/** Why importing would be refused (the server's words); empty when it would go ahead. */
	conflicts: string[];
	/** The import was carried out (`confirm`), not just previewed. */
	imported: boolean;
}

/** Every release on the server, or `projectId`'s only. */
export async function listSystemReleases(projectId?: string): Promise<SystemReleaseResponse[]> {
	const qs = projectId ? `?${new URLSearchParams({ project: projectId })}` : "";
	return request<SystemReleaseResponse[]>("GET", `/system/releases${qs}`, undefined, true);
}

/** The runnable versions of any project, for whoever manages projects, dataspaces or releases. */
export async function listSystemProjectVersions(
	projectId: string,
): Promise<ProjectVersionResponse[]> {
	return request<ProjectVersionResponse[]>(
		"GET",
		`/system/projects/${projectId}/versions`,
		undefined,
		true,
	);
}

/**
 * A release as a `.notitia-release.zip` another server can import. A Blob, because the route
 * needs the bearer token a plain link cannot send — save it with `downloadBlob`.
 */
export async function exportSystemRelease(projectId: string, version: string): Promise<Blob> {
	return requestBlob(
		"GET",
		`/system/releases/${projectId}/${encodeURIComponent(version)}/export`,
		true,
	);
}

/**
 * Uploads a release archive. Without `confirm` it only checks it and says what an import would
 * do; with it, it imports. `400 invalid_archive` for a file that is not one or fails its checks;
 * `409 release_conflict` for a confirmed import the preview listed conflicts for.
 */
export async function importSystemRelease(
	file: Blob,
	confirm: boolean,
): Promise<ImportPreviewResponse> {
	const form = new FormData();
	form.append("file", file);
	form.append("confirm", confirm ? "true" : "false");
	return requestMultipart<ImportPreviewResponse>("POST", "/system/releases/import", form, true);
}

// ── Control center: departments (server/src/routes/system/departments.rs) ──

export interface SystemDepartmentOwnerResponse {
	user_id: string;
	display_name: string;
}

/** A department as the control center lists it: its license, its numbers and its owners. */
export interface SystemDepartmentResponse {
	id: string;
	name: string;
	description: string | null;
	is_default: boolean;
	/** The role default membership gives everyone: set for the default department only. */
	default_role_id: number | null;
	/** The department's roles, for the default department's role to be picked from. */
	roles: RoleRef[];
	license: DepartmentLicenseResponse;
	member_count: number;
	project_count: number;
	dataspace_count: number;
	/** The explicit members holding its `owner` role, oldest first. */
	owners: SystemDepartmentOwnerResponse[];
	created_at: string;
	updated_at: string;
}

export interface SystemCreateDepartmentRequest extends CreateDepartmentRequest {
	/**
	 * The email of the person who becomes its first owner: they are invited to own it, and own it
	 * once they accept — until then the department has no owner. The caller's own verified email is
	 * the one exception: the caller is its owner at once.
	 */
	owner_email: string;
}

/** What became of an invitation to own a department, as the control center is told. */
export interface OwnerInvitationResponse {
	email: string;
	/** Whether the invitation was mailed. */
	mail_sent: boolean;
	/** The link, to pass on, when it was not mailed: in this one answer and never returned again. */
	accept_url?: string;
}

/** A department the control center started, with the invitation that makes somebody its owner. */
export type SystemCreatedDepartmentResponse = SystemDepartmentResponse & {
	/** `null` when the caller named their own verified email and became the owner at once. */
	owner_invitation: OwnerInvitationResponse | null;
};

/** What appointing an owner by email did: promoted a member who holds it, or invited the address. */
export interface SystemOwnerAddedResponse {
	result: "promoted" | "invited";
	/** For `invited`. */
	invitation?: OwnerInvitationResponse;
	department: SystemDepartmentResponse;
}

export async function listSystemDepartments(): Promise<SystemDepartmentResponse[]> {
	return request<SystemDepartmentResponse[]>("GET", "/system/departments", undefined, true);
}

/**
 * Starts a department for the person with `owner_email`, who is invited to own it; the key is
 * checked with the license server first.
 */
export async function createSystemDepartment(
	data: SystemCreateDepartmentRequest,
): Promise<SystemCreatedDepartmentResponse> {
	return request<SystemCreatedDepartmentResponse>("POST", "/system/departments", data, true);
}

export async function deleteSystemDepartment(id: string): Promise<void> {
	await request<unknown>("DELETE", `/system/departments/${id}`, undefined, true);
}

export async function rekeySystemDepartment(
	id: string,
	licenseKey: string,
): Promise<SystemDepartmentResponse> {
	return request<SystemDepartmentResponse>(
		"PUT",
		`/system/departments/${id}/license`,
		{ license_key: licenseKey },
		true,
	);
}

/**
 * Makes the person with `email` an owner. A member of the department who holds it is promoted
 * (`result: "promoted"`); anybody else — an account that is no member, or none at all — is invited
 * to own it (`result: "invited"`), and the answer is the same whichever it was.
 */
export async function addSystemDepartmentOwner(
	id: string,
	email: string,
): Promise<SystemOwnerAddedResponse> {
	return request<SystemOwnerAddedResponse>(
		"POST",
		`/system/departments/${id}/owners`,
		{ email },
		true,
	);
}

/** Demotes an owner to `member`; `409 last_owner` for the last one. */
export async function removeSystemDepartmentOwner(
	id: string,
	userId: string,
): Promise<SystemDepartmentResponse> {
	return request<SystemDepartmentResponse>(
		"DELETE",
		`/system/departments/${id}/owners/${userId}`,
		undefined,
		true,
	);
}

/** Moves any projects and dataspaces into the department, with no right on the spaces themselves. */
export async function moveSystemSpacesIntoDepartment(
	id: string,
	spaces: SpacesRequest,
): Promise<MoveSpacesResponse> {
	return request<MoveSpacesResponse>("POST", `/system/departments/${id}/spaces`, spaces, true);
}

/**
 * Makes the department's spaces personal. `409 space_needs_admin` for one that would be left with
 * no admin of its own: the control center has no role of its own to give, so an admin is added
 * first, from the project's or the dataspace's page.
 */
export async function makeSystemSpacesPersonal(
	id: string,
	spaces: SpacesRequest,
): Promise<MoveSpacesResponse> {
	return request<MoveSpacesResponse>("DELETE", `/system/departments/${id}/spaces`, spaces, true);
}

export interface DefaultDepartment {
	department_id: string | null;
	role_id: number | null;
}

/**
 * Every user becomes an implicit member of `department_id`, holding `role_id` there; `null` and
 * `null` clears it. A department needs its role (`400 role_required`).
 */
export async function setDefaultDepartment(data: DefaultDepartment): Promise<DefaultDepartment> {
	return request<DefaultDepartment>("PUT", "/system/departments/default", data, true);
}
